// kernel/useModels.ts
import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { getCloudTmpUrl, uploadFileToCloud } from '#lib/azure-client';
import { assembleNanoListProviderParams, closestSupportedAspectRatio, NANO_SUPPORTED_RATIOS, NANO_2_SUPPORTED_RATIOS, FLUX_TWO_SUPPORTED_RATIOS } from '#lib/list-generation-aspect-ratio';
import { GROK_2_SUPPORTED_RATIOS, resolveGrokImagine2Resolution } from '#lib/grok-imagine-2';
import { deriveMuseImageAspectRatio, museImageInputPixels, parseMuseImageAspectRatio } from '#lib/muse-image';
import { deriveFlux3ImageAspectRatio, FLUX_3_IMAGE_MAX_INPUT_PIXELS, parseFlux3ImageAspectRatio } from '#lib/flux-3-image';
import { FLUX_3_KLEIN_MAX_INPUT_PIXELS } from '#lib/flux-3-klein';
import { isInternalAssetUrl } from '#lib/azure-shared';
import { loadListReferenceImages } from '#lib/list/listReferenceImages';
import { cropImageFileToAspectRatio, loadImageDataFromBlob, loadImageDataFromUrl, resizeImageDataToPixelBudget } from '#lib/canvas-utils';
import { MIRELO_SFX_MAX_DURATION_SECONDS, MIRELO_SFX_MIN_DURATION_SECONDS } from '#lib/mirelo-sfx';
import { KernelHandle, useModelsReturnType, Layer, Rect, Workspace, GenerationTask, MergeStrategy, DiffusionParams, RuntimeState, Seedream4_5Params, NanoBananaAspectRatio, Kling3OmniParams, Seedance2Params, GeminiOmniFlashParams, TaskCompletionData, LockableFile, NanoBananaProParams, FluxTwoProParams, FluxTwoProResolution, FluxTwoProAspectRatio, NanoBanana2Params, ModelRunGenerationOptions, MaskShape, TopazVideoUpscaleParams, MireloSfxParams, MagnificVideoUpscaleParams, MagnificImageUpscaleParams, SeedVR2Params, TopazImageEnhanceParams, QwenImage21Params, GrokImagine2Params, GrokImagine2AspectRatio, QwenImage3Params, MuseImageParams, Flux3ImageParams, StreetCuts, RecastZone, CropPoint } from '#types/types';
import { clamp, normalizeVideoDimension, toFinite } from '#lib/timeline-video-types';
import type { TimelineExportPayload, TimelineTrackInput } from '#lib/timeline-video-types';
import { v4 as uuidv4 } from 'uuid';
import { getDefaultParamsForModel } from '#lib/models';
import { sortLayersByRenderOrder } from '#lib/layer-render-order';
import {
  enforceStudioModelMinimumGenerations,
  fitReferenceImageToBudget,
  getStudioModelProjectCompatibilityError,
  getStudioModelReferenceLimits,
  ReferenceImageBudgetError,
  resolveRegistryModelIdFromRuntimeName,
  STUDIO_MODEL_REGISTRY,
  studioModelRequiresPrompt,
  StudioModelId,
  videoModelStartsFromText,
  compareStudioModels,
  filterStudioModelIdsForProjectType,
  getActiveSelectableStudioModelIds,
  isStudioModelSupportedForProjectType,
} from '#lib/model-registry';
import { isLayerVectorized } from '#lib/svg-layer';
import { getDrawingAwareSvgLayerSource, getDrawingLayerSvgSource } from '#lib/drawing-layer';
import { findWorkspaceVersionById, resolveWorkspaceAnnotationMask, resolveWorkspaceAnnotationVersionId } from '#lib/workspace-annotations';
import { getWorkspaceRasterDimensions } from '@/components/workspaceRasterUtils';
import { inpaintMaskHasContent, isPointInsideInpaintMask, type MaskPoint } from '@/components/inpaintMaskGeometry';
import { getDefaultIdeogramInpaintParams } from '@/components/models/IdeogramInpaintControlPanel';
import { getDefaultSeedVR2Params } from '@/components/models/SeedVR2ControlPanel';
import { SWEEP_UPSCALE_MODEL_ID, SWEEP_UPSCALE_OUTPUT_MEGAPIXELS, buildSweepUpscaleRuntime, buildGridScript, type SweepUpscaleGridSize } from '#lib/grid-generation-script';
import {
  getProjectSelectedLayersId,
  patchProjectRuntimeField,
  readProjectRuntimeField,
  setProjectSelectedLayersId,
} from '#lib/project-runtime-layer-ui-state';
import {
  MIN_TIMELINE_TRACK_SPAN_MS,
  TIMELINE_PLAYBACK_RATE_PARAM_KEY,
  normalizeTimelineTrackPlaybackRate,
} from '#lib/workspace-timeline';
import { resolveGenerationOutputScopeForView } from '#lib/generation-output-scope';
import { isStreetPanoramaFrame, STREET_DEVELOP_RECIPES, STREET_REPAIR_RECIPE, STREET_TEXTURE_RECIPES, streetDevelopErrors, streetDevelopSteps, streetRepairErrors, streetRepairSteps, streetZoneRect } from '#lib/modelist/streetDevelop';
import { rememberedStreetCuts } from '#lib/modelist/projectScene';
import { groupRecastZones, maskBits, maskBlobBoxes, mergePeopleBoxes, RECAST_EXTRAS_RECIPE, RECAST_MAX_TILES, RECAST_RENDER_MAX_SIDE, RECAST_TILE_MAX_PX, recastExtrasErrors, recastExtrasSteps, recastRecipeErrors, recastMinBlobPixels, recastTiles, recastZonesFromPixels, type RecastBox } from '#lib/recastExtras';
import pLimit from 'p-limit';
import { resolveStudioLayerTransform } from '#lib/studio-layer-transform';
import { normalizeSeedVR2InputMegaPixels, normalizeSeedVR2OutputMegaPixels, resolveSeedVR2InputDimensions, resolveSeedVR2InputPixelBudget, resolveSeedVR2InputRenderPixelBudget, resolveSeedVR2OutputDimensions, SEEDVR2_INPUT_PIXELS, SEEDVR2_OUTPUT_PIXELS, SEEDVR2_STANDARD_POSTPROCESS } from '#lib/seedvr2';
import { isMaiModel as isMaiModelId, MaiImageInputError, resolveMaiImageDimensions } from '#lib/maiImage';
import { isNanoBanana2OrNewer } from '#lib/nanoBanana';
import { getTopazUpscaleFactorFromParams, hasTopazImageResolutionContract } from '#lib/topaz-image';
import { getProjectStudioViewMode } from '#lib/studio-project-view';
import { resolveGptImage25OutputGeometry } from '#lib/gpt-image-25';
import { extendQwenImage21Edges, QWEN_IMAGE_21_DIMENSION_MULTIPLE, QWEN_IMAGE_21_MODEL_ID, resolveQwenImage21BorderLock, resolveQwenImage21ReferenceScale, resolveQwenImage21TaskGeometry } from '#lib/qwen-image-21';
import { fitQwenImage3Reference, resolveQwenImage3TaskGeometry } from '#lib/qwen-image-3';
import { resolveAutoColorMatch, TASK_AUTO_INTEGRATE_BY_MODEL } from '#lib/layer-integration';
import { clientAccessSuspension, isModelInFamilies } from '#lib/access-suspension';
import { vectorTraceModelFor } from '#lib/vector-models';


// When the Master agent (mode:'master') drives a Studio generation from its own
// session, it has no active Studio project view of its own, so the normal
// active-session ownership check in `resolveTaskChatSessionId` would tag the
// resulting generation card to no session (it lands nowhere). While a master run
// is executing, the RUN_STUDIO_STEP command sets this module-level pointer to the
// MASTER session id via `setMasterRunChatSessionId`, so every generation produced
// during that run is tagged to the master session's timeline (rendering as the
// existing downloadable generation card) regardless of what, if anything, the
// operator has active. It is a single override point that covers all task-build
// call sites of `resolveTaskChatSessionId`. Always cleared (set back to null) in
// a finally block once the run completes.
let MASTER_RUN_CHAT_SESSION_ID: string | null = null;

const MODELS_WITH_DISABLED_ANNOTATION_OVERLAY: ReadonlySet<string> = new Set([
  'fater-enhance',
  'fater-upscale',
  'topaz-image-enhance',
  'magnific-image-upscale',
  // Inpaint uses the dedicated Mask Pen mask, not the annotation guide, keep the source clean.
  'ideogram-inpaint',
]);

const drawBitmapToBlob = async (
  bitmap: ImageBitmap,
  width: number,
  height: number,
  mimeType: string,
  label: string,
  quality?: number,
): Promise<Blob> => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error(`Could not prepare the ${label}.`);
  context.drawImage(bitmap, 0, 0, width, height);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, mimeType, quality));
  if (!blob) throw new Error(`Could not encode the ${label}.`);
  return blob;
};

/** `file`, the render of `area`, with an opaque red band over its full height at the band's world x and width. */
const paintRedBand = async (file: File, area: Rect, band: { x: number; w: number }, mimeType: string): Promise<File> => {
  const bitmap = await createImageBitmap(file);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Could not prepare the main image.');
    context.drawImage(bitmap, 0, 0);
    context.fillStyle = '#ff0000';
    context.fillRect(((band.x - area.x) / area.w) * bitmap.width, 0, (band.w / area.w) * bitmap.width, bitmap.height);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, mimeType, 0.95));
    if (!blob) throw new Error('Could not encode the main image.');
    return new File([blob], file.name, { type: mimeType });
  } finally {
    bitmap.close();
  }
};

// Canvas redraw keeps the alpha channel of the encoded format; the caller decides the fit, and
// asks for reencode when the provider constrains the format and not only the dimensions.
const redrawGenerationInput = async (
  file: File,
  fit: (width: number, height: number) => { width: number; height: number },
  mimeType: string,
  label: string,
  reencode = false,
): Promise<File> => {
  const bitmap = await createImageBitmap(file);
  try {
    const { width, height } = fit(bitmap.width, bitmap.height);
    if (!reencode && width === bitmap.width && height === bitmap.height) return file;
    const blob = await drawBitmapToBlob(bitmap, width, height, mimeType, label, 0.95);
    // The upload reads the extension off the name before the MIME type, so a reencoded file kept
    // under its old name would be served as the format it no longer carries.
    const name = reencode
      ? `${file.name.replace(/\.[^/.]+$/, '')}.${mimeType.split('/')[1] ?? 'bin'}`
      : file.name;
    return new File([blob], name, { type: mimeType });
  } finally {
    if (typeof bitmap.close === 'function') bitmap.close();
  }
};

/** `file` with every pixel outside `inside` replaced by the nearest pixel inside it (extendQwenImage21Edges). */
const extendEdgesPast = async (
  file: File,
  inside: { left: number; top: number; right: number; bottom: number },
  mimeType: string,
): Promise<File> => {
  const bitmap = await createImageBitmap(file);
  try {
    const { width, height } = bitmap;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Could not prepare the main image.');
    context.drawImage(bitmap, 0, 0);
    const pixels = context.getImageData(0, 0, width, height);
    pixels.data.set(extendQwenImage21Edges(pixels, inside));
    context.putImageData(pixels, 0, 0);
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, mimeType, 0.95));
    if (!blob) throw new Error('Could not encode the main image.');
    return new File([blob], file.name, { type: mimeType });
  } finally {
    bitmap.close();
  }
};

// Build both Ideogram's B/W provider mask and an alpha-only display mask for the canvas pulse.
// Both are rasterized from the same vector Mask Pen shapes so the generation request and its
// feedback overlay stay pixel-aligned across every launch path.
const buildInpaintMaskBlobs = async (
  shapes: MaskShape[],
  frameRect: Rect,
  rect: Rect,
  targetWidth: number,
  targetHeight: number,
): Promise<{ providerMaskBlob: Blob; pulseMaskBlob?: Blob } | null> => {
  const { width: frameRasterW, height: frameRasterH } = getWorkspaceRasterDimensions(frameRect);
  const srcCanvas = document.createElement('canvas');
  srcCanvas.width = frameRasterW;
  srcCanvas.height = frameRasterH;
  const srcCtx = srcCanvas.getContext('2d', { willReadFrequently: true });
  if (!srcCtx) return null;
  // Paint each shape opaque; the alpha channel is thresholded to B/W below.
  srcCtx.fillStyle = '#ffffff';
  srcCtx.strokeStyle = '#ffffff';
  srcCtx.lineCap = 'round';
  srcCtx.lineJoin = 'round';
  for (const shape of shapes) {
    const pts = shape.points;
    const first = pts[0];
    if (!first) continue;
    if (shape.kind === 'polygon') {
      if (pts.length < 3) continue;
      srcCtx.beginPath();
      srcCtx.moveTo(first.x, first.y);
      for (let i = 1; i < pts.length; i++) {
        const p = pts[i];
        if (p) srcCtx.lineTo(p.x, p.y);
      }
      srcCtx.closePath();
      srcCtx.fill();
    } else {
      const strokeWidth = Math.max(1, shape.width ?? 1);
      srcCtx.lineWidth = strokeWidth;
      if (pts.length === 1) {
        srcCtx.beginPath();
        srcCtx.arc(first.x, first.y, strokeWidth / 2, 0, 2 * Math.PI);
        srcCtx.fill();
      } else {
        srcCtx.beginPath();
        srcCtx.moveTo(first.x, first.y);
        for (let i = 1; i < pts.length; i++) {
          const p = pts[i];
          if (p) srcCtx.lineTo(p.x, p.y);
        }
        srcCtx.stroke();
      }
    }
  }

  const scaleX = frameRect.w > 0 ? frameRasterW / frameRect.w : 1;
  const scaleY = frameRect.h > 0 ? frameRasterH / frameRect.h : 1;
  const sx = (rect.x - frameRect.x) * scaleX;
  const sy = (rect.y - frameRect.y) * scaleY;
  const sw = rect.w * scaleX;
  const sh = rect.h * scaleY;

  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.round(targetWidth));
  out.height = Math.max(1, Math.round(targetHeight));
  const octx = out.getContext('2d', { willReadFrequently: true });
  if (!octx) return null;
  octx.clearRect(0, 0, out.width, out.height);
  octx.drawImage(srcCanvas, sx, sy, sw, sh, 0, 0, out.width, out.height);

  // Before thresholding, the painted region is opaque and everything else is transparent: this
  // is the alpha mask consumed by GeneratingLayers. Capture it now so the pulse and the provider
  // request share one launch-time geometry snapshot without retaining the live vector mask.
  const pulseMaskBlob = await new Promise<Blob | null>((resolve) => out.toBlob(resolve, 'image/png'));
  const od = octx.getImageData(0, 0, out.width, out.height);
  const d = od.data;
  for (let i = 0; i < d.length; i += 4) {
    if ((d[i + 3] ?? 0) > 10) {
      d[i] = 0; d[i + 1] = 0; d[i + 2] = 0; d[i + 3] = 255; // painted → edit → BLACK
    } else {
      d[i] = 255; d[i + 1] = 255; d[i + 2] = 255; d[i + 3] = 255; // keep → WHITE
    }
  }
  octx.putImageData(od, 0, 0);
  const providerMaskBlob = await new Promise<Blob | null>((resolve) => out.toBlob(resolve, 'image/png'));
  if (!providerMaskBlob) return null;
  return pulseMaskBlob ? { providerMaskBlob, pulseMaskBlob } : { providerMaskBlob };
};

const SEEDANCE_WORKSPACE_REFERENCE_TOKEN_REGEX = /@workspace\b/gi;

const imageDataToPngBlob = async (imageData: ImageData): Promise<Blob> => {
  const canvas = document.createElement('canvas');
  canvas.width = imageData.width;
  canvas.height = imageData.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not prepare layer image for vectorization.');
  }
  ctx.putImageData(imageData, 0, 0);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) {
    throw new Error('Could not encode layer image for vectorization.');
  }
  return blob;
};

const RECRAFT_VECTORIZE_WORKSPACE_MAX_FILE_BYTES = 5_000_000;
const RECRAFT_VECTORIZE_WORKSPACE_START_MAX_PIXELS = 8_000_000;
const RECRAFT_VECTORIZE_WORKSPACE_MIN_PIXELS = 256 * 256;
const RECRAFT_VECTORIZE_WORKSPACE_RETRY_PIXEL_SCALE = 0.7;

// The trace refuses a file over 5 MB: a heavier picture goes as a smaller PNG of itself, shrunk as the workspace trace is.
const shrinkImageForTrace = async (source: Blob | ImageData): Promise<Blob> => {
  if (source instanceof Blob && source.size <= RECRAFT_VECTORIZE_WORKSPACE_MAX_FILE_BYTES) return source;
  const imageData = source instanceof Blob ? await loadImageDataFromBlob(source) : source;
  let budget = Math.min(RECRAFT_VECTORIZE_WORKSPACE_START_MAX_PIXELS, imageData.width * imageData.height);
  for (let attempt = 0; attempt < 12; attempt += 1) {
    const png = await imageDataToPngBlob(resizeImageDataToPixelBudget(imageData, budget));
    if (png.size <= RECRAFT_VECTORIZE_WORKSPACE_MAX_FILE_BYTES) return png;
    const next = Math.max(RECRAFT_VECTORIZE_WORKSPACE_MIN_PIXELS, Math.floor(budget * RECRAFT_VECTORIZE_WORKSPACE_RETRY_PIXEL_SCALE));
    if (next >= budget) break;
    budget = next;
  }
  throw new Error('This picture stays over 5 MB even shrunk, so it cannot be vectorised.');
};

const compileSeedanceWorkspaceReferencePrompt = (
  prompt: unknown,
  workspaceReferenceImageIndex: number | null,
): string => {
  const normalizedPrompt = typeof prompt === 'string' ? prompt.trim() : '';
  if (!workspaceReferenceImageIndex || workspaceReferenceImageIndex <= 0) {
    return normalizedPrompt;
  }
  return normalizedPrompt.replace(
    SEEDANCE_WORKSPACE_REFERENCE_TOKEN_REGEX,
    `@image${workspaceReferenceImageIndex}`,
  );
};

const createAbortError = (): Error => {
  const error = new Error('Operation aborted.');
  error.name = 'AbortError';
  return error;
};

const normalizeNumOutputs = (value: unknown): number => (
  typeof value === 'number' && Number.isFinite(value) ? value : 1
);

const normalizeLockableFiles = (value: unknown): LockableFile[] => (
  Array.isArray(value) ? (value as LockableFile[]) : []
);

const isAbortError = (error: unknown): boolean => {
  return typeof error === 'object' && error !== null && (error as { name?: string }).name === 'AbortError';
};

const throwIfAborted = (abortSignal?: AbortSignal): void => {
  if (abortSignal?.aborted) {
    throw createAbortError();
  }
};

const fetchReferenceImageFile = async (url: string, abortSignal?: AbortSignal): Promise<File> => {
  const response = await fetch(url, { signal: abortSignal });
  if (!response.ok) throw new Error(`Failed to fetch reference image at ${url}`);
  const blob = await response.blob();
  return new File([blob], url.substring(url.lastIndexOf('/') + 1), { type: blob.type });
};

// Workspaces running Develop 360: two runs on one workspace would interleave their steps and references.
const streetDevelopRuns = new Set<string>();
const STREET_RUN_BUSY = 'Develop 360, Texturise or Repair cuts is already running on this workspace.';
// Workspaces running Recast extras, each with the stop asked from its menu, honoured before the next zone.
const recastExtrasRuns = new Map<string, { stopRequested: boolean }>();
const recastDetections = new Set<string>();

/**
 * One generation of the Recast extras recipe on a workspace, on `rect` or else its generation area, resolving once it
 * landed; `failed` words its errors. Refuses an unknown recipe model when made, before any run starts.
 */
const recastGeneration = (k: KernelHandle, workspaceId: string) => {
  const { model, params } = RECAST_EXTRAS_RECIPE;
  const defaultParams = getDefaultParamsForModel(model);
  if (!defaultParams) throw new Error('Recast extras names an unknown model.');
  return async (prompt: string, rect: Rect | undefined, failed: (reason: string) => Error, abortSignal?: AbortSignal): Promise<void> => {
    k.prepareWorkspaceForGeneration(workspaceId);
    // The region lock already joins the area to what surrounds it: a colour match would recolour it.
    const { tasks } = await k.runModel(workspaceId, {
      diffusionModel: model,
      parameters: { ...k.getModelParams(), [model]: { ...defaultParams(), ...params, prompt } as DiffusionParams },
      referenceImages: [],
    }, 1, abortSignal, { rect, autoColorMatch: false });
    if (!tasks[0]) throw failed('the generation did not start.');
    const completion = await k.waitForTask(tasks[0].id);
    if (completion.status === 'FAILURE') {
      throw failed(completion.error instanceof Error ? completion.error.message : String(completion.error ?? 'the generation failed.'));
    }
  };
};

/** A square of the rendered frame, downscaled to what SAM takes. */
const recastTileJpeg = (bitmap: ImageBitmap, x: number, y: number, side: number): Promise<Blob> => {
  const out = Math.min(side, RECAST_TILE_MAX_PX);
  const canvas = new OffscreenCanvas(out, out);
  const context = canvas.getContext('2d');
  if (!context) throw new Error('The image could not be cut into tiles.');
  context.imageSmoothingQuality = 'high';
  context.drawImage(bitmap, x, y, side, side, 0, 0, out, out);
  return canvas.convertToBlob({ type: 'image/jpeg', quality: 0.92 });
};

const SEEDANCE_REFERENCE_VIDEO_MAX_DURATION_MS = 15_000;
const SEEDANCE_REFERENCE_VIDEO_MIN_DURATION_MS = 3_000;
const KLING_OMNI_REFERENCE_VIDEO_MIN_DURATION_MS = 3_000;
const KLING_OMNI_REFERENCE_VIDEO_MAX_DURATION_MS = 10_000;
const TIMELINE_AUDIO_ONLY_PARAM_KEY = '__timelineAudioOnly';
const TIMELINE_MUTE_AUDIO_PARAM_KEY = '__timelineMuteAudio';
const TIMELINE_AUDIO_GAIN_PARAM_KEY = '__timelineAudioGain';
const TIMELINE_FADE_IN_RATIO_PARAM_KEY = '__timelineFadeInRatio';
const TIMELINE_FADE_OUT_RATIO_PARAM_KEY = '__timelineFadeOutRatio';
const TOP_LAYER_RENDER_ORDER_OFFSET = 1_000_000;
const AUDIO_GAIN_MAX = 8;
const TIMELINE_REFERENCE_MAX_DIMENSION = 4096;
const TIMELINE_REFERENCE_MAX_DURATION_MS = 60 * 60 * 1000;

// Input-resolution budgets (in PIXELS) for timeline reference clips / composed
// start frames. The workspace frameRect is purely a UI canvas, the user's
// coordinate space for organizing an infinite dashboard, and has NOTHING to do
// with content resolution: it only supplies the ASPECT RATIO here. Sizing the
// composition to the frameRect would downsample high-res sources (1080p/2K/4K)
// before they reach a generation model (e.g. a 4K clip degraded to 720p, then
// handed to Topaz/Magnific/Kling/Seedance). Instead the pixel budget is:
//   target = clamp( min(taskMaxPixels, nativePixels), 1 MP floor, taskMaxPixels )
// i.e. the highest resolution the target task supports, bounded by the source's
// native pixels (never fabricate detail), with a hard 1 megapixel floor for ANY
// ratio. Native unknown → target the task max (never under-serve).
const VIDEO_UPSCALE_MODEL_IDS = new Set<string>(['topaz-video-upscale', 'magnific-video-upscale']);
const REFERENCE_CLIP_MIN_PIXELS = 1_000_000;                // 1 MP floor, any ratio, independent of the frameRect
const REFERENCE_CLIP_MAX_PIXELS_UPSCALE = 2560 * 1440;      // ~2K master so the upscale factor multiplies real detail
const REFERENCE_CLIP_MAX_PIXELS_EDIT = 1920 * 1080;         // 1080p, practical input ceiling for edit/generate video models

const resolveReferenceClipMaxPixels = (modelId: string): number =>
  VIDEO_UPSCALE_MODEL_IDS.has(modelId)
    ? REFERENCE_CLIP_MAX_PIXELS_UPSCALE
    : REFERENCE_CLIP_MAX_PIXELS_EDIT;

// The invariant half of a video-edit task, identical whether the source clip was
// composed off the timeline or came straight from a List output. Only the source
// URLs, the placement and the frame size differ, and those ride in `perTask`.
/**
 * Size and duration of a clip WITHOUT downloading it.
 *
 * WHY: the upscale path used to fetch the whole file into the browser and re-upload it just to
 * hand the provider a public URL. On a real 4K clip that is hundreds of megabytes each way, and
 * it died on a 45s fetch timeout with "The source clip took too long to download" before a
 * request was ever made (production, 2026-08-16). A <video preload="metadata"> reads only the
 * header through a Range request, and the server now signs the stored blob for the provider, so
 * no clip bytes cross the browser at all.
 */
const probeVideoMetadataByUrl = (
  url: string,
  timeoutMs = 15_000,
): Promise<{ width: number; height: number; duration: number }> =>
  new Promise((resolve) => {
    const video = document.createElement('video');
    let settled = false;
    const finish = (result: { width: number; height: number; duration: number }) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      video.onloadedmetadata = null;
      video.onerror = null;
      video.removeAttribute('src');
      video.load();
      resolve(result);
    };
    // Zeroes are an accepted answer, never a failure: the caller falls back to the frame rect,
    // and the provider needs the bytes, not our measurements.
    const timer = setTimeout(() => finish({ width: 0, height: 0, duration: 0 }), timeoutMs);
    video.preload = 'metadata';
    video.muted = true;
    video.onloadedmetadata = () => finish({
      width: video.videoWidth || 0,
      height: video.videoHeight || 0,
      duration: Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 0,
    });
    video.onerror = () => finish({ width: 0, height: 0, duration: 0 });
    video.src = url;
  });

const buildVideoEditTasks = (
  base: {
    workspaceId: string;
    projectId: string;
    chatSessionId: string | null;
    isAgentSpawned: boolean;
    model: string;
    params: TopazVideoUpscaleParams | MireloSfxParams | MagnificVideoUpscaleParams;
    numGenerations: number;
    layerContextSnapshot: GenerationTask['layerContextSnapshot'];
    parentVersionId: string | null;
    outputScope: GenerationTask['outputScope'];
  },
  // Placement and frame size are never invariant, so they stay required: a third
  // caller that forgets `rect` must not compile, it would throw at merge time.
  perTask: Pick<GenerationTask, 'rect' | 'targetWidth' | 'targetHeight'> & Partial<GenerationTask>,
): GenerationTask[] =>
  Array.from({ length: base.numGenerations }, () => ({
    id: crypto.randomUUID(),
    chatSessionId: base.chatSessionId,
    isAgentSpawned: base.isAgentSpawned,
    type: 'film' as const,
    workspaceId: base.workspaceId,
    params: {
      ...base.params,
      seed: (base.numGenerations === 1 && base.params.seed !== -1)
        ? base.params.seed
        : Math.floor(Math.random() * 2100000000),
    },
    model: base.model,
    mergeStrategy: 'addInList' as const,
    upscaleFactor: 1,
    blurRadius: 0,
    projectId: base.projectId,
    outputScope: base.outputScope,
    layerContextSnapshot: base.layerContextSnapshot,
    parentVersionId: base.parentVersionId,
    ...perTask,
    // Fresh per task: consumers only read these today, but sharing one object
    // across N tasks makes any future in-place edit corrupt its siblings.
    rect: { ...perTask.rect },
  }));

// frameRect supplies ONLY the aspect ratio; the pixel budget comes from the task
// (bounded by native, floored at 1 MP). fallbackW/H are used solely to recover a
// ratio when neither the frameRect nor the native dims are usable.
const resolveReferenceClipDimensions = ({
  frameRectW,
  frameRectH,
  nativeW,
  nativeH,
  maxPixels,
  fallbackW,
  fallbackH,
}: {
  frameRectW: number | null;
  frameRectH: number | null;
  nativeW: number | null;
  nativeH: number | null;
  maxPixels: number;
  fallbackW: number;
  fallbackH: number;
}): { width: number; height: number } => {
  const ratioW = frameRectW && frameRectW > 1 ? frameRectW : (nativeW && nativeW > 1 ? nativeW : fallbackW);
  const ratioH = frameRectH && frameRectH > 1 ? frameRectH : (nativeH && nativeH > 1 ? nativeH : fallbackH);
  if (!(ratioW > 0) || !(ratioH > 0)) {
    return { width: normalizeVideoDimension(fallbackW), height: normalizeVideoDimension(fallbackH) };
  }
  const nativePixels = (nativeW ?? 0) > 1 && (nativeH ?? 0) > 1 ? (nativeW as number) * (nativeH as number) : null;
  const cappedMaxPixels = Math.min(maxPixels, TIMELINE_REFERENCE_MAX_DIMENSION * TIMELINE_REFERENCE_MAX_DIMENSION);
  const targetPixels = Math.max(
    REFERENCE_CLIP_MIN_PIXELS,
    Math.min(cappedMaxPixels, nativePixels ?? cappedMaxPixels),
  );
  // Derive dimensions from the target area at the frameRect aspect ratio.
  const aspect = ratioW / ratioH;
  let width = Math.sqrt(targetPixels * aspect);
  let height = Math.sqrt(targetPixels / aspect);
  // Respect the per-side hard cap without distorting the ratio.
  const sideScale = Math.min(
    1,
    TIMELINE_REFERENCE_MAX_DIMENSION / Math.max(width, height),
  );
  width *= sideScale;
  height *= sideScale;
  return {
    width: normalizeVideoDimension(width),
    height: normalizeVideoDimension(height),
  };
};

const formatSeconds = (durationMs: number): string => {
  const seconds = Math.max(0, durationMs) / 1000;
  return Number.isInteger(seconds) ? `${seconds.toFixed(0)}s` : `${seconds.toFixed(2)}s`;
};

const readLayerParamFlag = (params: unknown, flagKey: string): boolean => {
  if (!params || typeof params !== 'object' || Array.isArray(params)) return false;
  const value = (params as Record<string, unknown>)[flagKey];
  if (value === true) return true;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1' || normalized === 'yes' || normalized === 'on';
  }
  return false;
};

const readLayerParamNumber = (params: unknown, key: string): number | null => {
  if (!params || typeof params !== 'object' || Array.isArray(params)) return null;
  return toFinite((params as Record<string, unknown>)[key]);
};

const normalizeTrackEffectRatio = (value: unknown): number =>
  clamp(toFinite(value) ?? 0, 0, 1);

type TimelineReferenceLayerType = 'video' | 'image' | 'audio';

type TimelineReferenceTrack = {
  layer: Layer;
  layerType: TimelineReferenceLayerType;
  renderOrder: number;
  startMs: number;
  endMs: number;
  sourceInMs: number;
  mediaDurationMs?: number | null;
  audioEnabled?: boolean;
  audioGain?: number;
  playbackRate?: number;
  fadeInRatio?: number;
  fadeOutRatio?: number;
};

type VideoInputRangeRequest = {
  startMs: number | null;
  endMs: number | null;
  hasExplicitRange: boolean;
  error: string | null;
};

const readVideoInputRangeRequest = (params: unknown): VideoInputRangeRequest => {
  const source = params && typeof params === 'object' && !Array.isArray(params)
    ? params as Record<string, unknown>
    : {};
  const readOptionalTime = (key: string, label: string): { value: number | null; provided: boolean; error: string | null } => {
    const raw = source[key];
    if (raw === null || raw === undefined) return { value: null, provided: false, error: null };
    if (typeof raw === 'string' && raw.trim().length === 0) {
      return { value: null, provided: false, error: null };
    }
    const parsed = toFinite(raw);
    if (parsed === null || parsed < 0) {
      return { value: null, provided: true, error: `${label} must be a non-negative millisecond value.` };
    }
    return { value: Math.round(parsed), provided: true, error: null };
  };

  const start = readOptionalTime('video_input_start_ms', 'Video input start');
  if (start.error) {
    return { startMs: null, endMs: null, hasExplicitRange: true, error: start.error };
  }
  const end = readOptionalTime('video_input_end_ms', 'Video input end');
  if (end.error) {
    return { startMs: start.value, endMs: null, hasExplicitRange: true, error: end.error };
  }
  return {
    startMs: start.value,
    endMs: end.value,
    hasExplicitRange: start.provided || end.provided,
    error: null,
  };
};

const resolveExplicitVideoInputRange = ({
  request,
  tracks,
  fallbackEndMs = 0,
}: {
  request: VideoInputRangeRequest;
  tracks: TimelineReferenceTrack[];
  fallbackEndMs?: number;
}): { startMs: number; endMs: number } | { error: string } | null => {
  if (!request.hasExplicitRange) return null;
  if (request.error) return { error: request.error };
  const validTracks = tracks.filter((track) => track.endMs > track.startMs);
  const timelineEndMs = validTracks.length > 0
    ? validTracks.reduce((max, track) => Math.max(max, track.endMs), Math.max(0, fallbackEndMs))
    : Math.max(0, fallbackEndMs);
  const startMs = Math.max(0, Math.round(request.startMs ?? 0));
  const endMs = Math.max(0, Math.round(request.endMs ?? timelineEndMs));
  if (endMs <= startMs) {
    return { error: 'Video input end time must be after the start time.' };
  }
  return { startMs, endMs };
};

const validateVideoInputDuration = (
  label: string,
  durationMs: number,
  minMs?: number,
  maxMs?: number,
): string | null => {
  if (!Number.isFinite(durationMs) || durationMs <= 0) {
    return `${label} video input range is empty.`;
  }
  if (typeof minMs === 'number' && durationMs < minMs) {
    return `${label} video input is too short. Minimum is ${formatSeconds(minMs)}; selected range is ${formatSeconds(durationMs)}.`;
  }
  if (typeof maxMs === 'number' && durationMs > maxMs) {
    return `${label} video input is too long. Maximum is ${formatSeconds(maxMs)}; selected range is ${formatSeconds(durationMs)}.`;
  }
  return null;
};

const normalizeTimelineReferenceTracks = (
  tracks: TimelineReferenceTrack[],
  range?: { startMs: number; endMs: number } | null,
): TimelineTrackInput[] => (
  tracks
    .filter((track) => !range || (track.endMs > range.startMs && track.startMs < range.endMs))
    .map((track) => {
      const imageSource = track.layerType === 'image'
        ? (isLayerVectorized(track.layer)
            ? getDrawingAwareSvgLayerSource({
                imageSrc: typeof track.layer.imageSrc === 'string' ? track.layer.imageSrc : undefined,
                params: track.layer.params ?? undefined,
              })
            : (getDrawingLayerSvgSource(track.layer.params)?.source ?? (typeof track.layer.imageSrc === 'string' ? track.layer.imageSrc.trim() : '')))
        : '';
      const sourceSrc = track.layerType === 'video' || track.layerType === 'audio'
        ? (typeof track.layer.videoSrc === 'string' ? track.layer.videoSrc.trim() : '')
        : imageSource;
      const transform = track.layerType === 'audio'
        ? { rotationDeg: 0, flipX: false, flipY: false }
        : resolveStudioLayerTransform(track.layer.params);
      return {
        layerId: track.layer.id,
        layerType: track.layerType,
        sourceSrc,
        videoSrc: track.layerType === 'video' ? sourceSrc : null,
        imageSrc: track.layerType === 'image' ? sourceSrc : null,
        audioEnabled: track.layerType === 'video'
          ? track.audioEnabled !== false
          : (track.layerType === 'audio' ? true : false),
        audioGain: clamp(toFinite(track.audioGain) ?? 1, 0, AUDIO_GAIN_MAX),
        renderOrder: track.renderOrder,
        startMs: Math.max(0, Math.round(track.startMs)),
        endMs: Math.max(Math.round(track.startMs + 1), Math.round(track.endMs)),
        sourceInMs: track.layerType === 'image' ? 0 : Math.max(0, Math.round(track.sourceInMs)),
        playbackRate: track.layerType === 'image' ? 1 : normalizeTimelineTrackPlaybackRate(track.playbackRate),
        fadeInRatio: track.layerType === 'audio' ? 0 : normalizeTrackEffectRatio(track.fadeInRatio),
        fadeOutRatio: track.layerType === 'audio' ? 0 : normalizeTrackEffectRatio(track.fadeOutRatio),
        rotationDeg: transform.rotationDeg,
        flipX: transform.flipX,
        flipY: transform.flipY,
        rect: {
          x: Number.isFinite(track.layer.rect?.x) ? Number(track.layer.rect.x) : 0,
          y: Number.isFinite(track.layer.rect?.y) ? Number(track.layer.rect.y) : 0,
          w: Number.isFinite(track.layer.rect?.w) ? Number(track.layer.rect.w) : 0,
          h: Number.isFinite(track.layer.rect?.h) ? Number(track.layer.rect.h) : 0,
        },
        scale: typeof track.layer.scale === 'number' && Number.isFinite(track.layer.scale) ? track.layer.scale : 1,
      };
    })
    .filter((track) => typeof track.sourceSrc === 'string' && track.sourceSrc.trim().length > 0)
);

const hasVisibleAnnotationsInRenderArea = (
  annotationMask: ImageData | null | undefined,
  frameRect: Rect,
  areaToRender: Rect
): boolean => {
  if (!annotationMask) return false;
  if (frameRect.w <= 0 || frameRect.h <= 0) return false;
  if (areaToRender.w <= 0 || areaToRender.h <= 0) return false;

  const maskWidth = annotationMask.width;
  const maskHeight = annotationMask.height;
  if (maskWidth <= 0 || maskHeight <= 0) return false;

  const scaleX = maskWidth / frameRect.w;
  const scaleY = maskHeight / frameRect.h;

  const sx = (areaToRender.x - frameRect.x) * scaleX;
  const sy = (areaToRender.y - frameRect.y) * scaleY;
  const sw = areaToRender.w * scaleX;
  const sh = areaToRender.h * scaleY;

  const startX = Math.max(0, Math.floor(sx));
  const startY = Math.max(0, Math.floor(sy));
  const endX = Math.min(maskWidth, Math.ceil(sx + sw));
  const endY = Math.min(maskHeight, Math.ceil(sy + sh));

  if (endX <= startX || endY <= startY) return false;

  const data = annotationMask.data;
  const rowStride = maskWidth * 4;

  for (let y = startY; y < endY; y++) {
    const rowOffset = y * rowStride;
    for (let x = startX; x < endX; x++) {
      const alpha = data[rowOffset + x * 4 + 3] || 0;
      if (alpha > 0) return true;
    }
  }

  return false;
};

interface useModelsProps {
  kernel: React.RefObject<KernelHandle | null>;
}

const createOutputOnlyWorkspace = (projectId: string, sizing?: Pick<Workspace, 'generationRect' | 'frameRect' | 'aspectRatio'>): Workspace => ({
  id: '',
  projectId,
  name: 'Output',
  layerIds: [],
  activeVersionId: null,
  generationRect: sizing?.generationRect ?? { x: 0, y: 0, w: 1024, h: 1024 },
  aspectRatio: sizing?.aspectRatio ?? 1,
  frameRect: sizing?.frameRect ?? { x: 0, y: 0, w: 1024, h: 1024 },
  annotationMask: null,
  annotationOpaque: false,
  expanded: false,
  locked: true,
  visible: true,
  validatedVersionId: null,
});

const useModels = ({
  kernel
}: useModelsProps) : useModelsReturnType => {
  const getProjectIdFromWorkspaceId = useCallback((workspaceId: string, source: string): string => {
    if (!kernel.current) {
      throw new Error(`${source}: kernel indisponible.`);
    }
    const normalizedWorkspaceId = typeof workspaceId === 'string' ? workspaceId.trim() : '';
    if (!normalizedWorkspaceId) {
      throw new Error(`${source}: workspaceId manquant.`);
    }
    const workspace = kernel.current.getInventory().StudioWorkspace.registry[normalizedWorkspaceId];
    const projectId = typeof workspace?.projectId === 'string' ? workspace.projectId.trim() : '';
    if (!projectId) {
      throw new Error(`${source}: projectId introuvable pour workspaceId=${normalizedWorkspaceId}.`);
    }
    return projectId;
  }, [kernel]);

  const resolveTaskChatSessionId = useCallback((projectId: string): string | null => {
    // Master-run override: while the Master is executing a Studio step from its own
    // session, force every generation this run produces to be tagged to the master
    // session's timeline, no active-session ownership or navigation required.
    if (MASTER_RUN_CHAT_SESSION_ID) return MASTER_RUN_CHAT_SESSION_ID;
    const k = kernel.current;
    if (!k) return null;
    const activeSessionId = k.getActiveChatSessionId();
    if (!activeSessionId) return null;
    const sessionProjectId = k.getLastProjectId(activeSessionId);
    if (!sessionProjectId || sessionProjectId.trim() !== projectId.trim()) return null;
    return activeSessionId;
  }, [kernel]);

  // resolveTaskChatSessionId tague pour la timeline, pas pour la propriete : il rend la
  // session active des qu'elle pointe sur le meme projet, que l'agent soit pour quelque
  // chose ou non. Le stop ne doit emporter que les generations du tour agentique en cours,
  // d'ou ce marqueur, fige a la creation pendant que le runner tient encore le verrou de
  // session (runningScriptId). Sans lui, arreter l'agent tuait aussi les rendus manuels.
  const resolveTaskIsAgentSpawned = useCallback((chatSessionId: string | null): boolean => {
    if (!chatSessionId) return false;
    // Un pas Studio pilote par le Master est agentique par construction.
    if (MASTER_RUN_CHAT_SESSION_ID) return true;
    const k = kernel.current;
    if (!k) return false;
    return Boolean(k.readEntityRuntimeState('ChatSession', chatSessionId).runningScriptId);
  }, [kernel]);

  // Set (or clear, with null) the master-run session pointer that forces
  // generation cards produced during a Master-driven Studio run to be tagged to
  // the master session's timeline. See MASTER_RUN_CHAT_SESSION_ID above.
  const setMasterRunChatSessionId = useCallback<KernelHandle['setMasterRunChatSessionId']>((id) => {
    MASTER_RUN_CHAT_SESSION_ID =
      typeof id === 'string' && id.trim().length > 0 ? id.trim() : null;
  }, []);

  const getLocalNumOutputs = useCallback<KernelHandle['getLocalNumOutputs']>((projectIdInput) => {
    const k = kernel.current;
    if (!k) return 1;
    return readProjectRuntimeField(k, projectIdInput, 'localNumOutputs', 1, normalizeNumOutputs);
  }, [kernel]);

  const setLocalNumOutputs = useCallback<KernelHandle['setLocalNumOutputs']>((projectIdInput, updater) => {
    const k = kernel.current;
    if (!k) return;
    patchProjectRuntimeField(k, projectIdInput, 'localNumOutputs', updater, 1, normalizeNumOutputs);
  }, [kernel]);

  const getExtraFiles = useCallback<KernelHandle['getExtraFiles']>((projectIdInput) => {
    const k = kernel.current;
    if (!k) return [];
    return readProjectRuntimeField(k, projectIdInput, 'extraFiles', [], normalizeLockableFiles);
  }, [kernel]);

  const setExtraFiles = useCallback<KernelHandle['setExtraFiles']>((projectIdInput, updater) => {
    const k = kernel.current;
    if (!k) return;
    patchProjectRuntimeField(k, projectIdInput, 'extraFiles', updater, [], normalizeLockableFiles);
  }, [kernel]);

  const getAudioReferenceFiles = useCallback<KernelHandle['getAudioReferenceFiles']>((projectIdInput) => {
    const k = kernel.current;
    if (!k) return [];
    return readProjectRuntimeField(k, projectIdInput, 'audioReferenceFiles', [], normalizeLockableFiles);
  }, [kernel]);

  const setAudioReferenceFiles = useCallback<KernelHandle['setAudioReferenceFiles']>((projectIdInput, updater) => {
    const k = kernel.current;
    if (!k) return;
    patchProjectRuntimeField(k, projectIdInput, 'audioReferenceFiles', updater, [], normalizeLockableFiles);
  }, [kernel]);

  const getVideoLastImages = useCallback<KernelHandle['getVideoLastImages']>((projectIdInput) => {
    const k = kernel.current;
    if (!k) return [];
    return readProjectRuntimeField(k, projectIdInput, 'videoLastImages', [], normalizeLockableFiles);
  }, [kernel]);

  const setVideoLastImages = useCallback<KernelHandle['setVideoLastImages']>((projectIdInput, updater) => {
    const k = kernel.current;
    if (!k) return;
    patchProjectRuntimeField(k, projectIdInput, 'videoLastImages', updater, [], normalizeLockableFiles);
  }, [kernel]);

  const getWorkspaceLayers = useCallback((workspaceId: string): Layer[] => {
    if (!kernel.current) return [];
    return kernel.current.getLayersInWorkspaceContext(workspaceId);
  }, [kernel]);

  const getVisibleWorkspaceLayers = useCallback((workspaceId: string): Layer[] => {
    return getWorkspaceLayers(workspaceId).filter((layer) => layer.visible);
  }, [getWorkspaceLayers]);

  const getContextSnapshot = useCallback((workspaceId: string) => {
    if (!kernel.current) return [];
    const contextLayers = getWorkspaceLayers(workspaceId);
    return contextLayers.map(l => ({ id: l.id, visible: l.visible }));
  }, [getWorkspaceLayers, kernel]);

  const getModelParameterValue = useCallback((modelId: string, parameterName: string): any | undefined => {
    if (!kernel.current) return undefined;
    const currentModelParams = kernel.current.getModelParams();
    const paramsForSpecificModel: Record<string, any> | undefined = currentModelParams[modelId];
    if (paramsForSpecificModel && Object.prototype.hasOwnProperty.call(paramsForSpecificModel, parameterName)) {
      return paramsForSpecificModel[parameterName];
    }
    return undefined;
  }, [kernel]);

  const inferExtensionFromBlob = useCallback((blob: Blob, fallbackExtension = 'bin'): string => {
    if (blob && 'name' in blob && typeof (blob as File).name === 'string') {
      const name = (blob as File).name;
      const match = /\.[^.]+$/.exec(name);
      if (match) {
        return match[0].slice(1).toLowerCase();
      }
    }
    const type = (blob?.type ?? '').toLowerCase();
    if (type.includes('png')) return 'png';
    if (type.includes('jpeg')) return 'jpg';
    if (type.includes('jpg')) return 'jpg';
    if (type.includes('webp')) return 'webp';
    if (type.includes('gif')) return 'gif';
    if (type.includes('mp4')) return 'mp4';
    if (type.includes('webm')) return 'webm';
    if (type.includes('json')) return 'json';
    if (type.includes('pdf')) return 'pdf';
    if (type.includes('octet-stream')) return 'bin';
    if (type.includes('/')) {
      return type.split('/')[1]?.split(';')[0] ?? fallbackExtension;
    }
    return fallbackExtension;
  }, []);

  type DualUploadOptions = {
    ttlSeconds: number;
    filenamePrefix: string;
    fallbackContentType?: string;
    fallbackExtension?: string;
    projectIdOverride?: string;
  };

const dualUploadAsset = useCallback(async (
  blob: Blob,
    { ttlSeconds, filenamePrefix, fallbackContentType = 'application/octet-stream', fallbackExtension = 'bin', projectIdOverride }: DualUploadOptions,
    workspaceId: string,
    abortSignal?: AbortSignal,
  ) => {
    throwIfAborted(abortSignal);
    const projectId = projectIdOverride?.trim() || getProjectIdFromWorkspaceId(workspaceId, 'dualUploadAsset');
    const projectOwnerId = kernel.current?.getInventory().Project.registry[projectId]?.userId;
    const contentType = blob.type && blob.type.trim().length > 0 ? blob.type : fallbackContentType;
    const extension = inferExtensionFromBlob(blob, fallbackExtension);
    const uniqueSuffix = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const filename = `${filenamePrefix}-${uniqueSuffix}.${extension}`;

    const [publicUrl, privateUrl] = await Promise.all([
      getCloudTmpUrl(blob, ttlSeconds, undefined, abortSignal),
      uploadFileToCloud(blob, filename, contentType, abortSignal, projectOwnerId),
    ]);
    throwIfAborted(abortSignal);

    if (!publicUrl) {
      throw new Error('Failed to upload asset to temporary storage.');
    }
    if (!privateUrl) {
      throw new Error('Failed to upload asset to permanent storage.');
    }

    return { publicUrl, privateUrl };
  }, [kernel, inferExtensionFromBlob, getProjectIdFromWorkspaceId]);

  const fluxTwoResolutionToMegapixels = (resolution: FluxTwoProResolution): number | null => {
    switch (resolution) {
      case '0.5 MP':
        return 0.5;
      case '1 MP':
        return 1;
      case '2 MP':
        return 2;
      case '4 MP':
        return 4;
      case 'match_input_image':
        return null; // pas de cible fixe : on retombe sur la taille du workspace
      default:
        return null;
    }
  };

  const dualUploadFiles = useCallback(async (
    files: readonly (Blob | null | undefined)[],
    basePrefix: string,
    ttlSeconds: number,
    workspaceId: string,
    abortSignal?: AbortSignal,
    projectIdOverride?: string,
  ): Promise<{ publicUrls: string[]; privateUrls: string[] }> => {
    if (!files.length) {
      return { publicUrls: [], privateUrls: [] };
    }
    throwIfAborted(abortSignal);
    const uploads = await Promise.all(
      files.map((file, index) => {
        if (!file) {
          return Promise.resolve(null);
        }
        const inferredType = file.type && file.type.trim().length > 0 ? file.type : 'application/octet-stream';
        return dualUploadAsset(file, {
          ttlSeconds,
          filenamePrefix: `${basePrefix}-${index + 1}`,
          fallbackContentType: inferredType,
          fallbackExtension: inferExtensionFromBlob(file, 'bin'),
          projectIdOverride,
        }, workspaceId, abortSignal);
      }),
    );
    throwIfAborted(abortSignal);

    const publicUrls: string[] = [];
    const privateUrls: string[] = [];
    uploads.forEach((result) => {
      if (result) {
        publicUrls.push(result.publicUrl);
        privateUrls.push(result.privateUrl);
      }
    });

    return { publicUrls, privateUrls };
  }, [dualUploadAsset, inferExtensionFromBlob]);

  const prepareTimelineReferenceClipFromCursor = useCallback(async ({
    workspaceId,
    tracks,
    playbackStartMs,
    playbackEndMs,
    cursorMs,
    filenamePrefix,
    abortSignal,
  }: {
    workspaceId: string;
    tracks: TimelineReferenceTrack[];
    playbackStartMs: number;
    playbackEndMs: number;
    cursorMs: number;
    filenamePrefix: string;
    abortSignal?: AbortSignal;
    // Accepted (and currently ignored) so the three call sites that pass output
    // dimensions type-check. Added to unbreak a pre-existing preprod build failure
    // from the timeline commits (which added these to the callers but not here).
    // TODO(timeline): honor these to size the trimmed reference clip if intended.
    outputWidth?: number;
    outputHeight?: number;
    outputFps?: number;
  }): Promise<{
    publicUrl: string;
    privateUrl: string;
    timelineInsertStartMs: number;
    remainingTimelineSeconds: number | undefined;
  }> => {
    throwIfAborted(abortSignal);

    if (tracks.length === 0) {
      throw new Error('No timeline tracks were available for video reference input.');
    }
    if (!Number.isFinite(playbackStartMs) || !Number.isFinite(playbackEndMs) || playbackEndMs <= playbackStartMs) {
      throw new Error('Timeline playback range is invalid.');
    }

    const clampedCursorMs = Math.max(playbackStartMs, Math.min(cursorMs, playbackEndMs));
    const remainingMs = Math.max(0, Math.round(playbackEndMs - clampedCursorMs));
    if (remainingMs <= 0) {
      throw new Error('No remaining timeline segment after the current cursor.');
    }

    const normalizedTracks = normalizeTimelineReferenceTracks(tracks);

    if (normalizedTracks.length === 0) {
      throw new Error('No valid timeline track sources were available for video reference input.');
    }

    const resolveTrimApiUrl = (relativePath: string): string => {
      if (typeof window !== 'undefined' && typeof window.location?.origin === 'string' && window.location.origin.length > 0) {
        return new URL(relativePath, window.location.origin).toString();
      }
      return relativePath;
    };

    const waitWithAbort = (ms: number): Promise<void> => (
      new Promise<void>((resolve, reject) => {
        const timeoutId = setTimeout(() => resolve(), ms);
        if (!abortSignal) return;
        abortSignal.addEventListener('abort', () => {
          clearTimeout(timeoutId);
          reject(createAbortError());
        }, { once: true });
      })
    );

    const fetchTrimApi = async (relativePath: string, init: RequestInit, retries = 2): Promise<Response> => {
      const requestUrl = resolveTrimApiUrl(relativePath);
      let lastError: unknown = null;
      for (let attempt = 0; attempt <= retries; attempt++) {
        throwIfAborted(abortSignal);
        try {
          return await fetch(requestUrl, { ...init, signal: abortSignal });
        } catch (error) {
          lastError = error;
          if (isAbortError(error) || abortSignal?.aborted) {
            throw createAbortError();
          }
          console.warn('[TimelineTrim] fetch retry scheduled', {
            requestUrl,
            attempt: attempt + 1,
            retries,
            error: error instanceof Error ? error.message : String(error),
          });
          if (attempt >= retries) break;
          await waitWithAbort(250 * (attempt + 1));
        }
      }
      if (lastError instanceof Error) throw lastError;
      throw new Error('Failed to fetch trim API.');
    };

    // Fast path for the no-explicit-range contract: one active source video track.
    const maybeSingleTrack = normalizedTracks[0];
    const maybeSingleTrackPlaybackRate = normalizeTimelineTrackPlaybackRate(maybeSingleTrack?.playbackRate);
    if (
      normalizedTracks.length === 1 &&
      maybeSingleTrack?.layerType === 'video' &&
      maybeSingleTrackPlaybackRate === 1
    ) {
      const singleTrack = maybeSingleTrack;
      const sourceVideoSrc = typeof singleTrack.videoSrc === 'string' ? singleTrack.videoSrc.trim() : '';
      if (sourceVideoSrc) {
        const relativeCursorMs = Math.max(0, Math.round(clampedCursorMs - singleTrack.startMs));
        const sourceClipStartMs = Math.max(0, Math.round(singleTrack.sourceInMs + relativeCursorMs * maybeSingleTrackPlaybackRate));
        const sourceClipEndMs = Math.max(
          sourceClipStartMs + 1,
          Math.round(sourceClipStartMs + remainingMs * maybeSingleTrackPlaybackRate),
        );

        const sourceResponse = await fetch(sourceVideoSrc, {
          cache: 'no-store',
          signal: abortSignal,
        });
        if (!sourceResponse.ok) {
          throw new Error(`Failed to download source video track (${sourceResponse.status}).`);
        }
        const sourceVideoBlob = await sourceResponse.blob();
        if (!sourceVideoBlob || sourceVideoBlob.size <= 0) {
          throw new Error('Source video track is empty.');
        }

        const trimPrepareResponse = await fetchTrimApi(
          `/api/export/trim-video?mode=prepare&startMs=${sourceClipStartMs}&endMs=${sourceClipEndMs}&includeAudio=true`,
          {
            method: 'POST',
            headers: {
              'Content-Type': sourceVideoBlob.type || 'video/mp4',
            },
            body: sourceVideoBlob,
          },
        );
        if (!trimPrepareResponse.ok) {
          const errorText = await trimPrepareResponse.text().catch(() => '');
          throw new Error(errorText || `Track trim prepare failed with status ${trimPrepareResponse.status}.`);
        }
        const trimPreparePayload = await trimPrepareResponse.json() as {
          cacheKey?: string;
          status?: { status?: 'processing' | 'ready' | 'error' | 'missing'; error?: string };
        };
        const trimCacheKey = typeof trimPreparePayload?.cacheKey === 'string'
          ? trimPreparePayload.cacheKey.trim()
          : '';
        if (!trimCacheKey) {
          throw new Error('Track trim did not return a cache key.');
        }

        let trimStatus = trimPreparePayload?.status;
        const trimStartedAt = Date.now();
        while (trimStatus?.status === 'processing') {
          if (Date.now() - trimStartedAt > 6 * 60 * 1000) {
            throw new Error('Timed out waiting for timeline trim worker.');
          }
          await new Promise<void>((resolve, reject) => {
            const timeoutId = setTimeout(() => resolve(), 700);
            if (abortSignal) {
              abortSignal.addEventListener('abort', () => {
                clearTimeout(timeoutId);
                reject(createAbortError());
              }, { once: true });
            }
          });
          throwIfAborted(abortSignal);
          const trimStatusResponse = await fetchTrimApi(
            `/api/export/trim-video?mode=status&cacheKey=${encodeURIComponent(trimCacheKey)}`,
            { method: 'GET' },
            4,
          );
          if (!trimStatusResponse.ok) {
            const errorText = await trimStatusResponse.text().catch(() => '');
            throw new Error(errorText || `Track trim status failed with status ${trimStatusResponse.status}.`);
          }
          const trimStatusPayload = await trimStatusResponse.json() as {
            status?: { status?: 'processing' | 'ready' | 'error' | 'missing'; error?: string };
          };
          trimStatus = trimStatusPayload?.status;
        }
        if (trimStatus?.status === 'error') {
          throw new Error(trimStatus.error || 'Timeline trim failed.');
        }
        if (trimStatus?.status !== 'ready') {
          throw new Error('Timeline trim did not reach ready state.');
        }

        const trimFileResponse = await fetchTrimApi(
          `/api/export/trim-video?mode=file&cacheKey=${encodeURIComponent(trimCacheKey)}`,
          { method: 'GET' },
          4,
        );
        if (!trimFileResponse.ok) {
          const errorText = await trimFileResponse.text().catch(() => '');
          throw new Error(errorText || `Track trim file fetch failed with status ${trimFileResponse.status}.`);
        }
        const trimmedReferenceBlob = await trimFileResponse.blob();
        if (!trimmedReferenceBlob || trimmedReferenceBlob.size <= 0) {
          throw new Error('Trimmed source video reference is empty.');
        }

        const fallbackExt = 'mp4';
        const ext = inferExtensionFromBlob(trimmedReferenceBlob, fallbackExt);
        const trimmedReferenceFile = new File(
          [trimmedReferenceBlob],
          `${filenamePrefix}-${Date.now()}.${ext}`,
          { type: trimmedReferenceBlob.type || 'video/mp4' },
        );
        const uploadedReference = await dualUploadAsset(trimmedReferenceFile, {
          ttlSeconds: 86400,
          filenamePrefix,
          fallbackContentType: 'video/mp4',
          fallbackExtension: 'mp4',
        }, workspaceId, abortSignal);

        return {
          publicUrl: uploadedReference.publicUrl,
          privateUrl: uploadedReference.privateUrl,
          timelineInsertStartMs: Math.round(clampedCursorMs),
          remainingTimelineSeconds: remainingMs > 0 ? remainingMs / 1000 : undefined,
        };
      }
    }

    throw new Error('Video model input requires one valid visible video track at the current frame when no time range is provided.');
  }, [dualUploadAsset, inferExtensionFromBlob]);

  const prepareComposedTimelineReferenceClip = useCallback(async ({
    workspaceId,
    tracks,
    rangeStartMs,
    rangeEndMs,
    outputWidth,
    outputHeight,
    outputFps,
    filenamePrefix,
    abortSignal,
  }: {
    workspaceId: string;
    tracks: TimelineReferenceTrack[];
    rangeStartMs: number;
    rangeEndMs: number;
    outputWidth: number;
    outputHeight: number;
    outputFps: number;
    filenamePrefix: string;
    abortSignal?: AbortSignal;
  }): Promise<{
    publicUrl: string;
    privateUrl: string;
    timelineInsertStartMs: number;
    remainingTimelineSeconds: number | undefined;
  }> => {
    throwIfAborted(abortSignal);

    const startMs = Math.max(0, Math.round(rangeStartMs));
    const endMs = Math.max(startMs + 1, Math.round(rangeEndMs));
    const durationMs = endMs - startMs;
    if (durationMs <= 0) {
      throw new Error('Timeline video input range is empty.');
    }
    if (durationMs > TIMELINE_REFERENCE_MAX_DURATION_MS) {
      throw new Error(`Timeline video input range exceeds ${Math.round(TIMELINE_REFERENCE_MAX_DURATION_MS / 60000)} minutes.`);
    }
    const normalizedOutputWidth = normalizeVideoDimension(outputWidth);
    const normalizedOutputHeight = normalizeVideoDimension(outputHeight);
    if (normalizedOutputWidth > TIMELINE_REFERENCE_MAX_DIMENSION || normalizedOutputHeight > TIMELINE_REFERENCE_MAX_DIMENSION) {
      throw new Error(`Timeline video input dimensions exceed ${TIMELINE_REFERENCE_MAX_DIMENSION}px.`);
    }

    const normalizedTracks = normalizeTimelineReferenceTracks(tracks, { startMs, endMs });
    if (normalizedTracks.length === 0) {
      throw new Error('No visible timeline tracks overlap the requested video input range.');
    }
    const hasVideoTrack = normalizedTracks.some((track) => track.layerType === 'video');
    if (!hasVideoTrack) {
      throw new Error('The requested video input range does not overlap a visible video track.');
    }

    const resolveApiUrl = (relativePath: string): string => {
      if (typeof window !== 'undefined' && typeof window.location?.origin === 'string' && window.location.origin.length > 0) {
        return new URL(relativePath, window.location.origin).toString();
      }
      return relativePath;
    };

    const waitWithAbort = (ms: number): Promise<void> => (
      new Promise<void>((resolve, reject) => {
        const timeoutId = setTimeout(() => resolve(), ms);
        if (!abortSignal) return;
        abortSignal.addEventListener('abort', () => {
          clearTimeout(timeoutId);
          reject(createAbortError());
        }, { once: true });
      })
    );

    const fetchTimelineApi = async (relativePath: string, init: RequestInit, retries = 2): Promise<Response> => {
      const requestUrl = resolveApiUrl(relativePath);
      let lastError: unknown = null;
      for (let attempt = 0; attempt <= retries; attempt++) {
        throwIfAborted(abortSignal);
        try {
          return await fetch(requestUrl, { ...init, signal: abortSignal });
        } catch (error) {
          lastError = error;
          if (isAbortError(error) || abortSignal?.aborted) {
            throw createAbortError();
          }
          console.warn('[TimelineReferenceCompose] fetch retry scheduled', {
            requestUrl,
            attempt: attempt + 1,
            retries,
            error: error instanceof Error ? error.message : String(error),
          });
          if (attempt >= retries) break;
          await waitWithAbort(250 * (attempt + 1));
        }
      }
      if (lastError instanceof Error) throw lastError;
      throw new Error('Failed to fetch timeline composition API.');
    };

    const requestBody: TimelineExportPayload = {
      workspaceId,
      output: {
        width: normalizedOutputWidth,
        height: normalizedOutputHeight,
        fps: Math.max(8, Math.min(60, Math.round(outputFps))),
        format: 'mp4',
        quality: 'balanced',
        requireMp4: true,
        includeAudio: true,
        profile: 'reference',
      },
      range: {
        startMs,
        endMs,
      },
      tracks: normalizedTracks,
    };

    const prepareResponse = await fetchTimelineApi('/api/export/timeline-video?mode=prepare', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    });
    if (!prepareResponse.ok) {
      const errorText = await prepareResponse.text().catch(() => '');
      throw new Error(errorText || `Timeline reference prepare failed with status ${prepareResponse.status}.`);
    }
    const preparePayload = await prepareResponse.json() as {
      cacheKey?: string;
      status?: { status?: 'processing' | 'ready' | 'error' | 'missing'; error?: string };
    };
    const cacheKey = typeof preparePayload?.cacheKey === 'string' ? preparePayload.cacheKey.trim() : '';
    if (!cacheKey) {
      throw new Error('Timeline reference composition did not return a cache key.');
    }

    let statusPayload = preparePayload.status;
    const startedAt = Date.now();
    while (statusPayload?.status === 'processing') {
      if (Date.now() - startedAt > 6 * 60 * 1000) {
        throw new Error('Timed out waiting for timeline reference composition worker.');
      }
      await waitWithAbort(700);
      const statusResponse = await fetchTimelineApi(
        `/api/export/timeline-video?mode=status&cacheKey=${encodeURIComponent(cacheKey)}`,
        { method: 'GET' },
        4,
      );
      if (!statusResponse.ok) {
        const errorText = await statusResponse.text().catch(() => '');
        throw new Error(errorText || `Timeline reference status failed with status ${statusResponse.status}.`);
      }
      const statusEnvelope = await statusResponse.json() as {
        status?: { status?: 'processing' | 'ready' | 'error' | 'missing'; error?: string };
      };
      statusPayload = statusEnvelope.status;
    }
    if (statusPayload?.status === 'error') {
      throw new Error(statusPayload.error || 'Timeline reference composition failed.');
    }
    if (statusPayload?.status !== 'ready') {
      throw new Error('Timeline reference composition did not reach ready state.');
    }

    const fileResponse = await fetchTimelineApi(
      `/api/export/timeline-video?mode=file&cacheKey=${encodeURIComponent(cacheKey)}`,
      { method: 'GET' },
      4,
    );
    if (!fileResponse.ok) {
      const errorText = await fileResponse.text().catch(() => '');
      throw new Error(errorText || `Timeline reference file fetch failed with status ${fileResponse.status}.`);
    }
    const referenceBlob = await fileResponse.blob();
    if (!referenceBlob || referenceBlob.size <= 0) {
      throw new Error('Composed timeline reference video is empty.');
    }

    const ext = inferExtensionFromBlob(referenceBlob, 'mp4');
    const referenceFile = new File(
      [referenceBlob],
      `${filenamePrefix}-${Date.now()}.${ext}`,
      { type: referenceBlob.type || 'video/mp4' },
    );
    const uploadedReference = await dualUploadAsset(referenceFile, {
      ttlSeconds: 86400,
      filenamePrefix,
      fallbackContentType: 'video/mp4',
      fallbackExtension: 'mp4',
    }, workspaceId, abortSignal);

    return {
      publicUrl: uploadedReference.publicUrl,
      privateUrl: uploadedReference.privateUrl,
      timelineInsertStartMs: startMs,
      remainingTimelineSeconds: durationMs / 1000,
    };
  }, [dualUploadAsset, inferExtensionFromBlob]);

  const selectTopByRenderOrder = useCallback(<T extends { renderOrder: number; layer: { id: string } }>(items: T[]): T | null => {
    if (!items.length) return null;
    const sorted = [...items].sort((left, right) => {
      if (left.renderOrder !== right.renderOrder) return left.renderOrder - right.renderOrder;
      return left.layer.id.localeCompare(right.layer.id);
    });
    return sorted[sorted.length - 1] ?? null;
  }, []);

  const resolvePreferredTimelineTrackSelection = useCallback(
    <TRenderable extends { layerType: TimelineReferenceLayerType; renderOrder: number; layer: { id: string }; startMs: number; endMs: number }, TVideo extends { renderOrder: number; layer: { id: string }; startMs: number; endMs: number; sourceInMs: number; mediaDurationMs?: number | null }>(
      projectId: string,
      cursorMs: number,
      renderableTracks: TRenderable[],
      videoTracks: TVideo[],
    ) => {
      if (!kernel.current) {
        return {
          selectedRenderableTrack: null as TRenderable | null,
          implicitSelectedRenderableTrack: null as TRenderable | null,
          selectedVideoTrack: null as TVideo | null,
          selectedRenderableTracksCount: 0,
        };
      }
      const selectedLayerIdSet = new Set(getProjectSelectedLayersId(kernel.current, projectId));
      const selectedRenderableTrack = selectTopByRenderOrder(
        renderableTracks.filter((track) => selectedLayerIdSet.has(track.layer.id)),
      );
      const selectedRenderableTracks = renderableTracks.filter((track) => selectedLayerIdSet.has(track.layer.id));
      const overlappingRenderableTracks = renderableTracks.filter((track) => (
        cursorMs >= track.startMs && cursorMs < track.endMs
      ));
      const topVisibleRenderableAtCursor = selectTopByRenderOrder(overlappingRenderableTracks);
      const implicitSelectedRenderableTrack = selectedRenderableTrack ?? topVisibleRenderableAtCursor;
      const explicitSelectedVideoTrack = selectedRenderableTrack?.layerType === 'video'
        ? (videoTracks.find((track) => track.layer.id === selectedRenderableTrack.layer.id) ?? null)
        : null;
      const selectedVideoTrack = implicitSelectedRenderableTrack?.layerType === 'video'
        ? (videoTracks.find((track) => track.layer.id === implicitSelectedRenderableTrack.layer.id) ?? null)
        : null;
      return {
        selectedRenderableTrack,
        implicitSelectedRenderableTrack,
        explicitSelectedVideoTrack,
        selectedVideoTrack,
        selectedRenderableTracksCount: selectedRenderableTracks.length,
      };
    },
    [kernel, selectTopByRenderOrder],
  );

  const clampDimension = (value: number | undefined | null): number => {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      return 2048;
    }
    const rounded = Math.round(value);
    const min = 1024;
    const max = 4096;
    return Math.min(max, Math.max(min, rounded));
  };

  const setModelParameter = useCallback(async (
    targetModelId: string,
    parameterName: string | undefined,
    value: any,
    skipEventLogging? : boolean
  ): Promise<void> => {
    if (parameterName === undefined) parameterName = "prompt";
    
    if(!skipEventLogging) kernel.current?.logUserAction('Setting Changed', { modelId: targetModelId, parameterName, value });
    kernel.current?.setModelParams(prevModelParams => {
        const currentParamsForTargetModel = prevModelParams[targetModelId];

        if (!currentParamsForTargetModel) {
            kernel.current?.handleError(`SET_MODEL_PARAMETER: Model ID "${targetModelId}" not found in modelParams.`);
            console.warn(`SET_MODEL_PARAMETER: Attempted to set parameter for unknown model ID: ${targetModelId}`);
            return prevModelParams; // Return previous state if model not found
        }

        let processedValue = value;
        const existingParamValue = currentParamsForTargetModel[parameterName as keyof DiffusionParams];

        if (existingParamValue !== undefined) {
            if (typeof existingParamValue === 'number') {
                const numValue = Number(value);
                if (isNaN(numValue)) {
                    kernel.current?.handleError(`SET_MODEL_PARAMETER: Invalid number value "${value}" for parameter "${parameterName}" (expected number).`);
                    return prevModelParams;
                }
                processedValue = numValue;
            } else if (typeof existingParamValue === 'boolean') {
                if (typeof value === 'string') {
                    if (value.toLowerCase() === 'true') processedValue = true;
                    else if (value.toLowerCase() === 'false') processedValue = false;
                    else {
                         kernel.current?.handleError(`SET_MODEL_PARAMETER: Invalid boolean value "${value}" for parameter "${parameterName}" (expected true/false).`);
                         return prevModelParams;
                    }
                } else if (typeof value !== 'boolean') {
                    kernel.current?.handleError(`SET_MODEL_PARAMETER: Invalid value type for boolean parameter "${parameterName}".`);
                    return prevModelParams;
                }
                // If value is already boolean, it's fine.
            } else if (typeof existingParamValue === 'string') {
                if (typeof value !== 'string') {
                    // Allow numbers to be converted to strings for string params (e.g. seed)
                    if (typeof value === 'number' || typeof value === 'boolean') {
                        processedValue = String(value);
                    } else {
                        kernel.current?.handleError(`SET_MODEL_PARAMETER: Invalid string value for parameter "${parameterName}" (expected string).`);
                        return prevModelParams;
                    }
                }
            }
        }

        const updatedParamsForTargetModel = {
            ...currentParamsForTargetModel,
            [parameterName]: processedValue,
        };

        return {
            ...prevModelParams,
            [targetModelId]: updatedParamsForTargetModel,
        };
    });
  }, [kernel]);

  const resetModelParameters = useCallback(async (targetModelId: string): Promise<void> => {
    if (!kernel.current) return;
    const defaultParamsGetter = getDefaultParamsForModel(targetModelId);

    if (defaultParamsGetter) {
      const defaults = defaultParamsGetter();
      kernel.current.setModelParams(prevModelParams => {
        if (!prevModelParams[targetModelId] && !Object.keys(defaultParamsGetter).includes(targetModelId)) {
          console.warn(`RESET_MODEL_PARAMETERS: Model ID "${targetModelId}" is not recognized for default parameters and not in current modelParams. No action taken.`);
          return prevModelParams;
        }

        return {
          ...prevModelParams,
          [targetModelId]: defaults
        };
      });
    } else {
      kernel.current?.handleError(`No default parameters defined for model: ${targetModelId}.`);
    }
  }, [kernel]);


  // The trace a project gets: Ideogram in a Vector project, unless this account or server cannot run it, Recraft elsewhere.
  const resolveVectorTraceModel = useCallback((projectId: string): string => {
    const k = kernel.current;
    const preferred = vectorTraceModelFor(k?.getInventory().Project.registry[projectId]?.projectType);
    if (!k || preferred === 'recraft-vectorize') return preferred;
    const userContext = k.getUserContext();
    const withheld = clientAccessSuspension(userContext.accessSuspension, userContext.organisationId, userContext.teamId).withheldModelFamilies;
    return isModelInFamilies(preferred, withheld) ? 'recraft-vectorize' : preferred;
  }, [kernel]);

  const ensureDiffusionModelForProject = useCallback<KernelHandle['ensureDiffusionModelForProject']>((projectId) => {
    const k = kernel.current;
    if (!k) return;
    const projectType = k.getInventory().Project.registry[projectId]?.projectType;
    if (!projectType || isStudioModelSupportedForProjectType(k.getDiffusionModel(), projectType)) return;
    const userContext = k.getUserContext();
    // Only the server's verdict knows which providers this server holds keys for; choose once it has arrived.
    if (!userContext.accessSuspension) return;
    const selectable = getActiveSelectableStudioModelIds({
      accessScope: userContext.permissions?.accessScope,
      organisationRestricted: k.getOrganisationCredits()?.isRestricted ?? null,
      withheldModelFamilies: clientAccessSuspension(userContext.accessSuspension, userContext.organisationId, userContext.teamId).withheldModelFamilies,
      modelVisibility: k.getUserPreferences().modelVisibility || {},
    });
    // In a Vector project the best-ranked model leads whatever was used before; elsewhere the user's habits do.
    const candidates = filterStudioModelIdsForProjectType(selectable, projectType).sort(compareStudioModels(projectType === 'VECTOR' ? undefined : userContext.studioModelUsage));
    if (candidates[0]) k.selectDiffusionModel(candidates[0]);
  }, [kernel]);

  // Switches the active diffusion model and carries the user's own prompt over to it:
  // a prompt that differs from the current model's default is user-authored, so it follows
  // the switch; otherwise the new model's default prompt applies.
  const selectDiffusionModel = useCallback<KernelHandle['selectDiffusionModel']>((newModel) => {
    const currentModel = kernel.current?.getDiffusionModel() ?? '';
    const currentModelParams = kernel.current?.getModelParams()[currentModel];

    const currentDefaultParamsGetter = getDefaultParamsForModel(currentModel);
    const currentDefaultPrompt = currentDefaultParamsGetter ? currentDefaultParamsGetter().prompt || "" : "";

    const newDefaultParamsGetter = getDefaultParamsForModel(newModel);
    const newDefaultPrompt = newDefaultParamsGetter ? newDefaultParamsGetter().prompt || "" : "";

    let promptForNewModel: string;
    if (currentModelParams && currentModelParams.prompt && currentModelParams.prompt !== "" && currentModelParams.prompt !== currentDefaultPrompt) {
      promptForNewModel = currentModelParams.prompt;
    } else {
      promptForNewModel = newDefaultPrompt;
    }

    kernel.current?.setModelParams(prevModelParams => {
      const newModelParams = prevModelParams[newModel];
      const baseParams = newModelParams ?? (newDefaultParamsGetter ? newDefaultParamsGetter() : undefined);

      if (!baseParams) {
        console.error(`[Editor] Could not get base parameters for model "${newModel}". Aborting state update.`);
        return prevModelParams;
      }

      return {
        ...prevModelParams,
        [newModel]: {
          ...baseParams,
          prompt: promptForNewModel
        }
      };
    });

    kernel.current?.setDiffusionModel(newModel);
  }, [kernel]);

  const setReferenceImages = useCallback(async (newFiles: File[], mode: 'replace' | 'append'): Promise<void> => {
    if (!kernel.current) return;
    const activeSessionId = kernel.current.getActiveChatSessionId();
    const activeProjectIdRaw = activeSessionId ? kernel.current.getLastProjectId(activeSessionId) : '';
    const projectId = typeof activeProjectIdRaw === 'string' ? activeProjectIdRaw.trim() : '';
    if (!projectId) {
      kernel.current?.handleError('Cannot set reference images: no active project scope.');
      return;
    }
    const diffusionModel = kernel.current.getDiffusionModel();
    const maxRefs = getStudioModelReferenceLimits(diffusionModel).max;

    const lockableFiles: LockableFile[] = newFiles.map(file => ({
      id: uuidv4(),
      file,
      locked: false,
    }));

    if (mode === 'replace') {
      if (lockableFiles.length > maxRefs && maxRefs > 0) {
        toast.warning(`Maximum ${maxRefs} reference images allowed. Truncating to ${maxRefs}.`);
        setExtraFiles(projectId, lockableFiles.slice(0, maxRefs));
      } else if (maxRefs === 0 && lockableFiles.length > 0) {
        kernel.current?.handleError(`The current model (${diffusionModel}) does not support reference images.`);
        setExtraFiles(projectId, []); // Clear if user attempted to set for an unsupported model
      } else {
        setExtraFiles(projectId, lockableFiles);
      }
    } else if (mode === 'append') {
      if (maxRefs === 0 && lockableFiles.length > 0) {
         kernel.current?.handleError(`The current model (${diffusionModel}) does not support reference images. Cannot append.`);
         return;
      }
      setExtraFiles(projectId, prevFiles => {
        const combinedFiles = [...prevFiles, ...lockableFiles];
        if (combinedFiles.length > maxRefs && maxRefs > 0) {
          toast.warning(`Maximum ${maxRefs} reference images allowed. Appended and truncated to ${maxRefs}.`);
          return combinedFiles.slice(0, maxRefs); // Keeps the oldest + new ones up to the limit
        }
        return combinedFiles;
      });
    }
  }, [kernel, setExtraFiles]);

  const handleQueueEditDiffusion = useCallback(async (
    workspaceId: string,
    runtimeStateOverride: RuntimeState | undefined,
    parentVersionId: string | null,
    numOutputsOverride?: number,
    rectOverride?: Rect,
    abortSignal?: AbortSignal,
    generationOptions?: ModelRunGenerationOptions
  ): Promise<{ tasks: GenerationTask[], newWorkspaceId: string | null }> => {
    if (!kernel.current) return { tasks: [], newWorkspaceId: null };
    throwIfAborted(abortSignal);

    const sourceWorkspace = workspaceId ? kernel.current.getWorkspace(workspaceId) : null;
    const projectId = generationOptions?.projectId?.trim()
      || (workspaceId ? getProjectIdFromWorkspaceId(workspaceId, 'handleQueueDiffusion') : '');
    const generationOutputScope = generationOptions?.outputScope
      ?? resolveGenerationOutputScopeForView(projectId, workspaceId, getProjectStudioViewMode(kernel.current, projectId));
    const isListProjectGeneration = generationOutputScope?.kind === 'PROJECT';
    const allLayers = workspaceId && !isListProjectGeneration ? getWorkspaceLayers(workspaceId) : [];
    const outputWorkspace = isListProjectGeneration
      ? createOutputOnlyWorkspace(projectId, sourceWorkspace ?? undefined)
      : sourceWorkspace ?? (projectId ? createOutputOnlyWorkspace(projectId) : null);

    if (!outputWorkspace) {
        kernel.current?.handleError("Could not resolve a project for output-only diffusion.");
        return { tasks: [], newWorkspaceId: null };
    }

    const visibleLayers = allLayers.filter((layer) => layer.visible);
    const hasBaseLayers = visibleLayers.length > 0;
    const explicitMainInputUrl = typeof generationOptions?.mainImageUrlOverride === 'string'
      ? generationOptions.mainImageUrlOverride.trim() || null : null;
    const hasBaseImage = hasBaseLayers || (isListProjectGeneration && Boolean(explicitMainInputUrl));
    const generatedImageTimelineOverride: Partial<Layer> | undefined = isListProjectGeneration ? undefined : (() => {
      const cursorMs = Math.max(0, toFinite(outputWorkspace.timelineCursorMs) ?? 0);
      return {
        timelineStartMs: Math.round(cursorMs),
        timelineEndMs: Math.round(cursorMs) + MIN_TIMELINE_TRACK_SPAN_MS,
        timelineSourceInMs: 0,
      };
    })();

    const diffusionModel = runtimeStateOverride?.diffusionModel || kernel.current.getDiffusionModel();
    if ((diffusionModel === 'qwen-edit-multiangle') && !hasBaseImage) {
      kernel.current?.handleError("Qwen Multiangle requires a base image.");
      return { tasks: [], newWorkspaceId: null };
    }
    if (diffusionModel === 'gpt-image-2' && !hasBaseImage) {
      kernel.current?.handleError("GPT-Image-2 requires a base image.");
      return { tasks: [], newWorkspaceId: null };
    }
    if (diffusionModel === 'seedvr2' && !hasBaseLayers && !generationOptions?.mainImageUrlOverride) {
      kernel.current?.handleError('SeedVR2 requires a base image.');
      return { tasks: [], newWorkspaceId: null };
    }
    if (diffusionModel === QWEN_IMAGE_21_MODEL_ID && !hasBaseImage) {
      kernel.current?.handleError('Qwen Image 2.1 requires a base image.');
      return { tasks: [], newWorkspaceId: null };
    }
    const currentRunParams = (runtimeStateOverride?.parameters ?? kernel.current.getModelParams())[diffusionModel] as unknown as Record<string, unknown> | undefined;
    const isListPersonaIntent = (generationOptions?.outputScope
      ? generationOptions.outputScope.kind === 'PROJECT'
      : getProjectStudioViewMode(kernel.current, projectId) === 'marketing')
      && (/(^|[\s(])@[a-z0-9][a-z0-9-]*(?=$|[\s),.;:!?])/i.test(String(currentRunParams?.prompt ?? ''))
        || ['listPersonas', 'listObjects'].some(key => Array.isArray(currentRunParams?.[key]) && currentRunParams[key].length > 0));
    if (diffusionModel === 'mai-2.5-flash' && !hasBaseImage && !isListPersonaIntent) {
      kernel.current?.handleError("MAI 2.5 Flash requires a base image.");
      return { tasks: [], newWorkspaceId: null };
    }
    if (diffusionModel === 'ideogram-inpaint') {
      if (!hasBaseLayers) {
        kernel.current?.handleError("Ideogram Inpaint requires a base image.");
        return { tasks: [], newWorkspaceId: null };
      }
      if (!inpaintMaskHasContent(kernel.current?.getInpaintMaskShapes()[workspaceId] ?? null)) {
        kernel.current?.handleError("Paint a region to inpaint with the Mask tool first.");
        return { tasks: [], newWorkspaceId: null };
      }
    }

    const requestedGenerations = numOutputsOverride ?? getLocalNumOutputs(projectId);
    const numGenerations = enforceStudioModelMinimumGenerations(diffusionModel, requestedGenerations);
    kernel.current?.setIsSubmittingAITask(true);
    throwIfAborted(abortSignal);

    try {
        const finalWorkspaceId = workspaceId;
        let finalWorkspace = outputWorkspace;

        const modelParams = runtimeStateOverride?.parameters || kernel.current.getModelParams();
        const params = modelParams[diffusionModel];
        if(!params) throw "Missing model params in array";
        // Keep the complete Fater/List task context here. GPT-Image-2.5 projects only its
        // provider-owned keys at the server adapter boundary, after List foundation resolution.
        let finalParams : DiffusionParams = { ...params };
        if (diffusionModel === 'seedvr2') {
          const { denoise: _legacyDenoise, ...seedvr2Params } = finalParams as DiffusionParams & { denoise?: unknown };
          finalParams = seedvr2Params as DiffusionParams;
        }
        throwIfAborted(abortSignal);

        let upscaleFactor = 1;
        let maxPixels: number | undefined;
        // Resolved once from the Generation Area: render budget, redraw, task dims and merge factor all share it.
        const generationArea = rectOverride || finalWorkspace.generationRect;
        const qwen21Geometry = diffusionModel === QWEN_IMAGE_21_MODEL_ID
          ? resolveQwenImage21TaskGeometry(generationArea, finalWorkspace.frameRect, (params as QwenImage21Params).resolution)
          : null;
        // The rect the task renders, masks and lands on: the capture on Qwen 2.1, whose border lock alone reads the area.
        const taskRectOverride = qwen21Geometry?.rect ?? rectOverride;
        // fal takes an explicit image_size on Qwen Image 3: the input we send and the output we ask
        // for share this one shape, so the run never pays to upscale beyond what it was given.
        const qwen3Geometry = diffusionModel === 'qwen-image-3-pro'
          ? resolveQwenImage3TaskGeometry(rectOverride || finalWorkspace.generationRect, (params as QwenImage3Params).resolution)
          : null;

        if (diffusionModel === "seedream4.5") {
            const seedreamParams = params as Seedream4_5Params;
            if(seedreamParams.prompt.length == 0) throw "Prompt is empty";
            const normalizedSeedreamMegaPixels = seedreamParams.outputMegaPixels >= 16 ? 16 : 4;
            const requestedMaxPixels = normalizedSeedreamMegaPixels * 1000000;
            
            const generationRect = rectOverride || finalWorkspace.generationRect;
            const aspectRatio = generationRect.w > 0 && generationRect.h > 0 ? generationRect.w / generationRect.h : 1;

            const idealWidth = Math.sqrt(requestedMaxPixels * aspectRatio);
            const idealHeight = Math.sqrt(requestedMaxPixels / aspectRatio);

            const MAX_SIDE_LENGTH = 4096;

            if (idealWidth > MAX_SIDE_LENGTH || idealHeight > MAX_SIDE_LENGTH) {
                let newWidth, newHeight;
                if (aspectRatio >= 1) { // width is longer or square
                    newWidth = MAX_SIDE_LENGTH;
                    newHeight = MAX_SIDE_LENGTH / aspectRatio;
                } else { // height is longer
                    newHeight = MAX_SIDE_LENGTH;
                    newWidth = MAX_SIDE_LENGTH * aspectRatio;
                }

                // Apply 5% margin
                newWidth *= 0.95;
                newHeight *= 0.95;

                maxPixels = newWidth * newHeight;
            } else {
                maxPixels = requestedMaxPixels;
            }
        } else if (diffusionModel === "nano-banana-pro") {
            const nanoParams = params as NanoBananaProParams;
            if (nanoParams.imageSize === '4K') {
                maxPixels = 16 * 1024 * 1024; // 16MP
            } else if (nanoParams.imageSize === '2K') {
                maxPixels = 4 * 1024 * 1024;  // 4MP
            } else {
                maxPixels = 1024 * 1024;      // 1MP
            }
        } else if (diffusionModel === "grok-imagine-image") {
            maxPixels = 4 * 1024 * 1024; // 4MP (2K)
        } else if (diffusionModel === "grok-imagine-2") {
            // The input has to match the billed tier: a 2K-sized source on a 1K run is paid at 1K.
            maxPixels = resolveGrokImagine2Resolution((params as GrokImagine2Params).resolution) === '1K'
                ? 1024 * 1024
                : 4 * 1024 * 1024;
        } else if (diffusionModel === "muse-image") {
            const generationRect = rectOverride || finalWorkspace.generationRect;
            maxPixels = museImageInputPixels(generationRect.w, generationRect.h);
        } else if (diffusionModel === "flux-3-image") {
            maxPixels = FLUX_3_IMAGE_MAX_INPUT_PIXELS;
        } else if (diffusionModel === "flux-3-klein") {
            // The output size is sent explicitly from the frame, so this only bounds the source sent.
            maxPixels = FLUX_3_KLEIN_MAX_INPUT_PIXELS;
        } else if (diffusionModel === "qwen-image-2-pro") {
            // 2 Pro exposes no output size: with match_input_image the output comes back at the
            // resolution of the image we send, so this budget sets the output resolution on every
            // run that composites the canvas.
            maxPixels = 4 * 1024 * 1024;
        } else if (diffusionModel === "gpt-image-2" || diffusionModel === "gpt-image-2.5") {
            // Keep GA ratio from workspace and let renderWorkspaceArea fit under the product budget.
            maxPixels = 8_294_400;
        } else if (isMaiModelId(diffusionModel)) {
            // Max total pixels is 1,048,576 (equivalent to 1024x1024)
            maxPixels = 1_048_576;
        } else if (diffusionModel === "ideogram-4-edit" || diffusionModel === "ideogram-inpaint") {
            maxPixels = 4 * 1024 * 1024; // 4MP; keep the source under Ideogram's 10MB limit

        } else if (diffusionModel === "flux-2-pro" || diffusionModel === "flux-2-klein-4b") {
            const fluxParams = params as FluxTwoProParams;
            const desiredMegaPixels = fluxTwoResolutionToMegapixels(fluxParams.resolution);
            if (desiredMegaPixels) {
                maxPixels = desiredMegaPixels * 1000000;
            } else {
                const generationRect = rectOverride || finalWorkspace.generationRect;
                maxPixels = generationRect.w * generationRect.h;
            }
        } else if (diffusionModel === "topaz-image-enhance") {
          const topazParams = params as TopazImageEnhanceParams;
          const hasResolutionContract = hasTopazImageResolutionContract(topazParams);
          if (hasResolutionContract) {
            const topazInput = normalizeSeedVR2InputMegaPixels(topazParams.inputMegaPixels);
            maxPixels = SEEDVR2_INPUT_PIXELS[topazInput === 'auto' ? 16 : topazInput];
          } else {
            maxPixels = 16 * 1000000;
          }
        } else if (diffusionModel === "seedvr2") {
          maxPixels = resolveSeedVR2InputPixelBudget(
            (params as SeedVR2Params).inputMegaPixels,
            (params as SeedVR2Params).outputMegaPixels,
          );
        } else if (qwen21Geometry || qwen3Geometry) {
          const geometry = (qwen21Geometry ?? qwen3Geometry)!;
          maxPixels = geometry.width * geometry.height;
        } else if (diffusionModel === "magnific-image-upscale") {
          const magnificParams = params as MagnificImageUpscaleParams;
          const rawScaleFactor = typeof magnificParams.scale_factor === 'number' && Number.isFinite(magnificParams.scale_factor)
            ? magnificParams.scale_factor
            : 2;
          const scaleFactor = Math.max(2, Math.min(16, Math.round(rawScaleFactor)));
          const maxOutputPixels = 10060 * 10060;
          maxPixels = Math.max(256 * 256, Math.min(16 * 1000000, Math.floor(maxOutputPixels / (scaleFactor * scaleFactor))));
        } else {
          maxPixels = 1024*1024;
        }

        let multipleOf : undefined | number = diffusionModel === QWEN_IMAGE_21_MODEL_ID ? QWEN_IMAGE_21_DIMENSION_MULTIPLE : 16;
        // Qwen Image 3 edits the scene it is given and has no mask input: a transparent hole would
        // reach it as arbitrary pixels, so the render is composited opaque.
        let alphaChannel = diffusionModel === 'gpt-image-2.5' || diffusionModel === 'qwen-image-3-pro' ? false : true;

        let extraFilesLockable = runtimeStateOverride?.referenceImages || getExtraFiles(projectId);
        let extraFiles = extraFilesLockable.map(lf => lf.file);
        if (isMaiModelId(diffusionModel) && isListPersonaIntent) {
          throw new MaiImageInputError('MAI does not support persona or object scenes yet. Use Nano Banana 2 or Flux 2 for this request.');
        }
        if (isMaiModelId(diffusionModel) && (extraFiles.length > 0
          || (!isListProjectGeneration && inpaintMaskHasContent(kernel.current.getInpaintMaskShapes()[workspaceId] ?? null)))) {
          throw new MaiImageInputError('MAI supports one source image, without masks or extra references.');
        }

        const annotationsAllowedForModel = !MODELS_WITH_DISABLED_ANNOTATION_OVERLAY.has(diffusionModel);
        const runtimeModelParams = runtimeStateOverride?.parameters?.[diffusionModel] as Record<string, unknown> | undefined;
        const hasRuntimeAnnotationOverride = !!runtimeModelParams && Object.prototype.hasOwnProperty.call(runtimeModelParams, 'add_annotations_as_ref');
        const shouldUseAnnotationsAsRef = !isListProjectGeneration && annotationsAllowedForModel && (
          hasRuntimeAnnotationOverride
            ? Boolean((runtimeModelParams as any).add_annotations_as_ref)
            : finalWorkspace.annotationsEnabled !== false
        );

        const resolvedStudioModelId = resolveRegistryModelIdFromRuntimeName(diffusionModel) as StudioModelId | null;
        const studioModelDefinition = resolvedStudioModelId ? STUDIO_MODEL_REGISTRY[resolvedStudioModelId] : undefined;
        const modelSupportsReferenceImages = studioModelDefinition?.capabilities.supportsReferenceImages === true;
        const modelSupportsMainImageInput = studioModelDefinition?.capabilities.supportsMainImageInput !== false;
        if (!modelSupportsReferenceImages) {
          extraFiles = [];
        }

        const annotationVersionId = isListProjectGeneration ? null : resolveWorkspaceAnnotationVersionId(finalWorkspace);
        let resolvedAnnotationMask = isListProjectGeneration ? null : resolveWorkspaceAnnotationMask(finalWorkspace);

        if (!resolvedAnnotationMask && annotationVersionId) {
          const annotationVersion = findWorkspaceVersionById(finalWorkspace, annotationVersionId);
          const annotationMaskUrl = annotationVersion?.lastAnnotationMaskUrl ?? null;
          if (annotationMaskUrl) {
            try {
              resolvedAnnotationMask = await loadImageDataFromUrl(annotationMaskUrl);
              throwIfAborted(abortSignal);
              const loadedMask = resolvedAnnotationMask;

              kernel.current?.setWorkspace(
                finalWorkspaceId,
                (workspace) => {
                  const resolvedActiveVersionId =
                    (typeof workspace.activeVersionId === 'string' && workspace.activeVersionId.trim().length > 0)
                      ? workspace.activeVersionId
                      : null;
                  const shouldSyncWorkspaceCache = resolvedActiveVersionId === annotationVersionId;
                  return {
                    ...workspace,
                    versions: (workspace.versions ?? []).map((version) => (
                      version.id === annotationVersionId
                        ? { ...version, annotationMask: loadedMask, annotationMaskIsDirty: false }
                        : version
                    )),
                    ...(shouldSyncWorkspaceCache
                      ? { annotationMask: loadedMask, annotationMaskVersionId: annotationVersionId }
                      : {}),
                  };
                },
                projectId
              );
              finalWorkspace = kernel.current?.getWorkspace(finalWorkspaceId) ?? finalWorkspace;
            } catch (error) {
              console.warn(
                `[handleQueueEditDiffusion] Failed to load annotation mask for workspace ${finalWorkspaceId} version ${annotationVersionId}.`,
                error
              );
            }
          }
        }

        const renderArea = taskRectOverride || finalWorkspace.generationRect;
        const hasAnnotations = hasVisibleAnnotationsInRenderArea(
          resolvedAnnotationMask,
          finalWorkspace.frameRect,
          renderArea
        );

        const shouldBakeAnnotationsIntoMainImage =
          shouldUseAnnotationsAsRef &&
          hasAnnotations &&
          modelSupportsMainImageInput &&
          !modelSupportsReferenceImages;

        const isPngModel =
          diffusionModel === 'topaz-image-enhance' ||
          diffusionModel === 'magnific-image-upscale' ||
          isMaiModelId(diffusionModel);
        const renderFormat = isPngModel ? 'png' : 'webp';
        const renderMimeType = isPngModel ? 'image/png' : 'image/webp';
        const renderExtension = isPngModel ? 'png' : 'webp';

        if (shouldUseAnnotationsAsRef) {
          if (hasAnnotations && modelSupportsReferenceImages) {
            const annotationResult = await kernel.current?.renderWorkspaceArea(
              finalWorkspaceId,
              undefined,
              maxPixels,
              false,
              alphaChannel,
              multipleOf,
              taskRectOverride,
              renderFormat,
              undefined,
              'drawAnnotationOverlay'
            );

            if (annotationResult && annotationResult.file) {
              const maxRefs = getStudioModelReferenceLimits(diffusionModel).max;
              if (maxRefs > 0) {
                while (extraFiles.length >= maxRefs) {
                  extraFiles.shift();
                }
                extraFiles.push(annotationResult.file);
              }
            }
            throwIfAborted(abortSignal);
          }
        }

        const mainImageMaskInterpretation = shouldBakeAnnotationsIntoMainImage
          ? 'drawAnnotationOverlay'
          : undefined;

        // LIST projects: outputs are INDEPENDENT batch results and NEVER
        // composite the layer stack into a generation (it bleeds visible
        // reference layers into every output, a square reference pasted over a
        // 16:9 scene). Their references come SOLELY from the chat (model
        // reference images); the only main input is an explicit per-run
        // override (e.g. a tile's Upscale action targeting that one output's
        // asset). Canvas is unchanged: there the visible layer stack IS the
        // intended input.
        // Per-run override (e.g. a tile's Upscale action) targets ONE specific
        // output's asset.
        const mainInputAssetUrl = explicitMainInputUrl;
        if (isListProjectGeneration && explicitMainInputUrl && generationOptions?.sourceListOutputId) {
          finalParams = { ...finalParams, listEditSourceOutputId: generationOptions.sourceListOutputId } as unknown as DiffusionParams;
        }

        let mainInputWasTransformed = false;
        let assembledResult: Awaited<ReturnType<NonNullable<KernelHandle['renderWorkspaceArea']>>> | undefined;
        if (modelSupportsMainImageInput && mainInputAssetUrl) {
          try {
            const referenceResponse = await fetch(mainInputAssetUrl, { signal: abortSignal });
            if (!referenceResponse.ok) throw new Error(`HTTP ${referenceResponse.status}`);
            let referenceBlob = await referenceResponse.blob();
            const referenceBitmap = await createImageBitmap(referenceBlob);
            let referenceWidth = referenceBitmap.width;
            let referenceHeight = referenceBitmap.height;
            let referenceScaleFactor = 1;

            if (diffusionModel === 'seedvr2' || diffusionModel === 'topaz-image-enhance') {
              const targetPixels = diffusionModel === 'seedvr2'
                ? resolveSeedVR2InputPixelBudget(
                  (params as SeedVR2Params).inputMegaPixels,
                  (params as SeedVR2Params).outputMegaPixels,
                )
                : maxPixels;
              const inputDimensions = resolveSeedVR2InputDimensions({
                sourceWidth: referenceBitmap.width,
                sourceHeight: referenceBitmap.height,
                targetPixels,
              });
              referenceWidth = inputDimensions.width;
              referenceHeight = inputDimensions.height;
              referenceScaleFactor = inputDimensions.scaleFactor;
              if (referenceWidth !== referenceBitmap.width || referenceHeight !== referenceBitmap.height) {
                const canvas = document.createElement('canvas');
                canvas.width = referenceWidth;
                canvas.height = referenceHeight;
                const ctx = canvas.getContext('2d');
                if (!ctx) {
                  throw new Error(
                    diffusionModel === 'seedvr2'
                      ? 'Could not resize the SeedVR2 input image.'
                      : 'Could not resize the Topaz input image.',
                  );
                }
                ctx.drawImage(referenceBitmap, 0, 0, referenceWidth, referenceHeight);
                const resizedBlob = await new Promise<Blob | null>((resolve) => (
                  canvas.toBlob(resolve, renderMimeType, 0.95)
                ));
                if (!resizedBlob) {
                  throw new Error(
                    diffusionModel === 'seedvr2'
                      ? 'Could not encode the resized SeedVR2 input image.'
                      : 'Could not encode the resized Topaz input image.',
                  );
                }
                referenceBlob = resizedBlob;
                mainInputWasTransformed = true;
              }
            }

            // MAI is normalized below at its final provider geometry. Other PNG models only
            // need format conversion here; the canonical List URL remains separate for auth.
            if (isPngModel && referenceBlob.type !== 'image/png') {
              const canvas = document.createElement('canvas');
              canvas.width = referenceBitmap.width;
              canvas.height = referenceBitmap.height;
              const ctx = canvas.getContext('2d');
              if (ctx) {
                ctx.drawImage(referenceBitmap, 0, 0);
                const pngBlob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
                if (pngBlob) { referenceBlob = pngBlob; mainInputWasTransformed = true; }
              }
            }

            const referenceFile = new File([referenceBlob], `main-input.${renderExtension}`, {
              type: renderMimeType,
            });
            assembledResult = {
              file: referenceFile,
              targetWidth: referenceWidth,
              targetHeight: referenceHeight,
              upscaleFactor: referenceScaleFactor,
              imageData: undefined,
            } as unknown as Awaited<ReturnType<NonNullable<KernelHandle['renderWorkspaceArea']>>>;
            if (typeof referenceBitmap.close === 'function') referenceBitmap.close();
          } catch (referenceError) {
            if (isAbortError(referenceError)) throw referenceError;
            throw new Error(
              `The image to work from could not be loaded (${mainInputAssetUrl}). Nothing was generated. `
              + 'Re-attach it and try again.',
              { cause: referenceError },
            );
          }
        }
        if (!assembledResult) {
          // LIST never composites its layer stack (see above): without an
          // explicit main-input override there is no base render, the chat
          // reference images drive the generation on their own.
          const shouldRenderForSizing = modelSupportsMainImageInput && hasBaseLayers && !isListProjectGeneration;
          const renderTargetPixels = diffusionModel === 'seedvr2'
            ? resolveSeedVR2InputRenderPixelBudget({
              inputValue: (params as SeedVR2Params).inputMegaPixels,
              outputValue: (params as SeedVR2Params).outputMegaPixels,
            })
            : maxPixels;
          assembledResult = shouldRenderForSizing
            ? await kernel.current?.renderWorkspaceArea(finalWorkspaceId, undefined, renderTargetPixels, false, alphaChannel, multipleOf, taskRectOverride, renderFormat, undefined, mainImageMaskInterpretation)
            : undefined;
          const redBand = generationOptions?.mainImageRedBand;
          // The file sent only: imageData, which harmonization reads as the source, keeps the pixels under the run.
          if (redBand && assembledResult?.file) {
            assembledResult = { ...assembledResult, file: await paintRedBand(assembledResult.file, renderArea, redBand, renderMimeType) };
          }
        }
        throwIfAborted(abortSignal);

        let controlImageFile = modelSupportsMainImageInput ? assembledResult?.file : null;
        upscaleFactor = assembledResult?.upscaleFactor || upscaleFactor;

        const isNanoFamily = diffusionModel === 'nano-banana' || diffusionModel === 'nano-banana-pro' || isNanoBanana2OrNewer(diffusionModel);
        const isFluxTwoPro = diffusionModel === 'flux-2-pro' || diffusionModel === 'flux-2-klein-4b' || diffusionModel === 'fater-enhance' || diffusionModel === 'fater-upscale';
        const preserveSourceAspectRatio =
          generationOptions?.preserveSourceAspectRatio === true
          && typeof assembledResult?.targetWidth === 'number'
          && assembledResult.targetWidth > 0
          && typeof assembledResult?.targetHeight === 'number'
          && assembledResult.targetHeight > 0;

        if (isListProjectGeneration && isNanoFamily) {
          const workspace = kernel.current.getWorkspace(finalWorkspaceId) ?? finalWorkspace;
          const frame = workspace.frameRect;
          const gen = rectOverride || workspace.generationRect;
          const threshold = 1;
          const coversEntireFrame =
            Math.abs(frame.x - gen.x) < threshold
            && Math.abs(frame.y - gen.y) < threshold
            && Math.abs(frame.w - gen.w) < threshold
            && Math.abs(frame.h - gen.h) < threshold;
          const ratioMap = isNanoBanana2OrNewer(diffusionModel) ? NANO_2_SUPPORTED_RATIOS : NANO_SUPPORTED_RATIOS;
          finalParams = assembleNanoListProviderParams(finalParams as DiffusionParams & Record<string, unknown>, {
            explicitAspectRatio: (finalParams as { aspect_ratio?: unknown }).aspect_ratio,
            sourceWidth: assembledResult?.targetWidth,
            sourceHeight: assembledResult?.targetHeight,
            workspaceAspectRatio: typeof workspace.aspectRatio === 'number' ? workspace.aspectRatio : undefined,
            rectAspectRatio: gen.h > 0 ? gen.w / gen.h : 1,
            coversEntireFrame,
            supportedRatios: ratioMap,
            preferSourceAspectRatio: preserveSourceAspectRatio,
          });
        }

        if (diffusionModel === 'grok-imagine-2') {
          const explicitAspectRaw = (finalParams as { aspect_ratio?: unknown }).aspect_ratio;
          const honoursExplicitListRatio = isListProjectGeneration
            && typeof explicitAspectRaw === 'string'
            && explicitAspectRaw !== 'auto'
            && typeof (GROK_2_SUPPORTED_RATIOS as Record<string, number>)[explicitAspectRaw] === 'number';
          if (!honoursExplicitListRatio) {
            const gen = rectOverride || finalWorkspace.generationRect;
            finalParams = {
              ...(finalParams as GrokImagine2Params),
              // 'auto' exists only on the edit endpoint, where the rendered source already carries
              // the wanted geometry; text-to-image must be handed one of the 13 supported shapes.
              aspect_ratio: controlImageFile
                ? 'auto'
                : closestSupportedAspectRatio(gen.h > 0 ? gen.w / gen.h : 1, GROK_2_SUPPORTED_RATIOS) as GrokImagine2AspectRatio,
            };
          }
        }

        if (diffusionModel === 'flux-3-image') {
          // Only a LIST project may keep the preset the agent set; Canvas geometry is visible and wins.
          const requestedListRatio = isListProjectGeneration
            ? parseFlux3ImageAspectRatio((finalParams as Flux3ImageParams).aspect_ratio)
            : null;
          const gen = rectOverride || finalWorkspace.generationRect;
          finalParams = {
            ...(finalParams as Flux3ImageParams),
            // Per BFL, 'auto' keeps image 1's framing, and image 1 is the source.
            aspect_ratio: requestedListRatio && requestedListRatio !== 'auto'
              ? requestedListRatio
              : controlImageFile ? 'auto' : deriveFlux3ImageAspectRatio(gen.w, gen.h),
          };
        }

        if (diffusionModel === 'muse-image') {
          // Only a LIST project may keep the aspect_ratio the agent set; Canvas geometry is visible and wins.
          const explicitListRatio = isListProjectGeneration
            ? parseMuseImageAspectRatio((finalParams as MuseImageParams).aspect_ratio)
            : null;
          const gen = rectOverride || finalWorkspace.generationRect;
          // A named base image keeps its own shape, which can differ from the Generation Area.
          const [shapeWidth, shapeHeight] = controlImageFile && assembledResult
            ? [assembledResult.targetWidth, assembledResult.targetHeight]
            : [gen.w, gen.h];
          finalParams = {
            ...(finalParams as MuseImageParams),
            aspect_ratio: explicitListRatio ?? deriveMuseImageAspectRatio(shapeWidth, shapeHeight),
          };
        }

        if (isListProjectGeneration && isFluxTwoPro && preserveSourceAspectRatio) {
          finalParams = {
            ...(finalParams as FluxTwoProParams),
            aspect_ratio: closestSupportedAspectRatio(
              assembledResult!.targetWidth / assembledResult!.targetHeight,
              FLUX_TWO_SUPPORTED_RATIOS,
            ) as FluxTwoProAspectRatio,
          };
        }

        // LIST: an explicitly-set aspect_ratio param is the stated intent,
        // never overwrite it from canvas geometry the user cannot see. Canvas
        // keeps geometry-derived aspect: the drawn generation area is visible
        // and authoritative there.
        let explicitListAspectRatioValue: number | null = null;
        if (isListProjectGeneration && (isNanoFamily || isFluxTwoPro || diffusionModel === 'ideogram-3')) {
          const explicitAspectRaw = (finalParams as { aspect_ratio?: unknown }).aspect_ratio;
          if (typeof explicitAspectRaw === 'string' && explicitAspectRaw !== 'auto' && explicitAspectRaw !== 'match_input_image') {
            const ratioMapForModel: Record<string, number> = diffusionModel === 'ideogram-3'
              ? Object.fromEntries(['1x1', '16x9', '9x16', '4x3', '3x4', '3x2', '2x3'].map((ratio) => {
                  const [width = 1, height = 1] = ratio.split('x').map(Number);
                  return [ratio, width / height];
                }))
              : isFluxTwoPro
                ? (FLUX_TWO_SUPPORTED_RATIOS as Record<string, number>)
                : ((isNanoBanana2OrNewer(diffusionModel) ? NANO_2_SUPPORTED_RATIOS : NANO_SUPPORTED_RATIOS) as Record<string, number>);
            const ratioValue = ratioMapForModel[explicitAspectRaw];
            if (typeof ratioValue === 'number' && Number.isFinite(ratioValue) && ratioValue > 0) {
              explicitListAspectRatioValue = ratioValue;
              console.log(`[${diffusionModel}] LIST: honoring explicit aspect_ratio '${explicitAspectRaw}'.`);
            }
          }
        }

        if (isFluxTwoPro && explicitListAspectRatioValue === null) {
          const workspace = kernel.current.getWorkspace(finalWorkspaceId) ?? finalWorkspace;

          const frame = workspace.frameRect;
          const gen = rectOverride || workspace.generationRect;
          const threshold = 1;
          const coversEntireFrame =
            Math.abs(frame.x - gen.x) < threshold &&
            Math.abs(frame.y - gen.y) < threshold &&
            Math.abs(frame.w - gen.w) < threshold &&
            Math.abs(frame.h - gen.h) < threshold;

          const ratioFromWorkspace = typeof workspace.aspectRatio === 'number' ? workspace.aspectRatio : undefined;
          const ratioFromAssembly = assembledResult?.targetHeight ? assembledResult.targetWidth / assembledResult.targetHeight : undefined;
          const ratioFromRect = gen.h > 0 ? gen.w / gen.h : 1;
          const targetAspectRatio = coversEntireFrame ? (ratioFromAssembly ?? ratioFromRect) : (ratioFromWorkspace ?? ratioFromAssembly ?? ratioFromRect);

          if (!targetAspectRatio) {
            finalParams = { ...(finalParams as FluxTwoProParams), aspect_ratio: (modelSupportsMainImageInput && hasBaseLayers) ? 'match_input_image' : '1:1' };
          } else if (targetAspectRatio) {
            let bestMatch: { ratioKey: FluxTwoProAspectRatio | null; diff: number } = { ratioKey: null, diff: Number.POSITIVE_INFINITY };
            for (const key in FLUX_TWO_SUPPORTED_RATIOS) {
              const diff = Math.abs(FLUX_TWO_SUPPORTED_RATIOS[key as Exclude<FluxTwoProAspectRatio, 'match_input_image'>] - targetAspectRatio);
              if (diff < bestMatch.diff) {
                bestMatch = { ratioKey: key as FluxTwoProAspectRatio, diff };
              }
            }
            const TOLERANCE = 0.002;
            const ratioToUse = bestMatch.ratioKey && bestMatch.diff < TOLERANCE ? bestMatch.ratioKey : ((modelSupportsMainImageInput && hasBaseLayers) ? 'match_input_image' : '1:1');
            console.log(`[Flux 2 Pro] Source ratio ${targetAspectRatio.toFixed(3)} ${bestMatch.ratioKey && bestMatch.diff < TOLERANCE ? `matches ${ratioToUse}` : ((modelSupportsMainImageInput && hasBaseLayers) ? 'will use match_input_image mode.' : 'is custom but no base image; defaulting to 1:1.')}`);
            finalParams = { ...(finalParams as FluxTwoProParams), aspect_ratio: ratioToUse };
          }
        }

        const rect : Rect = taskRectOverride || finalWorkspace.generationRect;

        let targetWidth = assembledResult?.targetWidth || rect.w
        let targetHeight = assembledResult?.targetHeight || rect.h

        if (diffusionModel === 'gpt-image-2.5') {
          const geometry = resolveGptImage25OutputGeometry(rect);
          const currentPixels = Math.max(1, targetWidth * targetHeight);
          const targetPixels = geometry.width * geometry.height;
          const scaleFactor = Math.sqrt(targetPixels / currentPixels);
          targetWidth = geometry.width;
          targetHeight = geometry.height;
          // The task merger uses this to convert provider pixels back into workspace units.
          // Text-only/full-frame runs have no assembledResult, so this must not stay at 1.
          upscaleFactor *= scaleFactor;
        }

        const isMaiModel = isMaiModelId(diffusionModel);
        if (isMaiModel) {
          const dimensions = resolveMaiImageDimensions(
            preserveSourceAspectRatio ? targetWidth : rect.w,
            preserveSourceAspectRatio ? targetHeight : rect.h,
          );
          targetWidth = dimensions.width;
          targetHeight = dimensions.height;
          upscaleFactor = targetWidth / rect.w;
        }

        // LIST + explicit aspect: the OUTPUT geometry follows the requested
        // ratio, not the input image's shape, reshape the target dims to the
        // explicit ratio at the same pixel budget.
        if (explicitListAspectRatioValue !== null && targetWidth > 0 && targetHeight > 0) {
          const pixelBudget = targetWidth * targetHeight;
          targetWidth = Math.max(2, Math.round(Math.sqrt(pixelBudget * explicitListAspectRatioValue)));
          targetHeight = Math.max(2, Math.round(Math.sqrt(pixelBudget / explicitListAspectRatioValue)));
        }

        if (diffusionModel === 'nano-banana-pro' || isNanoBanana2OrNewer(diffusionModel)) {
            const nanoParams = params as NanoBananaProParams | NanoBanana2Params;
            let targetMegaPixels = 1; 

            if (nanoParams.imageSize === '4K') targetMegaPixels = 16;
            else if (nanoParams.imageSize === '2K') targetMegaPixels = 4;
            
            if (targetMegaPixels > 1) {
                const currentPixels = targetWidth * targetHeight;
                const desiredPixels = targetMegaPixels * 1000000;
                const scaleFactor = Math.sqrt(desiredPixels / currentPixels);
                targetWidth = Math.round(targetWidth * scaleFactor);
                targetHeight = Math.round(targetHeight * scaleFactor);
                upscaleFactor = upscaleFactor * scaleFactor;
            }
        } else if (diffusionModel === 'flux-2-pro' || diffusionModel === 'flux-2-klein-4b' || diffusionModel === 'fater-enhance' || diffusionModel === 'fater-upscale') {
            const fluxParams = params as FluxTwoProParams;
            const desiredMegaPixels = fluxTwoResolutionToMegapixels(fluxParams.resolution);
            if (desiredMegaPixels && desiredMegaPixels > 0) {
                const currentPixels = targetWidth * targetHeight;
                const desiredPixels = desiredMegaPixels * 1000000;
                if (desiredPixels > currentPixels) {
                    const scaleFactor = Math.sqrt(desiredPixels / currentPixels);
                    targetWidth = Math.round(targetWidth * scaleFactor);
                    targetHeight = Math.round(targetHeight * scaleFactor);
                    upscaleFactor = upscaleFactor * scaleFactor;
                }
            }
        } else if (diffusionModel === 'topaz-image-enhance') {
            const topazParams = params as TopazImageEnhanceParams;
            const hasResolutionContract = hasTopazImageResolutionContract(topazParams);
            if (hasResolutionContract) {
              const outputMegaPixels = topazParams.outputMegaPixels === 16 || topazParams.outputMegaPixels === 30
                ? topazParams.outputMegaPixels
                : 40;
              const topazInputWidth = targetWidth;
              const topazInputHeight = targetHeight;
              const effectiveFactor = getTopazUpscaleFactorFromParams(topazParams, topazInputWidth, topazInputHeight);
              targetWidth = Math.max(1, Math.round(topazInputWidth * effectiveFactor));
              targetHeight = Math.max(1, Math.round(topazInputHeight * effectiveFactor));
              upscaleFactor *= effectiveFactor;
              finalParams = {
                ...(finalParams as TopazImageEnhanceParams),
                outputMegaPixels,
                upscale_factor: effectiveFactor,
                topazInputWidth,
                topazInputHeight,
              } as DiffusionParams;
            }
        } else if (diffusionModel === 'seedvr2') {
            const seedvr2Params = params as SeedVR2Params;
            const sourceWidth = targetWidth;
            const sourceHeight = targetHeight;
            const outputMegaPixels = normalizeSeedVR2OutputMegaPixels(seedvr2Params.outputMegaPixels);
            const targetPixels = SEEDVR2_OUTPUT_PIXELS[outputMegaPixels];
            const outputDimensions = resolveSeedVR2OutputDimensions({ sourceWidth, sourceHeight, targetPixels });
            targetWidth = outputDimensions.width;
            targetHeight = outputDimensions.height;
            upscaleFactor *= outputDimensions.upscaleFactor;
            finalParams = {
              ...finalParams,
              ...SEEDVR2_STANDARD_POSTPROCESS,
              outputMegaPixels,
              upscale_factor: outputDimensions.upscaleFactor,
            };
        } else if (qwen21Geometry || qwen3Geometry) {
            // The provider is handed these dimensions, so the main image is redrawn to them below.
            ({ width: targetWidth, height: targetHeight, upscaleFactor } = (qwen21Geometry ?? qwen3Geometry)!);
            if (qwen21Geometry) {
              // The longest side at the densest layer's native pixels, over the workspace's own Generation Area.
              const nativeLongestSide = await kernel.current.getIdealLongestCanvasSize(finalWorkspaceId) ?? 0;
              throwIfAborted(abortSignal);
              const workspaceLongestSide = Math.max(finalWorkspace.generationRect.w, finalWorkspace.generationRect.h);
              finalParams = {
                ...(finalParams as QwenImage21Params),
                borderLock: resolveQwenImage21BorderLock(generationArea, finalWorkspace.frameRect, (params as QwenImage21Params).resolution, qwen21Geometry),
                referenceScale: resolveQwenImage21ReferenceScale(nativeLongestSide / workspaceLongestSide, qwen21Geometry.upscaleFactor),
              };
            }
        }

        if (diffusionModel === 'seedream4.5') {
            targetWidth = clampDimension(targetWidth);
            targetHeight = clampDimension(targetHeight);
        }

        // MAI takes a PNG at its exact output geometry; the API server never resizes pixels.
        if (isMaiModel && controlImageFile) {
          throwIfAborted(abortSignal);
          const bitmap = await createImageBitmap(controlImageFile);
          try {
            const blob = await drawBitmapToBlob(bitmap, Math.max(1, Math.round(targetWidth)), Math.max(1, Math.round(targetHeight)), 'image/png', 'MAI source image');
            controlImageFile = new File([blob], 'mai-source.png', { type: 'image/png' });
          } finally {
            if (typeof bitmap.close === 'function') bitmap.close();
          }
          throwIfAborted(abortSignal);
        }

        if ((qwen21Geometry || qwen3Geometry) && controlImageFile) {
          const geometry = (qwen21Geometry ?? qwen3Geometry)!;
          controlImageFile = await redrawGenerationInput(
            controlImageFile,
            () => geometry,
            renderMimeType,
            qwen21Geometry ? 'Qwen Image 2.1 input image' : 'Qwen Image 3 input image',
            // A List main-input override is fetched from storage and can already match the geometry:
            // without a reencode its original bytes would reach fal typed as something else.
            !qwen21Geometry,
          );
          // Past the frame the render holds what the canvas never shows: the frame's edges are extended over it instead.
          // A named base image is the image itself, left whole.
          const inside = qwen21Geometry && !mainInputAssetUrl ? qwen21Geometry.insidePx : null;
          if (inside && (inside.left > 0 || inside.top > 0 || inside.right < geometry.width || inside.bottom < geometry.height)) {
            controlImageFile = await extendEdgesPast(controlImageFile, inside, renderMimeType);
          }
          throwIfAborted(abortSignal);
        }

        // Per model, never a user preference: it also drives the output geometry.
        const shouldAutoIntegrate = generationOptions?.autoIntegrate !== false && TASK_AUTO_INTEGRATE_BY_MODEL[diffusionModel] === true;
        const autoColorMatch = resolveAutoColorMatch(generationOptions?.autoColorMatch, kernel.current?.getUserPreferences().autoColorMatch);
        const mergeStrategy: MergeStrategy = 'stretchOnArea';
        
        if (extraFiles.length > 0 && !isListProjectGeneration) {
          const targetAspectRatio = targetWidth / targetHeight;
          const resizePromises = extraFiles.map(async (file) => {
              const image = new Image();
              const imageUrl = URL.createObjectURL(file);
              try {
                  image.src = imageUrl;
                  await new Promise((resolve, reject) => {
                      image.onload = resolve;
                      image.onerror = () => reject(new Error(`Failed to load reference image: ${file.name}`));
                  });
              } finally {
                  URL.revokeObjectURL(imageUrl);
              }

              const imageWidth = image.naturalWidth;
              const imageHeight = image.naturalHeight;
              const imageAspectRatio = imageWidth / imageHeight;

              let canvasWidth, canvasHeight;
              if (imageAspectRatio > targetAspectRatio) {
                  canvasWidth = imageWidth;
                  canvasHeight = Math.round(imageWidth / targetAspectRatio);
              } else {
                  canvasHeight = imageHeight;
                  canvasWidth = Math.round(imageHeight * targetAspectRatio);
              }

              const canvas = document.createElement('canvas');
              canvas.width = canvasWidth;
              canvas.height = canvasHeight;
              const ctx = canvas.getContext('2d');
              if (!ctx) throw new Error(`Could not get canvas context for ${diffusionModel} ref resizer`);

              ctx.clearRect(0, 0, canvasWidth, canvasHeight);
              const offsetX = (canvasWidth - imageWidth) / 2;
              const offsetY = (canvasHeight - imageHeight) / 2;
              ctx.drawImage(image, offsetX, offsetY);

              const newBlob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, renderMimeType));
              if (!newBlob) throw new Error(`Could not create blob from canvas for ${diffusionModel} ref resizer`);

              const baseName = file.name.replace(/\.[^/.]+$/, '');
              return new File([newBlob], `${baseName}.${renderExtension}`, { type: renderMimeType });
          });

          extraFiles = await Promise.all(resizePromises);
          throwIfAborted(abortSignal);
        }

        // After the padding above, which can grow a reference past its budget.
        const referenceImageBudget = studioModelDefinition?.capabilities.referenceImageBudget;
        if (referenceImageBudget && extraFiles.length > 0) {
          extraFiles = await Promise.all(extraFiles.map((file) => redrawGenerationInput(
            file,
            (width, height) => fitReferenceImageToBudget(width, height, referenceImageBudget),
            renderMimeType,
            'reference image',
          )));
          throwIfAborted(abortSignal);
        }

        // fal refuses any reference side outside 384-2048px, a PNG carrying alpha, or a file above
        // 10 MB. The resizer above leaves the file untouched on the List path, where references come
        // straight from storage, so this pass always reencodes instead of only resizing.
        if (qwen3Geometry && extraFiles.length > 0) {
          extraFiles = await Promise.all(extraFiles.map((file) => (
            redrawGenerationInput(file, fitQwenImage3Reference, renderMimeType, 'Qwen Image 3 reference image', true)
          )));
          throwIfAborted(abortSignal);
        }

        const mainImagePrivateUrlOverride = generationOptions?.mainImagePrivateUrlOverride?.trim() || undefined;
        const listMaiCanonicalSource = isListProjectGeneration && explicitMainInputUrl && isMaiModel
          && typeof (finalParams as unknown as Record<string, unknown>).listEditSourceOutputId === 'string'
          ? explicitMainInputUrl
          : undefined;
        // Topaz and Magnific read the verified List original server-side, so conversion cannot sever identity lineage.
        const mainUploadPromise = isListProjectGeneration && explicitMainInputUrl && !isMaiModel && (!mainInputWasTransformed || diffusionModel === 'topaz-image-enhance' || diffusionModel === 'magnific-image-upscale') && typeof (finalParams as unknown as Record<string, unknown>).listEditSourceOutputId === 'string'
          ? Promise.resolve({ publicUrl: explicitMainInputUrl, privateUrl: undefined })
          : controlImageFile
          ? mainImagePrivateUrlOverride && !isMaiModel
            ? getCloudTmpUrl(controlImageFile, 1800, undefined, abortSignal).then((publicUrl) => ({ publicUrl, privateUrl: mainImagePrivateUrlOverride }))
            : dualUploadAsset(controlImageFile, {
                ttlSeconds: 1800,
                filenamePrefix: 'edit-diffuse-src',
                fallbackContentType: renderMimeType,
                fallbackExtension: renderExtension,
                projectIdOverride: projectId,
              }, finalWorkspaceId, abortSignal)
          : Promise.resolve({ publicUrl: undefined, privateUrl: undefined });

        const extraUploadsPromise =
          extraFiles.length > 0
            ? dualUploadFiles(extraFiles, 'edit-diffuse-ref', 1800, finalWorkspaceId, abortSignal, projectId)
            : Promise.resolve({ publicUrls: [], privateUrls: [] });

        // Ideogram inpaint: render the painted Mask Pen mask to a B/W PNG. Ideogram requires the
        // mask to be the EXACT pixel size of the source image, or it silently ignores it and does
        // a full regeneration, so size the mask from the decoded source file, not the (possibly
        // rounded) target dims.
        const inpaintMaskShapes = diffusionModel === 'ideogram-inpaint'
          ? (kernel.current.getInpaintMaskShapes()[finalWorkspaceId] ?? null)
          : null;
        const maskUploadPromise: Promise<{ publicUrl?: string; pulseMaskFile?: File }> = (inpaintMaskShapes && inpaintMaskShapes.length > 0)
          ? (async () => {
              let maskW = targetWidth;
              let maskH = targetHeight;
              if (controlImageFile) {
                try {
                  const bmp = await createImageBitmap(controlImageFile);
                  maskW = bmp.width;
                  maskH = bmp.height;
                  if (typeof bmp.close === 'function') bmp.close();
                } catch (e) {
                  console.warn('[ideogram-inpaint] could not decode source image dims; falling back to target dims', e);
                }
              }
              console.log(`[ideogram-inpaint] sourceDims=${maskW}x${maskH} targetDims=${targetWidth}x${targetHeight} shapes=${inpaintMaskShapes.length}`);
              const maskBlobs = await buildInpaintMaskBlobs(inpaintMaskShapes, finalWorkspace.frameRect, rect, maskW, maskH);
              if (!maskBlobs) return { publicUrl: undefined };
              const providerMaskFile = new File([maskBlobs.providerMaskBlob], 'inpaint-mask.png', { type: 'image/png' });
              const pulseMaskFile = maskBlobs.pulseMaskBlob
                ? new File([maskBlobs.pulseMaskBlob], 'inpaint-pulse-mask.png', { type: 'image/png' })
                : undefined;
              const { publicUrl } = await dualUploadAsset(providerMaskFile, {
                ttlSeconds: 1800,
                filenamePrefix: 'edit-diffuse-mask',
                fallbackContentType: 'image/png',
                fallbackExtension: 'png',
              }, finalWorkspaceId, abortSignal);
              return { publicUrl, pulseMaskFile };
            })()
          : Promise.resolve({ publicUrl: undefined });

        const [{ publicUrl: cloudUrl, privateUrl: cloudPrivateUrl }, extraUploadResults, maskUploadResult] = await Promise.all([
          mainUploadPromise,
          extraUploadsPromise,
          maskUploadPromise,
        ]);
        throwIfAborted(abortSignal);
        const inpaintMaskUrl = maskUploadResult.publicUrl;
        const inpaintPulseMaskFile = maskUploadResult.pulseMaskFile;
        
        const extraImageUrls = extraUploadResults.publicUrls;
        const extraImagePrivateUrls = extraUploadResults.privateUrls;
        const normalizedExtraImagePrivateUrls =
          extraImagePrivateUrls.length > 0 ? extraImagePrivateUrls : undefined;

        const tasksToCreate: GenerationTask[] = [];

        if(!finalParams) throw 'Missing params.'

        const taskProjectId = projectId;
        const taskChatSessionId = resolveTaskChatSessionId(taskProjectId);

        for (let i = 0; i < numGenerations; i++) {
          throwIfAborted(abortSignal);
          const randomSeed = Math.floor(Math.random() * 2100000000);
          const taskId = crypto.randomUUID();
          const newTask: GenerationTask = {
            id: taskId,
            chatSessionId: taskChatSessionId,
            isAgentSpawned: resolveTaskIsAgentSpawned(taskChatSessionId),
            type: 'diffuse',
            workspaceId : finalWorkspace?.id || workspaceId,
            outputScope: generationOutputScope ?? {
              kind: 'WORKSPACE',
              workspaceId: finalWorkspace?.id || workspaceId,
            },
            params: diffusionModel === 'gpt-image-2.5'
              ? { ...finalParams }
              : {
                  ...finalParams,
                  ...(generationOptions?.factoryRunId ? { factoryRunId: generationOptions.factoryRunId } : {}),
                  seed: ((numGenerations === 1) && typeof (finalParams as { seed?: number }).seed === 'number' && (finalParams as { seed: number }).seed !== -1) ? (finalParams as { seed: number }).seed : randomSeed,
                },
            ...(cloudUrl ? { mainImagePublicUrl: listMaiCanonicalSource ?? cloudUrl } : {}),
            ...(diffusionModel === 'gpt-image-2.5' ? (extraImageUrls.length > 0 ? { urls: extraImageUrls } : {}) : {
              maskUrl: inpaintMaskUrl,
              maskFile: inpaintPulseMaskFile,
              mainImageData: assembledResult?.imageData,
              extraImageUrl: extraImageUrls[0],
              extraImageUrlPrivate: extraImagePrivateUrls[0],
              mainImagePrivateUrl: cloudPrivateUrl,
              urls: extraImageUrls,
              referenceImagePrivateUrls: normalizedExtraImagePrivateUrls,
            }),
            model: diffusionModel,
            mergeStrategy,
            autoIntegrateWithUnderlying: shouldAutoIntegrate,
            autoColorMatch,
            targetWidth,
            targetHeight,
            upscaleFactor,
            blurRadius : 0,
            rect: rect,
            projectId: taskProjectId,
            layerOverride: generatedImageTimelineOverride
              ? { ...generatedImageTimelineOverride }
              : undefined,
            layerContextSnapshot: isListProjectGeneration ? [] : getContextSnapshot(finalWorkspaceId),
            parentVersionId: parentVersionId,
            layerConsolidation: generationOptions?.layerConsolidation ?? null,
            sourceListOutputId: generationOptions?.sourceListOutputId,
            factoryRunId: generationOptions?.factoryRunId,
          };
          tasksToCreate.push(newTask);
        }

        throwIfAborted(abortSignal);
        generationOptions?.onTasksCreated?.(tasksToCreate);
        kernel.current?.processTasks(tasksToCreate, { abortSignal });
        return { tasks: tasksToCreate, newWorkspaceId: null };
    } catch (error) {
      if (isAbortError(error)) {
        kernel.current?.setIsSubmittingAITask(false);
        throw error;
      }
      console.error('Error during diffusion queueing:', error);
      if (error instanceof MaiImageInputError || error instanceof ReferenceImageBudgetError) {
        kernel.current?.handleError(error.message);
        kernel.current?.setIsSubmittingAITask(false);
        throw error;
      }
      kernel.current?.handleError('Failed to queue diffusion task.');
      kernel.current?.setIsSubmittingAITask(false);
      return { tasks: [], newWorkspaceId: null };
    }
  }, [ kernel, getContextSnapshot, dualUploadAsset, dualUploadFiles, getProjectIdFromWorkspaceId, getExtraFiles, getLocalNumOutputs, getWorkspaceLayers, resolveTaskChatSessionId, resolveTaskIsAgentSpawned ]);


  const autoCut = useCallback(async (workspaceId: string, layerOverride?: Partial<Layer>, abortSignal?: AbortSignal): Promise<{ tasks: GenerationTask[], newWorkspaceId: string | null }> => {
    if (!kernel.current) {
      console.error('Inpaint canvas doesnt exists.');
      return { tasks: [], newWorkspaceId: null };
    }
    throwIfAborted(abortSignal);

    if (getVisibleWorkspaceLayers(workspaceId).length === 0) {
      kernel.current?.handleError('Auto-cut requires at least one visible layer in the workspace.');
      return { tasks: [], newWorkspaceId: null };
    }

    kernel.current?.setIsSubmittingAITask(true);
    try {
      const sourceWorkspace = kernel.current.getWorkspace(workspaceId);
      if (!sourceWorkspace) {
        kernel.current?.handleError("Could not find the active workspace for auto-cut.");
        return { tasks: [], newWorkspaceId: null };
      }

      const finalWorkspaceId = workspaceId;

      const longest_size = await kernel.current?.getIdealLongestCanvasSize(finalWorkspaceId);
      throwIfAborted(abortSignal);
      if(!longest_size) {
        throw 'Error Longest size null';
      }
      const outputSize = Math.min(longest_size, 2048);

      const result = await kernel.current?.renderWorkspaceArea(finalWorkspaceId, undefined, outputSize*outputSize, undefined, false);
      throwIfAborted(abortSignal);
      if (!result.file) throw 'No bg image';
      const { publicUrl: imageToCutUrl, privateUrl: imageToCutPrivateUrl } = await dualUploadAsset(result.file, {
        ttlSeconds: 1800,
        filenamePrefix: 'autocut-src',
        fallbackContentType: 'image/webp',
        fallbackExtension: 'webp',
      }, finalWorkspaceId, abortSignal);
      throwIfAborted(abortSignal);

      const tasksToCreate: GenerationTask[] = [];
      const taskId = crypto.randomUUID();
      const finalWorkspace = kernel.current.getWorkspace(finalWorkspaceId);
      if (!finalWorkspace) {
          throw new Error(`Workspace with ID ${finalWorkspaceId} not found.`);
      }
      const generationRect = finalWorkspace.generationRect;
      const projectId = getProjectIdFromWorkspaceId(finalWorkspaceId, 'autoCut');
      const taskChatSessionId = resolveTaskChatSessionId(projectId);
      const newTask: GenerationTask = {
        id: taskId,
        chatSessionId: taskChatSessionId,
        isAgentSpawned: resolveTaskIsAgentSpawned(taskChatSessionId),
        type: 'diffuse',
        workspaceId: finalWorkspaceId,
        outputScope: { kind: 'WORKSPACE', workspaceId: finalWorkspaceId },
        model: 'birefnet',
        mainImagePublicUrl: imageToCutUrl,
        mainImagePrivateUrl: imageToCutPrivateUrl,
        mergeStrategy: 'stretchOnArea',
        targetWidth: result.targetWidth,
        targetHeight: result.targetHeight,
        upscaleFactor: result.upscaleFactor,
        blurRadius: 0,
        rect: {...generationRect},
        layerOverride,
        projectId,
        layerContextSnapshot: getContextSnapshot(finalWorkspaceId)
      };

      tasksToCreate.push(newTask);
      throwIfAborted(abortSignal);
      kernel.current?.processTasks(tasksToCreate, { abortSignal });
      return { tasks: tasksToCreate, newWorkspaceId: null };
    } catch(e : any) {
      if (isAbortError(e) || abortSignal?.aborted) {
        kernel.current?.setIsSubmittingAITask(false);
        throw e;
      }
      console.error('Autocut failed with error :', e)
      kernel.current?.setIsSubmittingAITask(false);
      return { tasks: [], newWorkspaceId: null };
    }
  }, [kernel, getContextSnapshot, dualUploadAsset, getProjectIdFromWorkspaceId, getVisibleWorkspaceLayers, resolveTaskChatSessionId, resolveTaskIsAgentSpawned]);


  const handleQueueFilm = useCallback(async (
    workspaceId: string,
    runtimeStateOverride: RuntimeState | undefined,
    parentVersionId: string | null,
    numOutputsOverride?: number,
    abortSignal?: AbortSignal,
    generationOptions?: ModelRunGenerationOptions
  ): Promise<{ tasks: GenerationTask[], newWorkspaceId: string | null }> => {
    if (!kernel.current) return { tasks: [], newWorkspaceId: null };
    throwIfAborted(abortSignal);

    const sourceWorkspace = workspaceId ? kernel.current.getWorkspace(workspaceId) : null;
    const projectId = generationOptions?.projectId?.trim()
      || (workspaceId ? getProjectIdFromWorkspaceId(workspaceId, 'handleQueueFilm') : '');
    const generationOutputScope = generationOptions?.outputScope
      ?? resolveGenerationOutputScopeForView(projectId, workspaceId, getProjectStudioViewMode(kernel.current, projectId));
    const isListProjectGeneration = generationOutputScope?.kind === 'PROJECT';
    const allLayers = workspaceId && !isListProjectGeneration ? getWorkspaceLayers(workspaceId) : [];
    const outputWorkspace = isListProjectGeneration
      ? createOutputOnlyWorkspace(projectId, sourceWorkspace ?? undefined)
      : sourceWorkspace ?? (projectId ? createOutputOnlyWorkspace(projectId) : null);

    if (!outputWorkspace) {
        kernel.current?.handleError("Could not resolve a project for output-only video generation.");
        return { tasks: [], newWorkspaceId: null };
    }
    const diffusionModel = runtimeStateOverride?.diffusionModel || kernel.current.getDiffusionModel();
    const isSeedance2 = diffusionModel === 'seedance-2.0' || diffusionModel === 'seedance-2.0-fast' || diffusionModel === 'seedance-2.5';
    const isKling3Omni = diffusionModel === 'kling-3-omni';
    const isGeminiOmniFlash = diffusionModel === 'gemini-omni-flash-preview';
    const modelParams = runtimeStateOverride?.parameters || kernel.current.getModelParams();
    const params = modelParams[diffusionModel];
    if (!params) throw 'Missing params.';
    const promptText = typeof params.prompt === 'string' ? params.prompt.trim() : '';
    if (studioModelRequiresPrompt(diffusionModel) && promptText.length === 0) {
      kernel.current?.handleError(`${diffusionModel} requires a non-empty prompt before generation.`);
      return { tasks: [], newWorkspaceId: null };
    }
    const supportsReferenceInputMode = isSeedance2 || isKling3Omni || isGeminiOmniFlash;
    const referenceInputMode =
      supportsReferenceInputMode && (params as { reference_input_mode?: unknown }).reference_input_mode === 'video'
        ? 'video'
        : 'frame';
    const shouldUseVideoReference = supportsReferenceInputMode && referenceInputMode === 'video';
    const shouldUseSeedanceVideoReference = isSeedance2 && shouldUseVideoReference;
    const shouldUseKlingOmniVideoReference = isKling3Omni && shouldUseVideoReference;
    const shouldUseGeminiOmniVideoReference = isGeminiOmniFlash && shouldUseVideoReference;
    if (isListProjectGeneration && shouldUseVideoReference) {
      kernel.current.handleError('Video-reference input is not available for this model in List. Use an explicit image in frame mode, or a supported text-to-video request.');
      return { tasks: [], newWorkspaceId: null };
    }
    const directSourceImageUrl = typeof generationOptions?.mainImageUrlOverride === 'string'
      ? generationOptions.mainImageUrlOverride.trim() || undefined : undefined;

    const visibleLayers = allLayers.filter((layer) => layer.visible);

    if (!videoModelStartsFromText(diffusionModel) && visibleLayers.length === 0 && !(isListProjectGeneration && directSourceImageUrl)) {
        kernel.current?.handleError(isListProjectGeneration
          ? 'This video model requires an explicit source image in List.'
          : 'Video generation requires at least one visible layer in the workspace.');
        return { tasks: [], newWorkspaceId: null };
    }

    const requestedGenerations = numOutputsOverride ?? getLocalNumOutputs(projectId);
    const numGenerations = enforceStudioModelMinimumGenerations(diffusionModel, requestedGenerations);
    kernel.current?.setIsSubmittingAITask(true);
    let createdTasks: GenerationTask[] = [];
    throwIfAborted(abortSignal);

    try {
        const finalWorkspaceId = workspaceId;
        const sortedVisibleLayers = sortLayersByRenderOrder(visibleLayers);
        const visibleVideoLayers = sortedVisibleLayers
          .filter((layer): layer is Layer => (
            layer.type === 'video' &&
            typeof layer.videoSrc === 'string' &&
            layer.videoSrc.trim().length > 0 &&
            !readLayerParamFlag(layer.params, TIMELINE_AUDIO_ONLY_PARAM_KEY)
          ));
        const highestVisibleVideoLayer = visibleVideoLayers[visibleVideoLayers.length - 1] ?? null;

        const videoRefs = isListProjectGeneration ? undefined : kernel.current?.videoRefs.current;
        const workspaceTimelineDurationMs = Math.max(0, toFinite(outputWorkspace.timelineDurationMs) ?? 0);
        const getMediaDurationMsForLayer = async (layer: Layer): Promise<number | null> => {
          const durationSeconds = videoRefs?.[layer.id]?.duration;
          if (typeof durationSeconds === 'number' && Number.isFinite(durationSeconds) && durationSeconds > 0) {
            return durationSeconds * 1000;
          }
          return null;
        };
        const resolvedTimelineTracks = await Promise.all(visibleVideoLayers.map(async (layer, index) => {
          const startMs = Math.max(0, toFinite(layer.timelineStartMs) ?? 0);
          const sourceInMs = Math.max(0, toFinite(layer.timelineSourceInMs) ?? 0);
          const mediaDurationMs = await getMediaDurationMsForLayer(layer);
          const explicitEndMs = toFinite(layer.timelineEndMs);
          const fallbackEndMs = mediaDurationMs !== null
            ? startMs + Math.max(0, mediaDurationMs - sourceInMs)
            : (workspaceTimelineDurationMs > startMs ? workspaceTimelineDurationMs : startMs + 1000);
          const endMs = explicitEndMs !== null && explicitEndMs > startMs
            ? explicitEndMs
            : fallbackEndMs;
          const baseRenderOrder = typeof layer.renderOrder === 'number' && Number.isFinite(layer.renderOrder)
            ? layer.renderOrder
            : index;
          const renderOrder = (Reflect.get(layer, 'top-layer') === true || Reflect.get(layer, 'topLayer') === true)
            ? baseRenderOrder + TOP_LAYER_RENDER_ORDER_OFFSET
            : baseRenderOrder;
          return {
            layer,
            layerType: 'video' as const,
            startMs,
            endMs,
            sourceInMs,
            mediaDurationMs,
            renderOrder,
          };
        }));
        const maxVideoTrackEndMs = resolvedTimelineTracks.reduce((max, track) => Math.max(max, track.endMs), 0);
        const resolvedTimelineCompositionTracks = await Promise.all(sortedVisibleLayers.map(async (layer, index): Promise<TimelineReferenceTrack | null> => {
          const hasVideoSource = typeof layer.videoSrc === 'string' && layer.videoSrc.trim().length > 0;
          const isVideoLayer = layer.type === 'video' && hasVideoSource && !readLayerParamFlag(layer.params, TIMELINE_AUDIO_ONLY_PARAM_KEY);
          const isAudioLayer = (layer.type === 'audio' || (layer.type === 'video' && readLayerParamFlag(layer.params, TIMELINE_AUDIO_ONLY_PARAM_KEY))) && hasVideoSource;
          const isImageLayer = !isVideoLayer && !isAudioLayer && typeof layer.imageSrc === 'string' && layer.imageSrc.trim().length > 0;
          if (!isVideoLayer && !isAudioLayer && !isImageLayer) return null;

          const startMs = Math.max(0, toFinite(layer.timelineStartMs) ?? 0);
          const sourceInMs = (isVideoLayer || isAudioLayer) ? Math.max(0, toFinite(layer.timelineSourceInMs) ?? 0) : 0;
          const mediaDurationMs = isVideoLayer ? await getMediaDurationMsForLayer(layer) : null;
          const explicitEndMs = toFinite(layer.timelineEndMs);
          const fallbackEndMs = isVideoLayer
            ? (mediaDurationMs !== null
              ? startMs + Math.max(0, mediaDurationMs - sourceInMs)
              : (workspaceTimelineDurationMs > startMs ? workspaceTimelineDurationMs : startMs + 1000))
            : isAudioLayer
            ? (workspaceTimelineDurationMs > startMs ? workspaceTimelineDurationMs : startMs + 1000)
            : (
              workspaceTimelineDurationMs > startMs
                ? workspaceTimelineDurationMs
                : (maxVideoTrackEndMs > startMs ? maxVideoTrackEndMs : startMs + 1000)
            );
          const endMs = explicitEndMs !== null && explicitEndMs > startMs
            ? explicitEndMs
            : fallbackEndMs;
          const renderOrder = typeof layer.renderOrder === 'number' && Number.isFinite(layer.renderOrder)
            ? layer.renderOrder
            : index;
          return {
            layer,
            layerType: isVideoLayer ? 'video' as const : (isAudioLayer ? 'audio' as const : 'image' as const),
            startMs,
            endMs,
            sourceInMs,
            renderOrder: (Reflect.get(layer, 'top-layer') === true || Reflect.get(layer, 'topLayer') === true)
              ? renderOrder + TOP_LAYER_RENDER_ORDER_OFFSET
              : renderOrder,
            mediaDurationMs,
            audioEnabled: isVideoLayer ? !readLayerParamFlag(layer.params, TIMELINE_MUTE_AUDIO_PARAM_KEY) : isAudioLayer,
            audioGain: isAudioLayer
              ? clamp(readLayerParamNumber(layer.params, TIMELINE_AUDIO_GAIN_PARAM_KEY) ?? 1, 0, AUDIO_GAIN_MAX)
              : 1,
            fadeInRatio: isAudioLayer ? 0 : normalizeTrackEffectRatio(readLayerParamNumber(layer.params, TIMELINE_FADE_IN_RATIO_PARAM_KEY)),
            fadeOutRatio: isAudioLayer ? 0 : normalizeTrackEffectRatio(readLayerParamNumber(layer.params, TIMELINE_FADE_OUT_RATIO_PARAM_KEY)),
          };
        }));
        const resolvedRenderableTimelineTracks = resolvedTimelineCompositionTracks
          .filter((track): track is TimelineReferenceTrack => Boolean(track));

        const playbackStartMs = resolvedRenderableTimelineTracks.length > 0
          ? resolvedRenderableTimelineTracks.reduce((min, track) => Math.min(min, track.startMs), Number.POSITIVE_INFINITY)
          : 0;
        const playbackEndMs = resolvedRenderableTimelineTracks.reduce((max, track) => Math.max(max, track.endMs), 0);
        const workspaceCursorMs = Math.max(0, toFinite(outputWorkspace.timelineCursorMs) ?? 0);
        const clampedCursorMs = resolvedRenderableTimelineTracks.length > 0
          ? Math.max(playbackStartMs, Math.min(workspaceCursorMs, playbackEndMs))
          : workspaceCursorMs;
        const {
          selectedVideoTrack,
        } = isListProjectGeneration ? { selectedVideoTrack: null } : resolvePreferredTimelineTrackSelection(
          projectId,
          clampedCursorMs,
          resolvedRenderableTimelineTracks,
          resolvedTimelineTracks,
        );
        const imageInputFrameMs = (() => {
          const rawMs = clampedCursorMs;
          if (!Number.isFinite(playbackStartMs) || playbackEndMs <= playbackStartMs) return Math.max(0, rawMs);
          return Math.max(playbackStartMs, Math.min(rawMs, playbackEndMs));
        })();
        const shouldPrepareImageInputFrame = !shouldUseVideoReference;

        const getActiveTrackAtMs = (timelineMs: number) => {
          const overlappingTracks = resolvedTimelineTracks.filter((track) => timelineMs >= track.startMs && timelineMs < track.endMs);
          if (overlappingTracks.length === 0) return null;
          const sortedTracks = [...overlappingTracks].sort((left, right) => {
            if (left.renderOrder !== right.renderOrder) return left.renderOrder - right.renderOrder;
            return left.layer.id.localeCompare(right.layer.id);
          });
          return sortedTracks[sortedTracks.length - 1] ?? null;
        };

        const topVisibleVideoTrackAtCursor = getActiveTrackAtMs(clampedCursorMs);
        const videoInputRangeRequest = readVideoInputRangeRequest(params);
        const explicitVideoInputRangeResult = shouldUseVideoReference
          ? resolveExplicitVideoInputRange({
              request: videoInputRangeRequest,
              tracks: resolvedRenderableTimelineTracks,
              fallbackEndMs: playbackEndMs,
            })
          : null;
        if (explicitVideoInputRangeResult && 'error' in explicitVideoInputRangeResult) {
          kernel.current?.handleError(explicitVideoInputRangeResult.error);
          kernel.current?.setIsSubmittingAITask(false);
          return { tasks: [], newWorkspaceId: null };
        }
        const explicitVideoInputRange = explicitVideoInputRangeResult && !('error' in explicitVideoInputRangeResult)
          ? explicitVideoInputRangeResult
          : null;
        if (shouldUseVideoReference && explicitVideoInputRange) {
          const hasOverlappingVideoTrack = resolvedTimelineTracks.some((track) => (
            track.startMs < explicitVideoInputRange.endMs && track.endMs > explicitVideoInputRange.startMs
          ));
          if (!hasOverlappingVideoTrack) {
            kernel.current?.handleError('The requested video input range does not overlap a visible video track.');
            kernel.current?.setIsSubmittingAITask(false);
            return { tasks: [], newWorkspaceId: null };
          }
        }
        if (shouldUseVideoReference && !explicitVideoInputRange && !topVisibleVideoTrackAtCursor) {
          kernel.current?.handleError('Video input mode needs a visible video track at the current frame, or an explicit start/end time range.');
          kernel.current?.setIsSubmittingAITask(false);
          return { tasks: [], newWorkspaceId: null };
        }

        const waitForVideoMetadata = async (videoElement: HTMLVideoElement): Promise<void> => {
          if (videoElement.readyState >= 1) return;
          await new Promise<void>((resolve, reject) => {
            let timeoutId: ReturnType<typeof setTimeout> | null = null;
            const cleanup = () => {
              videoElement.removeEventListener('loadedmetadata', handleReady);
              videoElement.removeEventListener('error', handleError);
              if (abortSignal) {
                abortSignal.removeEventListener('abort', handleAbort);
              }
              if (timeoutId) {
                clearTimeout(timeoutId);
              }
            };
            const handleReady = () => {
              cleanup();
              resolve();
            };
            const handleError = () => {
              cleanup();
              reject(new Error('Failed to load video metadata while preparing model input frame.'));
            };
            const handleAbort = () => {
              cleanup();
              reject(createAbortError());
            };
            videoElement.addEventListener('loadedmetadata', handleReady, { once: true });
            videoElement.addEventListener('error', handleError, { once: true });
            if (abortSignal) {
              abortSignal.addEventListener('abort', handleAbort, { once: true });
            }
            timeoutId = setTimeout(() => {
              cleanup();
              reject(new Error('Timed out waiting for video metadata while preparing model input frame.'));
            }, 4000);
          });
        };

        const seekVideoTrackToTimelineMs = async (
          track: { layer: Layer; startMs: number; sourceInMs: number },
          timelineMs: number,
          explicitVideoElement?: HTMLVideoElement | null
        ): Promise<void> => {
          const videoElement = explicitVideoElement ?? videoRefs?.[track.layer.id];
          if (!videoElement) return;
          await waitForVideoMetadata(videoElement);
          throwIfAborted(abortSignal);

          const mediaDurationSec = Number.isFinite(videoElement.duration) && videoElement.duration > 0
            ? videoElement.duration
            : Number.POSITIVE_INFINITY;
          const targetSecUnclamped = Math.max(0, (timelineMs - track.startMs + track.sourceInMs) / 1000);
          const targetSec = Number.isFinite(mediaDurationSec)
            ? Math.min(targetSecUnclamped, Math.max(0, mediaDurationSec - 0.001))
            : targetSecUnclamped;
          if (Math.abs(videoElement.currentTime - targetSec) <= 1 / 30) {
            return;
          }

          await new Promise<void>((resolve, reject) => {
            let timeoutId: ReturnType<typeof setTimeout> | null = null;
            const cleanup = () => {
              videoElement.removeEventListener('seeked', handleSeeked);
              videoElement.removeEventListener('error', handleError);
              if (abortSignal) {
                abortSignal.removeEventListener('abort', handleAbort);
              }
              if (timeoutId) {
                clearTimeout(timeoutId);
              }
            };
            const handleSeeked = () => {
              cleanup();
              resolve();
            };
            const handleError = () => {
              cleanup();
              reject(new Error('Failed to seek timeline video while preparing reference.'));
            };
            const handleAbort = () => {
              cleanup();
              reject(createAbortError());
            };
            timeoutId = setTimeout(() => {
              cleanup();
              resolve();
            }, 1500);
            videoElement.addEventListener('seeked', handleSeeked, { once: true });
            videoElement.addEventListener('error', handleError, { once: true });
            if (abortSignal) {
              abortSignal.addEventListener('abort', handleAbort, { once: true });
            }
            try {
              videoElement.pause();
              videoElement.currentTime = targetSec;
            } catch (seekError) {
              cleanup();
              reject(seekError instanceof Error ? seekError : new Error('Video seek failed.'));
            }
          });
          await new Promise<void>((resolve, reject) => {
            let settled = false;
            let timeoutId: ReturnType<typeof setTimeout> | null = null;
            let frameCallbackId: number | null = null;
            let handleAbort: () => void = () => {};
            const withFrameCallback = videoElement as HTMLVideoElement & {
              requestVideoFrameCallback?: (callback: () => void) => number;
              cancelVideoFrameCallback?: (id: number) => void;
            };
            const cleanup = () => {
              if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
              }
              if (
                frameCallbackId !== null &&
                typeof withFrameCallback.cancelVideoFrameCallback === 'function'
              ) {
                withFrameCallback.cancelVideoFrameCallback(frameCallbackId);
              }
              if (abortSignal) {
                abortSignal.removeEventListener('abort', handleAbort);
              }
            };
            const finish = () => {
              if (settled) return;
              settled = true;
              cleanup();
              resolve();
            };
            handleAbort = () => {
              if (settled) return;
              settled = true;
              cleanup();
              reject(createAbortError());
            };
            timeoutId = setTimeout(finish, 500);
            if (abortSignal) {
              abortSignal.addEventListener('abort', handleAbort, { once: true });
            }
            if (typeof withFrameCallback.requestVideoFrameCallback === 'function') {
              frameCallbackId = withFrameCallback.requestVideoFrameCallback(finish);
              return;
            }
            window.requestAnimationFrame(finish);
          });
        };

        const getRenderableLayerIdsAtMs = (timelineMs: number): string[] => {
          const activeTrack = getActiveTrackAtMs(timelineMs);
          return sortedVisibleLayers
            .filter((layer) => {
              if (layer.type !== 'video') return true;
              return Boolean(activeTrack && activeTrack.layer.id === layer.id);
            })
            .map((layer) => layer.id);
        };

        let timelineClipStartMs = imageInputFrameMs;
        let activeTrackAtClipStart = getActiveTrackAtMs(timelineClipStartMs);
        const shouldUseTimelineReferenceClip = shouldUseKlingOmniVideoReference;
        if (
          !shouldUseTimelineReferenceClip &&
          shouldPrepareImageInputFrame
        ) {
          if (activeTrackAtClipStart) {
            try {
              await seekVideoTrackToTimelineMs(activeTrackAtClipStart, timelineClipStartMs);
            } catch (seekError) {
              console.warn(
                'Video seek for model input frame timed out; proceeding with fallback frame capture.',
                seekError
              );
            }
          }
        }

        const createResultFromVideoLayer = async (videoLayer: Layer) => {
          const frameUrl = videoLayer.videoFramesUrls?.[0] || videoLayer.imageSrc || videoLayer.videoSrc;
          if (!frameUrl) {
              throw new Error('Unable to locate a preview frame for the selected video layer.');
          }

          throwIfAborted(abortSignal);
          const frameResponse = await fetch(frameUrl, { cache: 'no-store', signal: abortSignal });
          if (!frameResponse.ok) {
              throw new Error(`Failed to download video preview frame: ${frameResponse.status}`);
          }

          const frameBlob = await frameResponse.blob();
          throwIfAborted(abortSignal);
          const extension = inferExtensionFromBlob(frameBlob, 'png');
          const fileName = `video-frame-${Date.now()}.${extension}`;
          const frameFile = new File([frameBlob], fileName, { type: frameBlob.type || 'image/png' });

          const dimensions = await new Promise<{ width: number; height: number }>((resolve, reject) => {
              const img = new Image();
              const objectUrl = URL.createObjectURL(frameBlob);
              img.onload = () => {
                  if (abortSignal?.aborted) {
                      URL.revokeObjectURL(objectUrl);
                      reject(createAbortError());
                      return;
                  }
                  const width = img.naturalWidth || img.width;
                  const height = img.naturalHeight || img.height;
                  URL.revokeObjectURL(objectUrl);
                  if (!width || !height) {
                      reject(new Error('Video preview frame has invalid dimensions.'));
                      return;
                  }
                  resolve({ width, height });
              };
              img.onerror = () => {
                  URL.revokeObjectURL(objectUrl);
                  reject(new Error('Failed to read video preview frame dimensions.'));
              };
              img.src = objectUrl;
          });

          return {
              file: frameFile,
              width: dimensions.width,
              height: dimensions.height,
              targetWidth: dimensions.width,
              targetHeight: dimensions.height,
              upscaleFactor: 1,
          };
        };

        const isCanvasTaintError = (error: unknown): boolean => {
          const domLikeError = error as { name?: string; message?: string };
          const name = typeof domLikeError?.name === 'string' ? domLikeError.name : '';
          const message = typeof domLikeError?.message === 'string' ? domLikeError.message : '';
          return name === 'SecurityError' || /tainted by cross-origin data/i.test(message);
        };
        const isTimeoutError = (error: unknown): boolean => {
          const message = error instanceof Error ? error.message : String(error ?? '');
          return /timed out/i.test(message);
        };
        const withQueueTimeout = async <T,>(
          operation: Promise<T>,
          timeoutMs: number,
          message: string,
        ): Promise<T> => (
          new Promise<T>((resolve, reject) => {
            let settled = false;
            let timeoutId: ReturnType<typeof setTimeout> | null = null;
            let handleAbort: () => void = () => {};
            const cleanup = () => {
              if (timeoutId) {
                clearTimeout(timeoutId);
                timeoutId = null;
              }
              if (abortSignal) {
                abortSignal.removeEventListener('abort', handleAbort);
              }
            };
            const finish = (callback: () => void) => {
              if (settled) return;
              settled = true;
              cleanup();
              callback();
            };
            handleAbort = () => finish(() => reject(createAbortError()));
            timeoutId = setTimeout(() => finish(() => reject(new Error(message))), timeoutMs);
            if (abortSignal) {
              abortSignal.addEventListener('abort', handleAbort, { once: true });
            }
            operation.then(
              (value) => finish(() => resolve(value)),
              (error) => finish(() => reject(error)),
            );
          })
        );

        const frameRectWidth = toFinite(outputWorkspace.frameRect?.w);
        const frameRectHeight = toFinite(outputWorkspace.frameRect?.h);
        const fallbackWidth = toFinite(outputWorkspace.generationRect?.w) ?? 1280;
        const fallbackHeight = toFinite(outputWorkspace.generationRect?.h) ?? 720;
        let targetWidthForTask = normalizeVideoDimension(frameRectWidth && frameRectWidth > 1 ? frameRectWidth : fallbackWidth);
        let targetHeightForTask = normalizeVideoDimension(frameRectHeight && frameRectHeight > 1 ? frameRectHeight : fallbackHeight);
        let upscaleFactorForTask = 1;

        let startImageUrl: string | undefined;
        let startImagePrivateUrl: string | undefined;
        let lastImageUrl: string | undefined = undefined;
        let lastImagePrivateUrl: string | undefined = undefined;
        let assembledStartImageFile: Blob | undefined;

        // List (and per-output actions) can feed a full-resolution source asset URL
        // straight to the video model instead of a canvas re-render. This preserves the
        // source at full quality (no 1440×1440 / lossy-WebP downscale, no alpha flatten)
        // and removes the visible-layer requirement that makes List video brittle.
        if (!shouldUseTimelineReferenceClip && shouldPrepareImageInputFrame) {
          if (directSourceImageUrl) {
            // Fetch the ORIGINAL bytes and re-host them byte-for-byte to a
            // provider-fetchable URL, no canvas re-render, no 1440×1440/WebP downscale,
            // no recompression. Mirrors the image-edit main-input path (~L1712). The
            // frame reaches the model at full source quality.
            try {
              const sourceResponse = await fetch(directSourceImageUrl);
              if (!sourceResponse.ok) throw new Error(`HTTP ${sourceResponse.status}`);
              const sourceBlob = await sourceResponse.blob();
              throwIfAborted(abortSignal);
              const sourceBitmap = await createImageBitmap(sourceBlob);
              targetWidthForTask = normalizeVideoDimension(sourceBitmap.width);
              targetHeightForTask = normalizeVideoDimension(sourceBitmap.height);
              if (typeof sourceBitmap.close === 'function') sourceBitmap.close();
              const sourceExtension = inferExtensionFromBlob(sourceBlob, 'png');
              const sourceFile = new File([sourceBlob], `film-start-source.${sourceExtension}`, {
                type: sourceBlob.type || 'image/png',
              });
              assembledStartImageFile = sourceFile;
              const uploadedSource = await dualUploadAsset(sourceFile, {
                ttlSeconds: 86400,
                filenamePrefix: 'film-start',
                fallbackContentType: sourceBlob.type || 'image/png',
                fallbackExtension: sourceExtension,
                projectIdOverride: projectId,
              }, finalWorkspaceId, abortSignal);
              startImageUrl = uploadedSource.publicUrl;
              startImagePrivateUrl = uploadedSource.privateUrl;
            } catch (sourceError) {
              if (isAbortError(sourceError)) throw sourceError;
              console.error('Failed to load the direct source image for video generation.', sourceError);
              kernel.current?.handleError('Could not load the source image for video generation.');
              kernel.current?.setIsSubmittingAITask(false);
              return { tasks: [], newWorkspaceId: null };
            }
          } else {
          const layerIdsForStartImage = getRenderableLayerIdsAtMs(timelineClipStartMs);
          if (layerIdsForStartImage.length === 0 && !videoModelStartsFromText(diffusionModel)) {
            kernel.current?.handleError('No visible layer is active at the selected video input frame.');
            kernel.current?.setIsSubmittingAITask(false);
            return { tasks: [], newWorkspaceId: null };
          }
          if (layerIdsForStartImage.length > 0) {
            let assembledImageResult: Awaited<ReturnType<NonNullable<KernelHandle['renderWorkspaceArea']>>>;

            try {
              assembledImageResult = await withQueueTimeout(
                kernel.current.renderWorkspaceArea(
                  finalWorkspaceId,
                  layerIdsForStartImage,
                  1440 * 1440,
                  false,
                  false,
                  2
                ),
                12000,
                'Timed out rendering the video model input frame.',
              );
            } catch (renderError) {
              if (!isCanvasTaintError(renderError) && !isTimeoutError(renderError)) {
                throw renderError;
              }
              const fallbackVideoLayer = activeTrackAtClipStart?.layer ?? visibleVideoLayers[visibleVideoLayers.length - 1];
              if (!fallbackVideoLayer) {
                throw renderError;
              }
              console.warn('Video start-image render used cross-origin video data; falling back to cached video preview frame.', renderError);
              assembledImageResult = await createResultFromVideoLayer(fallbackVideoLayer);
            }

            if (!assembledImageResult || !assembledImageResult.file) {
              throw new Error("Could not assemble start image from the source workspace's visible layers.");
            }
            throwIfAborted(abortSignal);

            targetWidthForTask = Math.max(2, Math.round(assembledImageResult.targetWidth || targetWidthForTask));
            targetHeightForTask = Math.max(2, Math.round(assembledImageResult.targetHeight || targetHeightForTask));
            upscaleFactorForTask = Number.isFinite(assembledImageResult.upscaleFactor) ? assembledImageResult.upscaleFactor : 1;

            const startImageFile = assembledImageResult.file;
            assembledStartImageFile = startImageFile;
            const uploadedStartImage = await dualUploadAsset(startImageFile, {
              ttlSeconds: 86400,
              filenamePrefix: 'film-start',
              fallbackContentType: 'image/webp',
              fallbackExtension: 'webp',
              projectIdOverride: projectId,
            }, finalWorkspaceId, abortSignal);
            startImageUrl = uploadedStartImage.publicUrl;
            startImagePrivateUrl = uploadedStartImage.privateUrl;
          }
          }

          const videoLastImageFile = runtimeStateOverride?.videoEndImage;
          const videoLastImagesLockable = videoLastImageFile ? [videoLastImageFile] : getVideoLastImages(projectId);
          const videoLastImages = videoLastImagesLockable.map((lf) => lf.file);
          if (videoLastImages.length > 0) {
            const lastImage = videoLastImages[0];
            if (lastImage) {
              const startImageAspectRatio = targetWidthForTask / targetHeightForTask;
              let imageToUpload = lastImage;
              try {
                imageToUpload = await cropImageFileToAspectRatio(lastImage, startImageAspectRatio);
                if (imageToUpload !== lastImage) {
                  console.log('Last frame aspect ratio corrected for video generation.');
                }
              } catch (cropError) {
                console.error('Failed to process last frame for aspect ratio correction, proceeding with original.', cropError);
              }

              const uploadedLastImage = await dualUploadAsset(imageToUpload, {
                ttlSeconds: 86400,
                filenamePrefix: 'film-last',
                fallbackContentType: imageToUpload.type || 'image/webp',
                fallbackExtension: inferExtensionFromBlob(imageToUpload, 'webp'),
                projectIdOverride: projectId,
              }, finalWorkspaceId, abortSignal);
              lastImageUrl = uploadedLastImage.publicUrl;
              lastImagePrivateUrl = uploadedLastImage.privateUrl;
            }
          }
        }
        throwIfAborted(abortSignal);

        let mainVideoPublicUrl: string | undefined;
        let referenceImagePublicUrls: string[] | undefined;
        let referenceImagePrivateUrls: string[] | undefined;
        let seedanceWorkspaceReferenceImageIndex: number | null = null;
        let timelineInsertStartMs: number | undefined;
        let timelineInsertEndMs: number | undefined;
        let remainingTimelineSeconds: number | undefined;
        const selectedInputTrackForVideoModel = shouldUseVideoReference
          ? topVisibleVideoTrackAtCursor
          : selectedVideoTrack;
        const prepareVideoReferenceInput = async ({
          label,
          minDurationMs,
          maxDurationMs,
          filenamePrefix,
        }: {
          label: string;
          minDurationMs?: number;
          maxDurationMs?: number;
          filenamePrefix: string;
        }) => {
          const outputFps = Math.max(8, Math.min(30, Math.round(toFinite(outputWorkspace.timelineFps) ?? 24)));
          const referenceClipMaxPixels = resolveReferenceClipMaxPixels(diffusionModel);
          // Native resolution of a video layer, from its loaded <video> element.
          const getLayerNativeVideoDims = (layer: Layer | null | undefined): { w: number; h: number } => {
            const element = layer ? videoRefs?.[layer.id] : null;
            const w = element && Number.isFinite(element.videoWidth) ? element.videoWidth : 0;
            const h = element && Number.isFinite(element.videoHeight) ? element.videoHeight : 0;
            return { w, h };
          };
          if (explicitVideoInputRange) {
            const durationMs = explicitVideoInputRange.endMs - explicitVideoInputRange.startMs;
            const durationError = validateVideoInputDuration(label, durationMs, minDurationMs, maxDurationMs);
            if (durationError) throw new Error(durationError);
            // Bound the composition by the highest-resolution video source overlapping
            // the range, so a 4K clip is not pre-downsampled to the frameRect.
            const rangeNative = resolvedRenderableTimelineTracks
              .filter((track) => (
                track.layerType === 'video' &&
                track.startMs < explicitVideoInputRange.endMs &&
                track.endMs > explicitVideoInputRange.startMs
              ))
              .map((track) => getLayerNativeVideoDims(track.layer))
              .reduce((best, dims) => (Math.max(dims.w, dims.h) > Math.max(best.w, best.h) ? dims : best), { w: 0, h: 0 });
            const referenceDims = resolveReferenceClipDimensions({
              frameRectW: frameRectWidth,
              frameRectH: frameRectHeight,
              nativeW: rangeNative.w > 0 ? rangeNative.w : null,
              nativeH: rangeNative.h > 0 ? rangeNative.h : null,
              maxPixels: referenceClipMaxPixels,
              fallbackW: targetWidthForTask,
              fallbackH: targetHeightForTask,
            });
            const timelineReference = await prepareComposedTimelineReferenceClip({
              workspaceId: finalWorkspaceId,
              tracks: resolvedRenderableTimelineTracks,
              rangeStartMs: explicitVideoInputRange.startMs,
              rangeEndMs: explicitVideoInputRange.endMs,
              outputWidth: referenceDims.width,
              outputHeight: referenceDims.height,
              outputFps,
              filenamePrefix,
              abortSignal,
            });
            return {
              publicUrl: timelineReference.publicUrl,
              timelineInsertStartMs: Math.round(explicitVideoInputRange.startMs),
              timelineInsertEndMs: Math.round(explicitVideoInputRange.endMs),
              remainingTimelineSeconds: timelineReference.remainingTimelineSeconds,
            };
          }

          if (!selectedInputTrackForVideoModel) {
            throw new Error(`${label} needs a visible video track at the current frame.`);
          }
          const inputStartMs = Math.max(0, Math.round(selectedInputTrackForVideoModel.startMs));
          const inputEndMsRaw = Math.max(inputStartMs + 1, Math.round(selectedInputTrackForVideoModel.endMs));
          const maxInputEndMs = selectedInputTrackForVideoModel.mediaDurationMs !== null && Number.isFinite(selectedInputTrackForVideoModel.mediaDurationMs)
            ? Math.max(
                inputStartMs + 1,
                Math.round(
                  inputStartMs +
                  Math.max(1, (selectedInputTrackForVideoModel.mediaDurationMs ?? 0) - selectedInputTrackForVideoModel.sourceInMs),
                ),
              )
            : inputEndMsRaw;
          const inputEndMs = Math.min(inputEndMsRaw, maxInputEndMs);
          const durationMs = Math.max(0, inputEndMs - inputStartMs);
          const durationError = validateVideoInputDuration(label, durationMs, minDurationMs, maxDurationMs);
          if (durationError) throw new Error(durationError);
          const cursorNative = getLayerNativeVideoDims(selectedInputTrackForVideoModel.layer);
          const referenceDims = resolveReferenceClipDimensions({
            frameRectW: frameRectWidth,
            frameRectH: frameRectHeight,
            nativeW: cursorNative.w > 0 ? cursorNative.w : null,
            nativeH: cursorNative.h > 0 ? cursorNative.h : null,
            maxPixels: referenceClipMaxPixels,
            fallbackW: targetWidthForTask,
            fallbackH: targetHeightForTask,
          });
          const timelineReference = await prepareTimelineReferenceClipFromCursor({
            workspaceId: finalWorkspaceId,
            tracks: [selectedInputTrackForVideoModel],
            playbackStartMs: inputStartMs,
            playbackEndMs: inputEndMs,
            cursorMs: inputStartMs,
            outputWidth: referenceDims.width,
            outputHeight: referenceDims.height,
            outputFps,
            filenamePrefix,
            abortSignal,
          });
          return {
            publicUrl: timelineReference.publicUrl,
            timelineInsertStartMs: Math.round(inputStartMs),
            timelineInsertEndMs: Math.round(inputEndMs),
            remainingTimelineSeconds: timelineReference.remainingTimelineSeconds,
          };
        };

        if (isKling3Omni) {
          const klingOmniParams = params as Kling3OmniParams;
          if (klingOmniParams.generate_audio && klingOmniParams.keep_original_sound) {
            kernel.current?.handleError('Generate Audio and Keep Original Sound are mutually exclusive. Disable one of them.');
            kernel.current?.setIsSubmittingAITask(false);
            return { tasks: [], newWorkspaceId: null };
          }

          if (shouldUseKlingOmniVideoReference) {
            if (klingOmniParams.generate_audio) {
              kernel.current?.handleError('Generate Audio is mutually exclusive with a reference video. Disable audio or hide the video layer.');
              kernel.current?.setIsSubmittingAITask(false);
              return { tasks: [], newWorkspaceId: null };
            }
            try {
              const timelineReference = await prepareVideoReferenceInput({
                label: 'Kling 3 Omni',
                minDurationMs: KLING_OMNI_REFERENCE_VIDEO_MIN_DURATION_MS,
                maxDurationMs: KLING_OMNI_REFERENCE_VIDEO_MAX_DURATION_MS,
                filenamePrefix: 'film-reference-video',
              });
              mainVideoPublicUrl = timelineReference.publicUrl;
              timelineInsertStartMs = timelineReference.timelineInsertStartMs;
              timelineInsertEndMs = timelineReference.timelineInsertEndMs;
              remainingTimelineSeconds = timelineReference.remainingTimelineSeconds;
            } catch (timelineReferenceError) {
              console.error('Failed to compose a timeline reference clip for Kling 3 Omni.', timelineReferenceError);
              const message = timelineReferenceError instanceof Error ? timelineReferenceError.message : 'Unknown timeline track reference preparation error.';
              kernel.current?.handleError(`Failed to prepare a Kling 3 Omni video input. ${message}`);
              kernel.current?.setIsSubmittingAITask(false);
              return { tasks: [], newWorkspaceId: null };
            }
          }
        }

        if (isSeedance2) {
          const seedanceReferenceLockables = runtimeStateOverride?.referenceImages || getExtraFiles(projectId);
          const externalReferenceFiles = seedanceReferenceLockables
            .map((lockableFile) => lockableFile.file)
            .filter((file): file is File => Boolean(file));
          const maxReferenceImages = getStudioModelReferenceLimits(diffusionModel).max;
          const effectiveMaxReferenceImages = assembledStartImageFile ? Math.max(1, maxReferenceImages) : maxReferenceImages;
          const externalReferenceLimit = assembledStartImageFile
            ? Math.max(0, effectiveMaxReferenceImages - 1)
            : effectiveMaxReferenceImages;
          if (isListProjectGeneration && externalReferenceFiles.length > externalReferenceLimit) {
            throw new Error('The selected model cannot receive all mentioned images together with the source. Choose fewer references or another model.');
          }
          const limitedExternalReferenceFiles = externalReferenceFiles.slice(0, externalReferenceLimit);
          const seedanceReferenceFiles: Blob[] = [
            ...limitedExternalReferenceFiles,
            ...(assembledStartImageFile ? [assembledStartImageFile] : []),
          ];
          const limitedReferenceFiles = seedanceReferenceFiles.slice(0, effectiveMaxReferenceImages);
          seedanceWorkspaceReferenceImageIndex = assembledStartImageFile
            ? limitedExternalReferenceFiles.length + 1
            : null;

          if (limitedReferenceFiles.length > 0) {
            const uploadResult = await dualUploadFiles(limitedReferenceFiles, 'film-seedance-ref', 86400, finalWorkspaceId, abortSignal, projectId);
            referenceImagePublicUrls = uploadResult.publicUrls;
            referenceImagePrivateUrls = uploadResult.privateUrls.length > 0 ? uploadResult.privateUrls : undefined;
            if (
              seedanceWorkspaceReferenceImageIndex !== null &&
              referenceImagePublicUrls.length < seedanceWorkspaceReferenceImageIndex
            ) {
              seedanceWorkspaceReferenceImageIndex = null;
            }
          }

          if (shouldUseSeedanceVideoReference) {
            try {
              const timelineReference = await prepareVideoReferenceInput({
                label: 'Seedance 2.0',
                minDurationMs: SEEDANCE_REFERENCE_VIDEO_MIN_DURATION_MS,
                maxDurationMs: SEEDANCE_REFERENCE_VIDEO_MAX_DURATION_MS,
                filenamePrefix: 'film-seedance-reference-video',
              });
              mainVideoPublicUrl = timelineReference.publicUrl;
              timelineInsertStartMs = timelineReference.timelineInsertStartMs;
              timelineInsertEndMs = timelineReference.timelineInsertEndMs;
              remainingTimelineSeconds = timelineReference.remainingTimelineSeconds;
            } catch (uploadError) {
              console.error('Failed to prepare reference video for Seedance 2.0.', uploadError);
              const message = uploadError instanceof Error ? uploadError.message : 'Unknown reference video upload error.';
              kernel.current?.handleError(`Failed to prepare a Seedance 2.0 video input. ${message}`);
              kernel.current?.setIsSubmittingAITask(false);
              return { tasks: [], newWorkspaceId: null };
            }
          }
        }

        if (isGeminiOmniFlash && shouldUseGeminiOmniVideoReference) {
          try {
            const timelineReference = await prepareVideoReferenceInput({
              label: 'Gemini Omni Flash',
              minDurationMs: 3000,
              maxDurationMs: 10000,
              filenamePrefix: 'film-gemini-omni-reference-video',
            });
            mainVideoPublicUrl = timelineReference.publicUrl;
            timelineInsertStartMs = timelineReference.timelineInsertStartMs;
            timelineInsertEndMs = timelineReference.timelineInsertEndMs;
            remainingTimelineSeconds = timelineReference.remainingTimelineSeconds;
          } catch (uploadError) {
            console.error('Failed to prepare reference video for Gemini Omni Flash.', uploadError);
            const message = uploadError instanceof Error ? uploadError.message : 'Unknown reference video upload error.';
            kernel.current?.handleError(`Failed to prepare a Gemini Omni Flash video input. ${message}`);
            kernel.current?.setIsSubmittingAITask(false);
            return { tasks: [], newWorkspaceId: null };
          }
        }

        if (isGeminiOmniFlash) {
          const geminiReferenceLockables = runtimeStateOverride?.referenceImages || getExtraFiles(projectId);
          const maxReferenceImages = getStudioModelReferenceLimits(diffusionModel).max;
          const geminiReferenceFiles = geminiReferenceLockables
            .map((lockableFile) => lockableFile.file)
            .filter((file): file is File => Boolean(file) && file.type.startsWith('image/'))
            .slice(0, maxReferenceImages);
          if (geminiReferenceFiles.length > 0) {
            const uploadResult = await dualUploadFiles(geminiReferenceFiles, 'film-gemini-omni-ref', 86400, finalWorkspaceId, abortSignal, projectId);
            referenceImagePublicUrls = uploadResult.publicUrls;
            referenceImagePrivateUrls = uploadResult.privateUrls.length > 0 ? uploadResult.privateUrls : undefined;
          }
        }

        let mainAudioPublicUrl: string | undefined;
        let mainAudioPrivateUrl: string | undefined;
        let mainAudioMimeType: string | undefined;
        if (isGeminiOmniFlash && (params as GeminiOmniFlashParams).use_audio_input === true) {
          const audioReferenceFile = getAudioReferenceFiles(projectId)
            .map((lockableFile) => lockableFile.file)
            .find((file): file is File => Boolean(file) && file.type.startsWith('audio/'));
          if (!audioReferenceFile) {
            kernel.current?.handleError('Gemini Omni Flash audio input is enabled, but no audio file is attached.');
            kernel.current?.setIsSubmittingAITask(false);
            return { tasks: [], newWorkspaceId: null };
          }
          try {
            const uploadedAudioReference = await dualUploadAsset(audioReferenceFile, {
              ttlSeconds: 86400,
              filenamePrefix: 'film-gemini-omni-reference-audio',
              fallbackContentType: audioReferenceFile.type || 'audio/mpeg',
              fallbackExtension: inferExtensionFromBlob(audioReferenceFile, 'mp3'),
              projectIdOverride: projectId,
            }, finalWorkspaceId, abortSignal);
            mainAudioPublicUrl = uploadedAudioReference.publicUrl;
            mainAudioPrivateUrl = uploadedAudioReference.privateUrl;
            mainAudioMimeType = audioReferenceFile.type || 'audio/mpeg';
          } catch (uploadError) {
            console.error('Failed to prepare audio reference for Gemini Omni Flash.', uploadError);
            const message = uploadError instanceof Error ? uploadError.message : 'Unknown audio reference upload error.';
            kernel.current?.handleError(`Failed to prepare a Gemini Omni Flash audio input. ${message}`);
            kernel.current?.setIsSubmittingAITask(false);
            return { tasks: [], newWorkspaceId: null };
          }
        }

        throwIfAborted(abortSignal);

        // The new layer will be placed relative to the final workspace's generationRect.
        const finalWorkspace = kernel.current.getWorkspace(finalWorkspaceId);
        const generationRect = finalWorkspace ? finalWorkspace.generationRect : outputWorkspace.generationRect;
        const taskProjectId = projectId;
        const taskChatSessionId = resolveTaskChatSessionId(taskProjectId);

        const finalParams = { ...params }; // Start with a copy
        // List demands true-4K finals: force the native-4K tier for the render-tier
        // models that support it (veo → 4k, ltx → 2160p). Cheap motion-validation
        // passes (hailuo/wan) keep their low res. §4.6: enforce quality app-side
        // rather than trusting the agent to request the 4K tier every time.
        const isListVideoProject = generationOutputScope?.kind === 'PROJECT';
        if (isListVideoProject) {
          // Only raise a resolution the caller LEFT AT THE MODEL DEFAULT. Overriding an
          // explicit choice silently multiplied the bill (veo 720p 4 cr/s -> 4k 12 cr/s, ltx
          // 1080p 2 cr/s -> 2160p 6 cr/s) while estimateStudioModelCredits still quoted the
          // pre-override price, so every displayed estimate for List video was ~3x low and the
          // user had no way to opt out of the more expensive tier.
          const currentResolution = (finalParams as unknown as { resolution?: string }).resolution;
          const defaultsGetter = getDefaultParamsForModel(diffusionModel);
          const defaultResolution = defaultsGetter
            ? (defaultsGetter() as unknown as { resolution?: string }).resolution
            : undefined;
          const leftAtDefault = currentResolution === undefined || currentResolution === defaultResolution;
          if (leftAtDefault) {
            if (diffusionModel === 'veo-3.1-fast') {
              (finalParams as unknown as { resolution?: string }).resolution = '4k';
            } else if (diffusionModel === 'ltx-2.3-fast') {
              (finalParams as unknown as { resolution?: string }).resolution = '2160p';
            }
          }
        }
        if (isSeedance2) {
          (finalParams as Seedance2Params).prompt = compileSeedanceWorkspaceReferencePrompt(
            (finalParams as Seedance2Params).prompt,
            seedanceWorkspaceReferenceImageIndex,
          );
        }
        if (isGeminiOmniFlash) {
          const geminiParams = finalParams as GeminiOmniFlashParams;
          geminiParams.duration = Math.max(3, Math.min(10, Math.round(Number(geminiParams.duration) || 5)));
          geminiParams.aspect_ratio = geminiParams.aspect_ratio === '16:9' || geminiParams.aspect_ratio === '9:16'
            ? geminiParams.aspect_ratio
            : 'auto';
        }

        const baseLayerForVideoPlacement = selectedInputTrackForVideoModel?.layer ?? highestVisibleVideoLayer;
        const layerContextSnapshot = isListProjectGeneration ? [] : getContextSnapshot(finalWorkspaceId);
        const tasksToCreate: GenerationTask[] = [];

        for (let i = 0; i < numGenerations; i++) {
          throwIfAborted(abortSignal);
          const randomSeed = Math.floor(Math.random() * 2100000000);
          const taskId = crypto.randomUUID();

          const newTask: GenerationTask = {
            id: taskId,
            chatSessionId: taskChatSessionId,
            isAgentSpawned: resolveTaskIsAgentSpawned(taskChatSessionId),
            type: 'film',
            workspaceId : finalWorkspaceId,
            outputScope: generationOutputScope ?? {
              kind: 'WORKSPACE',
              workspaceId: finalWorkspaceId,
            },
            // A Seedance run left on random sends no seed: a seed keeps Seedance 2.0 off Sume (app/api/film/route.ts hasSeed).
            params: { ...finalParams, seed: ((numGenerations === 1) && typeof (params as { seed?: number }).seed === 'number' && (params as { seed: number }).seed !== -1) ? (params as { seed: number }).seed : diffusionModel.startsWith('seedance-') ? -1 : randomSeed },
            model: diffusionModel,
            mainImagePublicUrl: startImageUrl,
            mainImagePrivateUrl: startImagePrivateUrl,
            mainVideoPublicUrl,
            mainAudioPublicUrl,
            mainAudioPrivateUrl,
            mainAudioMimeType,
            extraImageUrl: lastImageUrl,
            extraImageUrlPrivate: lastImagePrivateUrl,
            urls: referenceImagePublicUrls ? [...referenceImagePublicUrls] : undefined,
            referenceImagePrivateUrls: referenceImagePrivateUrls ? [...referenceImagePrivateUrls] : undefined,
            mergeStrategy: 'addInList',
            targetWidth: targetWidthForTask,
            targetHeight: targetHeightForTask,
            upscaleFactor: upscaleFactorForTask,
            blurRadius: 0,
            rect: { ...generationRect },
            projectId: taskProjectId,
            videoDurationSeconds: remainingTimelineSeconds,
            layerOverride: {
              name: '',
              ...(baseLayerForVideoPlacement
                ? {
                    rect: { ...baseLayerForVideoPlacement.rect },
                    ...(typeof baseLayerForVideoPlacement.scale === 'number' && Number.isFinite(baseLayerForVideoPlacement.scale)
                      ? { scale: baseLayerForVideoPlacement.scale }
                      : {}),
                  }
                : {}),
              ...(typeof timelineInsertStartMs === 'number' && Number.isFinite(timelineInsertStartMs)
                ? { timelineStartMs: timelineInsertStartMs }
                : {}),
              ...(typeof timelineInsertEndMs === 'number' && Number.isFinite(timelineInsertEndMs)
                ? { timelineEndMs: timelineInsertEndMs }
                : {}),
            },
            layerContextSnapshot,
            parentVersionId: parentVersionId,
          };

          tasksToCreate.push(newTask);
        }

        createdTasks = tasksToCreate;
        throwIfAborted(abortSignal);
        const processing = kernel.current?.processTasks(createdTasks, { abortSignal });
        // A List video runs for minutes and reports on its own tile; holding the turn for it
        // froze the conversation. processTasks settles each task's failure itself.
        if (isListProjectGeneration) {
          void processing?.catch(error => console.error('List video processing failed after launch.', error));
          return { tasks: createdTasks, newWorkspaceId: null };
        }
        await processing;
        return { tasks: createdTasks, newWorkspaceId: null };

    } catch (e: any) {
        if (isAbortError(e)) {
          kernel.current?.setIsSubmittingAITask(false);
          throw e;
        }
        kernel.current?.handleError(`Video queuing failed: ${e.message || 'Unknown error'}`);
        console.error('Handle Queue Film failed with error :', e);
        kernel.current?.setIsSubmittingAITask(false);
        return { tasks: [], newWorkspaceId: null };
    }
  }, [ kernel, getContextSnapshot, dualUploadAsset, dualUploadFiles, inferExtensionFromBlob, getProjectIdFromWorkspaceId, getAudioReferenceFiles, getExtraFiles, getLocalNumOutputs, getVideoLastImages, getWorkspaceLayers, prepareComposedTimelineReferenceClip, prepareTimelineReferenceClipFromCursor, resolvePreferredTimelineTrackSelection, resolveTaskChatSessionId, resolveTaskIsAgentSpawned ]);

  const handleQueueVideoEdit = useCallback(async (
    workspaceId: string,
    runtimeStateOverride: RuntimeState | undefined,
    parentVersionId: string | null,
    numOutputsOverride?: number,
    abortSignal?: AbortSignal,
    generationOptions?: ModelRunGenerationOptions
  ): Promise<{ tasks: GenerationTask[], newWorkspaceId: string | null }> => {
    if (!kernel.current) return { tasks: [], newWorkspaceId: null };
    throwIfAborted(abortSignal);

    const sourceWorkspace = workspaceId ? kernel.current.getWorkspace(workspaceId) : null;
    const projectId = generationOptions?.projectId?.trim()
      || (workspaceId ? getProjectIdFromWorkspaceId(workspaceId, 'handleQueueVideoEdit') : '');
    const generationOutputScope = generationOptions?.outputScope
      ?? resolveGenerationOutputScopeForView(projectId, workspaceId, getProjectStudioViewMode(kernel.current, projectId));
    const isListProjectGeneration = generationOutputScope?.kind === 'PROJECT';
    const outputWorkspace = isListProjectGeneration
      ? createOutputOnlyWorkspace(projectId, sourceWorkspace ?? undefined)
      : sourceWorkspace ?? (projectId ? createOutputOnlyWorkspace(projectId) : null);

    if (!outputWorkspace) {
        kernel.current?.handleError("Could not resolve a project for output-only video editing.");
        return { tasks: [], newWorkspaceId: null };
    }

    const diffusionModel = runtimeStateOverride?.diffusionModel || kernel.current.getDiffusionModel();
    const supportedVideoEditModels = ['topaz-video-upscale', 'mirelo-sfx-1.6', 'magnific-video-upscale'];
    if (!supportedVideoEditModels.includes(diffusionModel)) {
      kernel.current?.handleError(`Unsupported video editing model: ${diffusionModel}`);
      return { tasks: [], newWorkspaceId: null };
    }

    const modelParams = runtimeStateOverride?.parameters || kernel.current.getModelParams();
    const params = modelParams[diffusionModel] as TopazVideoUpscaleParams | MireloSfxParams | MagnificVideoUpscaleParams | undefined;
    if (!params) {
      kernel.current?.handleError(`Missing params for model ${diffusionModel}.`);
      return { tasks: [], newWorkspaceId: null };
    }
    // An explicit source clip skips the whole timeline resolution below. That
    // resolution reads mounted <video> elements and composes a clip off the
    // canvas, neither of which exists in a List project, which is why video
    // upscale was unreachable there. Here the clip IS the input: fetch it,
    // re-publish it, send it.
    const directSourceVideoUrl = generationOptions?.mainVideoUrlOverride?.trim() || null;
    if (directSourceVideoUrl) {
      kernel.current.setIsSubmittingAITask(true);
      try {
        throwIfAborted(abortSignal);
        // Header only. The clip itself never enters the browser: the film route signs the stored
        // blob for the provider (publishStoredAssetForProvider), so there is nothing to re-upload.
        const clip = await probeVideoMetadataByUrl(directSourceVideoUrl);
        throwIfAborted(abortSignal);
        // Metadata is best-effort by design: some mp4s fire neither loadedmetadata
        // nor error on a detached element and come back zeroed. The bytes are what
        // the upscaler needs, and these two only size the resulting layer, so a
        // silent probe must never cost the user a run.
        const clipWidth = clip.width || Math.round(outputWorkspace.generationRect.w);
        const clipHeight = clip.height || Math.round(outputWorkspace.generationRect.h);
        const clipDurationSeconds = clip.duration > 0 ? clip.duration : undefined;
        if (
          diffusionModel === 'mirelo-sfx-1.6' &&
          (typeof clipDurationSeconds !== 'number' ||
            clipDurationSeconds < MIRELO_SFX_MIN_DURATION_SECONDS ||
            clipDurationSeconds > MIRELO_SFX_MAX_DURATION_SECONDS)
        ) {
          kernel.current.handleError(
            `Mirelo SFX source duration must be between ${MIRELO_SFX_MIN_DURATION_SECONDS} and ${MIRELO_SFX_MAX_DURATION_SECONDS} seconds.`,
          );
          kernel.current.setIsSubmittingAITask(false);
          return { tasks: [], newWorkspaceId: null };
        }
        // The stored path goes to the task as-is. fal and Magnific need an absolute public URL and
        // ours are auth-gated, but the film route signs the blob it already holds
        // (publishStoredAssetForProvider), which moves zero bytes. Re-uploading from the browser
        // to obtain the same URL is what made a real 4K clip time out before it ever left.
        const clipPublicUrl = directSourceVideoUrl;

        const directTasks = buildVideoEditTasks(
          {
            workspaceId,
            projectId,
            chatSessionId: resolveTaskChatSessionId(projectId),
            isAgentSpawned: resolveTaskIsAgentSpawned(resolveTaskChatSessionId(projectId)),
            model: diffusionModel,
            params,
            numGenerations: enforceStudioModelMinimumGenerations(
              diffusionModel,
              numOutputsOverride ?? getLocalNumOutputs(projectId),
            ),
            layerContextSnapshot: isListProjectGeneration ? [] : getContextSnapshot(workspaceId),
            parentVersionId,
            outputScope: generationOutputScope ?? {
              kind: 'WORKSPACE',
              workspaceId,
            },
          },
          {
            mainVideoPublicUrl: clipPublicUrl,
            videoDurationSeconds: clipDurationSeconds,
            targetWidth: clipWidth,
            targetHeight: clipHeight,
            rect: { ...outputWorkspace.generationRect },
            sourceListOutputId: generationOptions?.sourceListOutputId,
          },
        );

        throwIfAborted(abortSignal);
        await kernel.current?.processTasks(directTasks, { abortSignal });
        return { tasks: directTasks, newWorkspaceId: null };
      } catch (error: any) {
        // Only the CALLER's abort is a user stop. Anything else wearing an AbortError is a
        // failure, and rethrowing it silently would leave the user with nothing but the
        // caller's generic "failed to start". Nothing here downloads the clip any more, so the
        // old "took too long to download" wording no longer describes any real path.
        if (isAbortError(error) && abortSignal?.aborted) {
          kernel.current?.setIsSubmittingAITask(false);
          throw error;
        }
        const message = isAbortError(error)
          ? 'Preparing the clip was interrupted.'
          : (error?.message || 'Unknown error');
        kernel.current?.handleError(`Video editing failed: ${message}`);
        console.error('Handle Queue Video Edit failed on the direct source clip path :', error);
        kernel.current?.setIsSubmittingAITask(false);
        return { tasks: [], newWorkspaceId: null };
      }
    }

    if (isListProjectGeneration) {
      kernel.current.handleError('This video operation requires an explicit source clip in List. Select a supported action on the video output.');
      return { tasks: [], newWorkspaceId: null };
    }
    const allLayers = workspaceId ? getWorkspaceLayers(workspaceId) : [];
    const visibleLayerCount = allLayers.filter((layer) => layer.visible).length;
    if (visibleLayerCount === 0) {
        kernel.current?.handleError("Video editing requires at least one visible layer in the workspace.");
        return { tasks: [], newWorkspaceId: null };
    }

    kernel.current?.setIsSubmittingAITask(true);
    throwIfAborted(abortSignal);
    const finalWorkspaceId = workspaceId;
    const visibleWorkspaceLayers = allLayers.filter((layer) => layer.visible);
    const visibleVideoLayers = visibleWorkspaceLayers
      .filter((layer): layer is Layer => (
        layer.type === 'video' &&
        typeof layer.videoSrc === 'string' &&
        layer.videoSrc.trim().length > 0 &&
        !readLayerParamFlag(layer.params, TIMELINE_AUDIO_ONLY_PARAM_KEY)
      ));

    if (visibleVideoLayers.length === 0) {
        kernel.current?.handleError('This video workflow requires a visible video layer with a source video.');
        kernel.current?.setIsSubmittingAITask(false);
        return { tasks: [], newWorkspaceId: null };
    }

    const sortedVisibleVideoLayers = sortLayersByRenderOrder(visibleVideoLayers);
    const sortedVisibleLayers = sortLayersByRenderOrder(visibleWorkspaceLayers);
    const workspaceCursorMs = Math.max(0, toFinite(outputWorkspace.timelineCursorMs) ?? 0);
    const videoRefs = kernel.current?.videoRefs.current;
    const workspaceTimelineDurationMs = Math.max(0, toFinite(outputWorkspace.timelineDurationMs) ?? 0);
    const resolvedTimelineTracks = sortedVisibleVideoLayers.map((layer, index) => {
      const startMs = Math.max(0, toFinite(layer.timelineStartMs) ?? 0);
      const sourceInMs = Math.max(0, toFinite(layer.timelineSourceInMs) ?? 0);
      const playbackRate = normalizeTimelineTrackPlaybackRate(
        readLayerParamNumber(layer.params, TIMELINE_PLAYBACK_RATE_PARAM_KEY),
      );
      const mediaDurationMs = (() => {
        const durationSeconds = videoRefs?.[layer.id]?.duration;
        return typeof durationSeconds === 'number' && Number.isFinite(durationSeconds) && durationSeconds > 0
          ? durationSeconds * 1000
          : null;
      })();
      const explicitEndMs = toFinite(layer.timelineEndMs);
      const fallbackEndMs = mediaDurationMs !== null
        ? startMs + Math.max(0, (mediaDurationMs - sourceInMs) / playbackRate)
        : startMs + 1000;
      const endMs = explicitEndMs !== null && explicitEndMs > startMs
        ? explicitEndMs
        : fallbackEndMs;
      const baseRenderOrder = typeof layer.renderOrder === 'number' && Number.isFinite(layer.renderOrder)
        ? layer.renderOrder
        : index;
      const renderOrder = (Reflect.get(layer, 'top-layer') === true || Reflect.get(layer, 'topLayer') === true)
        ? baseRenderOrder + TOP_LAYER_RENDER_ORDER_OFFSET
        : baseRenderOrder;
      return {
        layer,
        layerType: 'video' as const,
        startMs,
        endMs,
        sourceInMs,
        mediaDurationMs,
        playbackRate,
        renderOrder,
      };
    });
    const videoTimelineEndMs = resolvedTimelineTracks.reduce((max, track) => Math.max(max, track.endMs), 0);
    const resolvedTimelineCompositionTracks = sortedVisibleLayers
      .map((layer, index): TimelineReferenceTrack | null => {
        const hasVideoSource = typeof layer.videoSrc === 'string' && layer.videoSrc.trim().length > 0;
        const isVideoLayer = layer.type === 'video' && hasVideoSource && !readLayerParamFlag(layer.params, TIMELINE_AUDIO_ONLY_PARAM_KEY);
        const isAudioLayer = (layer.type === 'audio' || (layer.type === 'video' && readLayerParamFlag(layer.params, TIMELINE_AUDIO_ONLY_PARAM_KEY))) && hasVideoSource;
        const isImageLayer = !isVideoLayer && !isAudioLayer && typeof layer.imageSrc === 'string' && layer.imageSrc.trim().length > 0;
        if (!isVideoLayer && !isAudioLayer && !isImageLayer) return null;
        const startMs = Math.max(0, toFinite(layer.timelineStartMs) ?? 0);
        const sourceInMs = (isVideoLayer || isAudioLayer) ? Math.max(0, toFinite(layer.timelineSourceInMs) ?? 0) : 0;
        const playbackRate = (isVideoLayer || isAudioLayer)
          ? normalizeTimelineTrackPlaybackRate(readLayerParamNumber(layer.params, TIMELINE_PLAYBACK_RATE_PARAM_KEY))
          : 1;
        const mediaDurationMs = isVideoLayer
          ? (() => {
            const durationSeconds = videoRefs?.[layer.id]?.duration;
            return typeof durationSeconds === 'number' && Number.isFinite(durationSeconds) && durationSeconds > 0
              ? durationSeconds * 1000
              : null;
          })()
          : null;
        const explicitEndMs = toFinite(layer.timelineEndMs);
        const fallbackEndMs = isVideoLayer
          ? (mediaDurationMs !== null
            ? startMs + Math.max(0, (mediaDurationMs - sourceInMs) / playbackRate)
            : (workspaceTimelineDurationMs > startMs ? workspaceTimelineDurationMs : startMs + 1000))
          : isAudioLayer
          ? (workspaceTimelineDurationMs > startMs ? workspaceTimelineDurationMs : startMs + 1000)
          : (
            workspaceTimelineDurationMs > startMs
              ? workspaceTimelineDurationMs
              : (videoTimelineEndMs > startMs ? videoTimelineEndMs : startMs + 1000)
          );
        const endMs = explicitEndMs !== null && explicitEndMs > startMs
          ? explicitEndMs
          : fallbackEndMs;
        const baseRenderOrder = typeof layer.renderOrder === 'number' && Number.isFinite(layer.renderOrder)
          ? layer.renderOrder
          : index;
        const renderOrder = (Reflect.get(layer, 'top-layer') === true || Reflect.get(layer, 'topLayer') === true)
          ? baseRenderOrder + TOP_LAYER_RENDER_ORDER_OFFSET
          : baseRenderOrder;
        return {
          layer,
          layerType: isVideoLayer ? 'video' as const : (isAudioLayer ? 'audio' as const : 'image' as const),
          startMs,
          endMs,
          sourceInMs,
          renderOrder,
          mediaDurationMs,
          playbackRate,
          audioEnabled: isVideoLayer ? !readLayerParamFlag(layer.params, TIMELINE_MUTE_AUDIO_PARAM_KEY) : isAudioLayer,
          audioGain: isAudioLayer
            ? clamp(readLayerParamNumber(layer.params, TIMELINE_AUDIO_GAIN_PARAM_KEY) ?? 1, 0, AUDIO_GAIN_MAX)
            : 1,
          fadeInRatio: isAudioLayer ? 0 : normalizeTrackEffectRatio(readLayerParamNumber(layer.params, TIMELINE_FADE_IN_RATIO_PARAM_KEY)),
          fadeOutRatio: isAudioLayer ? 0 : normalizeTrackEffectRatio(readLayerParamNumber(layer.params, TIMELINE_FADE_OUT_RATIO_PARAM_KEY)),
        };
      })
      .filter((track): track is TimelineReferenceTrack => Boolean(track));

    const playbackStartMs = resolvedTimelineCompositionTracks.length > 0
      ? resolvedTimelineCompositionTracks.reduce((min, track) => Math.min(min, track.startMs), Number.POSITIVE_INFINITY)
      : 0;
    const playbackEndMs = resolvedTimelineCompositionTracks.reduce((max, track) => Math.max(max, track.endMs), 0);
    const clampedCursorMs = resolvedTimelineCompositionTracks.length > 0
      ? Math.max(playbackStartMs, Math.min(workspaceCursorMs, playbackEndMs))
      : workspaceCursorMs;
    const getTopVisibleVideoTrackAtMs = (timelineMs: number) => {
      const overlappingTracks = resolvedTimelineTracks.filter((track) => timelineMs >= track.startMs && timelineMs < track.endMs);
      return selectTopByRenderOrder(overlappingTracks);
    };
    const videoInputRangeRequest = readVideoInputRangeRequest(params);
    const mireloTrackSelection = diffusionModel === 'mirelo-sfx-1.6' && !videoInputRangeRequest.hasExplicitRange
      ? resolvePreferredTimelineTrackSelection(
          projectId,
          clampedCursorMs,
          resolvedTimelineCompositionTracks,
          resolvedTimelineTracks,
        )
      : null;
    if (mireloTrackSelection && mireloTrackSelection.selectedRenderableTracksCount > 1) {
      kernel.current?.handleError('Mirelo SFX accepts one selected video track at a time. Select one track, or set an explicit timeline range.');
      kernel.current?.setIsSubmittingAITask(false);
      return { tasks: [], newWorkspaceId: null };
    }
    if (
      mireloTrackSelection?.selectedRenderableTrack &&
      mireloTrackSelection.selectedRenderableTrack.layerType !== 'video'
    ) {
      kernel.current?.handleError('Mirelo SFX requires a video track. Select one video track before generating sound.');
      kernel.current?.setIsSubmittingAITask(false);
      return { tasks: [], newWorkspaceId: null };
    }
    const topVisibleVideoTrackAtCursor = mireloTrackSelection?.selectedVideoTrack
      ?? getTopVisibleVideoTrackAtMs(clampedCursorMs);
    const explicitVideoInputRangeResult = resolveExplicitVideoInputRange({
      request: videoInputRangeRequest,
      tracks: resolvedTimelineCompositionTracks,
      fallbackEndMs: playbackEndMs,
    });
    if (explicitVideoInputRangeResult && 'error' in explicitVideoInputRangeResult) {
      kernel.current?.handleError(explicitVideoInputRangeResult.error);
      kernel.current?.setIsSubmittingAITask(false);
      return { tasks: [], newWorkspaceId: null };
    }
    const explicitVideoInputRange = explicitVideoInputRangeResult && !('error' in explicitVideoInputRangeResult)
      ? explicitVideoInputRangeResult
      : null;
    if (explicitVideoInputRange) {
      const hasOverlappingVideoTrack = resolvedTimelineTracks.some((track) => (
        track.startMs < explicitVideoInputRange.endMs && track.endMs > explicitVideoInputRange.startMs
      ));
      if (!hasOverlappingVideoTrack) {
        kernel.current?.handleError('The requested video input range does not overlap a visible video track.');
        kernel.current?.setIsSubmittingAITask(false);
        return { tasks: [], newWorkspaceId: null };
      }
    }
    if (!explicitVideoInputRange && !topVisibleVideoTrackAtCursor) {
      kernel.current?.handleError('This video workflow needs a visible video track at the current frame, a selected video track, or an explicit start/end time range.');
      kernel.current?.setIsSubmittingAITask(false);
      return { tasks: [], newWorkspaceId: null };
    }

    const resolvedSourceTrack = explicitVideoInputRange ? null : topVisibleVideoTrackAtCursor;
    const sourceVideoLayer = resolvedSourceTrack?.layer
      ?? (explicitVideoInputRange
        ? (
            getTopVisibleVideoTrackAtMs(explicitVideoInputRange.startMs)?.layer
            ?? selectTopByRenderOrder(resolvedTimelineTracks.filter((track) => (
              track.startMs < explicitVideoInputRange.endMs && track.endMs > explicitVideoInputRange.startMs
            )))?.layer
          )
        : null)
      ?? sortedVisibleVideoLayers[sortedVisibleVideoLayers.length - 1]
      ?? null;
    if (!sourceVideoLayer) {
      kernel.current?.handleError('Unable to resolve a visible video layer for video upscale input.');
      kernel.current?.setIsSubmittingAITask(false);
      return { tasks: [], newWorkspaceId: null };
    }

    const timelineInsertStartMs = explicitVideoInputRange
      ? Math.round(explicitVideoInputRange.startMs)
      : Math.max(0, Math.round(resolvedSourceTrack!.startMs));
    const timelineInsertEndMsRaw = explicitVideoInputRange
      ? Math.round(explicitVideoInputRange.endMs)
      : Math.max(timelineInsertStartMs + 1, Math.round(resolvedSourceTrack!.endMs));
    const maxInputEndMs = !explicitVideoInputRange && resolvedSourceTrack?.mediaDurationMs !== null && Number.isFinite(resolvedSourceTrack?.mediaDurationMs)
      ? Math.max(
          timelineInsertStartMs + 1,
          Math.round(
            timelineInsertStartMs +
            Math.max(
              1,
              ((resolvedSourceTrack?.mediaDurationMs ?? 0) - (resolvedSourceTrack?.sourceInMs ?? 0)) /
                normalizeTimelineTrackPlaybackRate(resolvedSourceTrack?.playbackRate),
            ),
          ),
        )
      : timelineInsertEndMsRaw;
    const timelineInsertEndMs = explicitVideoInputRange
      ? Math.max(timelineInsertStartMs + 1, timelineInsertEndMsRaw)
      : Math.min(timelineInsertEndMsRaw, maxInputEndMs);
    const remainingTimelineSeconds =
      timelineInsertEndMs > timelineInsertStartMs
        ? (timelineInsertEndMs - timelineInsertStartMs) / 1000
        : undefined;
    if (
      diffusionModel === 'mirelo-sfx-1.6' &&
      (typeof remainingTimelineSeconds !== 'number' ||
        remainingTimelineSeconds < MIRELO_SFX_MIN_DURATION_SECONDS ||
        remainingTimelineSeconds > MIRELO_SFX_MAX_DURATION_SECONDS)
    ) {
      kernel.current?.handleError(`Mirelo SFX source duration must be between ${MIRELO_SFX_MIN_DURATION_SECONDS} and ${MIRELO_SFX_MAX_DURATION_SECONDS} seconds. Adjust the selected track trim or timeline range.`);
      kernel.current?.setIsSubmittingAITask(false);
      return { tasks: [], newWorkspaceId: null };
    }

    const createResultFromVideoLayer = async (videoLayer: Layer) => {
      const frameUrl = videoLayer.videoFramesUrls?.[0] || videoLayer.imageSrc || videoLayer.videoSrc;
      if (!frameUrl) {
          throw new Error('Unable to locate a preview frame for the video input layer.');
      }

      throwIfAborted(abortSignal);
      const frameResponse = await fetch(frameUrl, { cache: 'no-store', signal: abortSignal });
      if (!frameResponse.ok) {
          throw new Error(`Failed to download video preview frame: ${frameResponse.status}`);
      }

      const frameBlob = await frameResponse.blob();
      throwIfAborted(abortSignal);
      const extension = inferExtensionFromBlob(frameBlob, 'png');
      const fileName = `video-frame-${Date.now()}.${extension}`;
      const frameFile = new File([frameBlob], fileName, { type: frameBlob.type || 'image/png' });

      const dimensions = await new Promise<{ width: number; height: number }>((resolve, reject) => {
          const img = new Image();
          const objectUrl = URL.createObjectURL(frameBlob);
          img.onload = () => {
              if (abortSignal?.aborted) {
                  URL.revokeObjectURL(objectUrl);
                  reject(createAbortError());
                  return;
              }
              const width = img.naturalWidth || img.width;
              const height = img.naturalHeight || img.height;
              URL.revokeObjectURL(objectUrl);
              if (!width || !height) {
                  reject(new Error('Video preview frame has invalid dimensions.'));
                  return;
              }
              resolve({ width, height });
          };
          img.onerror = () => {
              URL.revokeObjectURL(objectUrl);
              reject(new Error('Failed to read video preview frame dimensions.'));
          };
          img.src = objectUrl;
      });

      return {
          file: frameFile,
          targetWidth: dimensions.width,
          targetHeight: dimensions.height,
          upscaleFactor: 1,
      };
    };

    try {
      throwIfAborted(abortSignal);

      const assembledImageResult = await createResultFromVideoLayer(sourceVideoLayer);
      throwIfAborted(abortSignal);

      if (!assembledImageResult || !assembledImageResult.file) {
          throw new Error("Could not assemble start image from the video layer.");
      }

      const startImageFile = assembledImageResult.file;
      const { publicUrl: startImageUrl, privateUrl: startImagePrivateUrl } = await dualUploadAsset(startImageFile, {
        ttlSeconds: 86400,
        filenamePrefix: 'film-start',
        fallbackContentType: 'image/webp',
        fallbackExtension: 'webp',
        projectIdOverride: projectId,
      }, finalWorkspaceId, abortSignal);

      const frameRectWidth = toFinite(outputWorkspace.frameRect?.w);
      const frameRectHeight = toFinite(outputWorkspace.frameRect?.h);
      // Size the composed source clip to the target task's input budget (2K for
      // upscalers, 1080p for edit/SFX), bounded by the clip's native resolution,
      // never to the ~1MPx frameRect, which would pre-downsample the master and
      // starve the upscaler of real detail. frameRect only supplies the ratio.
      const { width: outputWidth, height: outputHeight } = resolveReferenceClipDimensions({
        frameRectW: frameRectWidth,
        frameRectH: frameRectHeight,
        nativeW: toFinite(assembledImageResult.targetWidth),
        nativeH: toFinite(assembledImageResult.targetHeight),
        maxPixels: resolveReferenceClipMaxPixels(diffusionModel),
        fallbackW: assembledImageResult.targetWidth,
        fallbackH: assembledImageResult.targetHeight,
      });
      const outputFps = Math.max(8, Math.min(30, Math.round(toFinite(outputWorkspace.timelineFps) ?? 24)));

      let mainVideoPublicUrl: string | undefined;
      let timelineInsertStartMsForTask = timelineInsertStartMs;
      let timelineInsertEndMsForTask = timelineInsertEndMs;
      let remainingTimelineSecondsForTask = remainingTimelineSeconds;
      let timelineSourceInMsForTask = 0;
      const selectedTrackNeedsPlaybackComposition = Boolean(
        !explicitVideoInputRange &&
        resolvedSourceTrack &&
        Math.abs(normalizeTimelineTrackPlaybackRate(resolvedSourceTrack.playbackRate) - 1) > 0.0001,
      );
      try {
        const timelineReference = explicitVideoInputRange || selectedTrackNeedsPlaybackComposition
          ? await prepareComposedTimelineReferenceClip({
              workspaceId: finalWorkspaceId,
              tracks: explicitVideoInputRange ? resolvedTimelineCompositionTracks : [resolvedSourceTrack!],
              rangeStartMs: timelineInsertStartMs,
              rangeEndMs: timelineInsertEndMs,
              outputWidth,
              outputHeight,
              outputFps,
              filenamePrefix: diffusionModel === 'mirelo-sfx-1.6' ? 'mirelo-sfx-source-video' : 'film-reference-video',
              abortSignal,
            })
          : await prepareTimelineReferenceClipFromCursor({
              workspaceId: finalWorkspaceId,
              tracks: [resolvedSourceTrack!],
              playbackStartMs: timelineInsertStartMs,
              playbackEndMs: timelineInsertEndMs,
              cursorMs: timelineInsertStartMs,
              outputWidth,
              outputHeight,
              outputFps,
              filenamePrefix: diffusionModel === 'mirelo-sfx-1.6' ? 'mirelo-sfx-source-video' : 'film-reference-video',
              abortSignal,
            });
        mainVideoPublicUrl = timelineReference.publicUrl;
        timelineInsertStartMsForTask = timelineReference.timelineInsertStartMs;
        timelineInsertEndMsForTask = timelineInsertEndMs;
        remainingTimelineSecondsForTask = timelineReference.remainingTimelineSeconds;
        timelineSourceInMsForTask = 0;
      } catch (timelineReferenceError) {
        console.error('Failed to prepare timeline reference video for video edit workflow.', timelineReferenceError);
        const message = timelineReferenceError instanceof Error ? timelineReferenceError.message : 'Unknown timeline track reference preparation error.';
        kernel.current?.handleError(`Failed to prepare timeline video input. ${message}`);
        kernel.current?.setIsSubmittingAITask(false);
        return { tasks: [], newWorkspaceId: null };
      }

      const finalWorkspace = kernel.current.getWorkspace(finalWorkspaceId);
      const generationRect = finalWorkspace ? finalWorkspace.generationRect : outputWorkspace.generationRect;
      const frameRect = finalWorkspace ? finalWorkspace.frameRect : outputWorkspace.frameRect;
      const sourceLayerWorldRect = (() => {
        const layerRect = sourceVideoLayer.rect;
        const hasValidLayerRect =
          Number.isFinite(layerRect?.x) &&
          Number.isFinite(layerRect?.y) &&
          Number.isFinite(layerRect?.w) &&
          Number.isFinite(layerRect?.h) &&
          layerRect.w > 0 &&
          layerRect.h > 0;
        if (!hasValidLayerRect) return null;
        return {
          x: frameRect.x + layerRect.x,
          y: frameRect.y + layerRect.y,
          w: layerRect.w,
          h: layerRect.h,
        } satisfies Rect;
      })();
      const taskPlacementRect = explicitVideoInputRange
        ? { ...generationRect }
        : { ...(sourceLayerWorldRect ?? generationRect) };
      const taskDisplayWidth =
        Number.isFinite(taskPlacementRect.w) && taskPlacementRect.w > 0
          ? taskPlacementRect.w
          : outputWidth;
      const taskDisplayHeight =
        Number.isFinite(taskPlacementRect.h) && taskPlacementRect.h > 0
          ? taskPlacementRect.h
          : outputHeight;
      const taskChatSessionId = resolveTaskChatSessionId(projectId);
      throwIfAborted(abortSignal);
      const tasksToCreate = buildVideoEditTasks(
        {
          workspaceId: finalWorkspaceId,
          projectId,
          chatSessionId: taskChatSessionId,
          isAgentSpawned: resolveTaskIsAgentSpawned(taskChatSessionId),
          model: diffusionModel,
          params,
          numGenerations: enforceStudioModelMinimumGenerations(
            diffusionModel,
            numOutputsOverride ?? getLocalNumOutputs(projectId),
          ),
          layerContextSnapshot: getContextSnapshot(finalWorkspaceId),
          parentVersionId,
          outputScope: generationOutputScope ?? {
            kind: 'WORKSPACE',
            workspaceId: finalWorkspaceId,
          },
        },
        {
          mainImagePublicUrl: startImageUrl,
          mainImagePrivateUrl: startImagePrivateUrl,
          mainVideoPublicUrl,
          videoDurationSeconds: remainingTimelineSecondsForTask,
          targetWidth: taskDisplayWidth,
          targetHeight: taskDisplayHeight,
          rect: { ...taskPlacementRect },
          layerOverride: {
            name: diffusionModel === 'mirelo-sfx-1.6' ? 'Mirelo SFX carrier' : '',
            timelineStartMs: Math.round(timelineInsertStartMsForTask),
            timelineEndMs: Math.round(timelineInsertEndMsForTask),
            timelineSourceInMs: Math.round(timelineSourceInMsForTask),
          },
        },
      );

      throwIfAborted(abortSignal);
      await kernel.current?.processTasks(tasksToCreate, { abortSignal });
      return { tasks: tasksToCreate, newWorkspaceId: null };
    } catch (e: any) {
      if (isAbortError(e)) {
        kernel.current?.setIsSubmittingAITask(false);
        throw e;
      }
      kernel.current?.handleError(`Video editing failed: ${e.message || 'Unknown error'}`);
      console.error('Handle Queue Video Edit failed with error :', e);
      kernel.current?.setIsSubmittingAITask(false);
      return { tasks: [], newWorkspaceId: null };
    }
  }, [kernel, getContextSnapshot, dualUploadAsset, inferExtensionFromBlob, getProjectIdFromWorkspaceId, getLocalNumOutputs, getWorkspaceLayers, prepareComposedTimelineReferenceClip, prepareTimelineReferenceClipFromCursor, resolvePreferredTimelineTrackSelection, selectTopByRenderOrder, resolveTaskChatSessionId, resolveTaskIsAgentSpawned]);


  const prepareWorkspaceForGeneration = useCallback((workspaceId: string) => {
    if (!kernel.current) return;

    const { getActiveWorkspaceVersionId, promoteVersion, getUserPreferences } = kernel.current;

    if (getUserPreferences().autoPromoteOnGenerate === false) return;

    const activeVersionId = getActiveWorkspaceVersionId(workspaceId);
    if (!activeVersionId) return;

    const workspace = kernel.current.getWorkspace(workspaceId);
    const activeVersion = workspace?.versions?.find(v => v.id === activeVersionId);
    if (activeVersion && (activeVersion.status === 'CANDIDATE' || activeVersion.status === 'VALIDATED')) {
      if (activeVersion.status === 'CANDIDATE') {
        console.log(`[prepareWorkspaceForGeneration] Auto-promoting candidate version ${activeVersionId} before generation.`);
      } else {
        console.log(`[prepareWorkspaceForGeneration] Re-validating version ${activeVersionId}. This will reject other candidates.`);
      }
      promoteVersion(workspaceId, activeVersionId);
    }
  }, [kernel]);

  const generateInParallel = useCallback(async (overrides: Record<string, any>[], sourceWorkspaceId: string, options?: { allowedSourceImageUrls?: string[]; abortSignal?: AbortSignal }): Promise<void> => {
    if (!kernel.current) throw new Error("Kernel not available for parallel generation.");
    throwIfAborted(options?.abortSignal);

    const sourceWorkspace = kernel.current.getWorkspace(sourceWorkspaceId);
    if (!sourceWorkspace) {
      throw new Error(`Source workspace ${sourceWorkspaceId} not found for parallel generation.`);
    }

    const sourceProjectId = sourceWorkspace.projectId?.trim() || '';
    const generationOutputScope = resolveGenerationOutputScopeForView(
      sourceProjectId,
      sourceWorkspaceId,
      getProjectStudioViewMode(kernel.current, sourceProjectId),
    );
    const isListWorkspace = generationOutputScope?.kind === 'PROJECT';
    const allowedSourceImageUrls = new Set(
      (options?.allowedSourceImageUrls ?? []).filter((url): url is string => typeof url === 'string' && isInternalAssetUrl(url)),
    );
    toast.info(`Starting ${overrides.length} parallel generations in workspace "${sourceWorkspace.name}"...`);

    const allTaskWaitPromises: Promise<TaskCompletionData>[] = [];
    const listReferenceCache = new Map<string, Promise<LockableFile>>();

    const generationLaunchPromises = overrides.map(async (overrideSet, index) => {
      if (!overrideSet || !kernel.current) return;

      try {
        throwIfAborted(options?.abortSignal);
        // Separate special keys from standard model parameters
        const { referenceImageUrls, sourceImageUrl: rawSourceImageUrl, preserveSourceAspectRatio: rawPreserveSourceAspectRatio, autoColorMatch, ...parameterOverrides } = overrideSet;
        const sourceImageUrl = typeof rawSourceImageUrl === 'string' && rawSourceImageUrl.trim().length > 0
          ? rawSourceImageUrl.trim()
          : null;
        const requestedAspect = parameterOverrides.aspect_ratio;
        const preserveSourceAspectRatio = rawPreserveSourceAspectRatio !== false
          && !(typeof requestedAspect === 'string' && /^\d+(?:\.\d+)?:\d+(?:\.\d+)?$/.test(requestedAspect));

        // Handle reference images if provided
        let referenceImageFiles: File[] | undefined = undefined;
        if (!isListWorkspace && referenceImageUrls && Array.isArray(referenceImageUrls) && referenceImageUrls.length > 0) {
          const filePromises = referenceImageUrls.map(async (url: string) => {
            if (typeof url !== 'string' || !isInternalAssetUrl(url)) {
              throw new Error(`Invalid reference image URL format: "${url}". Must start with /api/assets/ (preferred) or the legacy /api/user-files/ fallback.`);
            }
            return fetchReferenceImageFile(url, options?.abortSignal);
          });
          referenceImageFiles = await Promise.all(filePromises);
        }

        // Construct a runtime state override for this specific generation
        const diffusionModel = kernel.current.getDiffusionModel();
        const modelParams = kernel.current.getModelParams();
        const currentParams = modelParams[diffusionModel];
        if (!currentParams) throw new Error(`Parameters for model ${diffusionModel} not found.`);

        if (sourceImageUrl) {
          if (!isListWorkspace || !isInternalAssetUrl(sourceImageUrl) || !allowedSourceImageUrls.has(sourceImageUrl)) {
            throw new Error('GENERATE_VARIATIONS: sourceImageUrl must be a current-turn attachment or explicitly mentioned List output.');
          }
          const sourceModelId = resolveRegistryModelIdFromRuntimeName(diffusionModel) as StudioModelId | null;
          if (sourceModelId && STUDIO_MODEL_REGISTRY[sourceModelId]?.capabilities.supportsMainImageInput === false) {
            throw new Error(`GENERATE_VARIATIONS: model "${diffusionModel}" cannot edit a source image. Select an image-edit model first.`);
          }
        }

        // Apply all standard parameter overrides
        const newParams: Record<string, any> = { ...currentParams };
        for (const key in parameterOverrides) {
          if (Object.prototype.hasOwnProperty.call(parameterOverrides, key)) {
            newParams[key] = parameterOverrides[key];
          }
        }

        const lockableReferenceFiles = isListWorkspace
          ? await loadListReferenceImages(Array.isArray(referenceImageUrls) ? referenceImageUrls : [], getStudioModelReferenceLimits(diffusionModel).max, {
              mainImageUrl: sourceImageUrl, cache: listReferenceCache, signal: options?.abortSignal,
            })
          : referenceImageFiles?.map(f => ({ id: uuidv4(), file: f, locked: false }));

        const runtimeStateOverride: RuntimeState = {
          diffusionModel: diffusionModel,
          parameters: {
            ...modelParams,
            [diffusionModel]: newParams as DiffusionParams
          },
          // Conditionally add the reference images to the override
          ...(lockableReferenceFiles && { referenceImages: lockableReferenceFiles }),
        };

        // Run the model in the *source* workspace with the override
        const generationOptions = {
          ...(sourceImageUrl ? { mainImageUrlOverride: sourceImageUrl, preserveSourceAspectRatio } : {}),
          ...(generationOutputScope ? { outputScope: generationOutputScope } : {}),
          autoColorMatch,
        };
        throwIfAborted(options?.abortSignal);
        const { tasks } = await kernel.current.runModel(sourceWorkspaceId, runtimeStateOverride, 1, options?.abortSignal, generationOptions);
        throwIfAborted(options?.abortSignal);
        
        if (tasks.length > 0) {
          const taskWaitPromises = tasks.map(task => kernel.current!.waitForTask(task.id));
          allTaskWaitPromises.push(...taskWaitPromises);
        } else {
          throw new Error(`No task was created for variation ${index + 1}.`);
        }
      } catch (error) {
        console.error(`Failed to launch generation for override set ${index} in workspace ${sourceWorkspaceId}:`, error);
        allTaskWaitPromises.push(Promise.resolve({ id: `launch-failure-${index}`, status: 'FAILURE', error: error instanceof Error ? error : new Error(String(error)) }));
      }
    });

    let stopWaiting: () => void = () => {};
    const stopped = new Promise<never>((_resolve, reject) => {
      stopWaiting = () => reject(new DOMException('Generation stopped.', 'AbortError'));
      options?.abortSignal?.addEventListener('abort', stopWaiting, { once: true });
    });
    try {
      if (options?.abortSignal?.aborted) stopWaiting();
      await Promise.race([Promise.all(generationLaunchPromises), stopped]);
      throwIfAborted(options?.abortSignal);

      if (allTaskWaitPromises.length > 0) {
        const results = await Promise.race([Promise.all(allTaskWaitPromises), stopped]);
        const failedTasks = results.filter(r => r.status === 'FAILURE');
        if (failedTasks.length > 0) {
          const successCount = results.length - failedTasks.length;
          const firstError = failedTasks[0]?.error?.message || 'One or more parallel generations failed.';
          throw new Error(`${failedTasks.length} parallel generation(s) failed. ${successCount} succeeded. First error: ${firstError}`);
        }
        toast.success(`${overrides.length} parallel generations completed successfully.`);
      } else {
        throw new Error('Parallel generation finished without creating any tasks.');
      }
    } finally {
      options?.abortSignal?.removeEventListener('abort', stopWaiting);
    }
  }, [kernel]);

  const buildQuickPowerRuntimeState = useCallback((
    modelId: 'fater-enhance' | 'fater-upscale',
    preserveHighRes: boolean
  ): RuntimeState => {
    const quickPowerParams = {
      prompt: "",
      add_annotations_as_ref: false,
      seed: -1,
      resolution: '1 MP',
      aspect_ratio: 'match_input_image',
      harmonize: 'On',
      harmonize_profile: modelId === 'fater-upscale' ? 'strict' : 'soft',
      preserve_high_res: preserveHighRes,
      alpha_radius: 64,
    } as unknown as FluxTwoProParams;

    return {
      diffusionModel: modelId,
      parameters: {
        [modelId]: quickPowerParams,
      },
      // Quick powers must run in isolation from UI reference attachments.
      referenceImages: [],
    };
  }, []);

  const quickEnhance = useCallback(async (workspaceId: string, options?: { preserveHighRes?: boolean; abortSignal?: AbortSignal }): Promise<{ tasks: GenerationTask[], newWorkspaceId: string | null }> => {
    if (!kernel.current) return { tasks: [], newWorkspaceId: null };

    prepareWorkspaceForGeneration(workspaceId);
    const runtimeStateOverride = buildQuickPowerRuntimeState('fater-enhance', options?.preserveHighRes ?? true);
    kernel.current?.playUISound('GENERATE_START', { volume: 0.4 });

    const projectId = kernel.current.getWorkspace(workspaceId)?.projectId;
    const generationOutputScope = resolveGenerationOutputScopeForView(
      projectId,
      workspaceId,
      getProjectStudioViewMode(kernel.current, projectId),
    );
    const isListProjectGeneration = generationOutputScope?.kind === 'PROJECT';
    const preGenerationVersionId = isListProjectGeneration
      ? null
      : await kernel.current.ensureCurrentStateAsVersion(
          workspaceId,
          'Pre-Generation',
          generationOutputScope ? { outputScope: generationOutputScope } : undefined,
        );
    const generationParentVersionId = isListProjectGeneration
      ? null
      : (kernel.current.getWorkspace(workspaceId)?.activeVersionId ?? preGenerationVersionId);
    return handleQueueEditDiffusion(
      workspaceId,
      runtimeStateOverride,
      generationParentVersionId,
      1,
      undefined,
      options?.abortSignal,
      generationOutputScope ? { outputScope: generationOutputScope } : undefined,
    );
  }, [buildQuickPowerRuntimeState, handleQueueEditDiffusion, prepareWorkspaceForGeneration, kernel]);


  const quickUpscale = useCallback(async (workspaceId: string, options?: { preserveHighRes?: boolean; abortSignal?: AbortSignal }): Promise<{ tasks: GenerationTask[], newWorkspaceId: string | null }> => {
    if (!kernel.current) return { tasks: [], newWorkspaceId: null };

    prepareWorkspaceForGeneration(workspaceId);
    const runtimeStateOverride = buildQuickPowerRuntimeState('fater-upscale', options?.preserveHighRes ?? true);
    kernel.current?.playUISound('GENERATE_START', { volume: 0.4 });

    const projectId = kernel.current.getWorkspace(workspaceId)?.projectId;
    const generationOutputScope = resolveGenerationOutputScopeForView(
      projectId,
      workspaceId,
      getProjectStudioViewMode(kernel.current, projectId),
    );
    const isListProjectGeneration = generationOutputScope?.kind === 'PROJECT';
    const preGenerationVersionId = isListProjectGeneration
      ? null
      : await kernel.current.ensureCurrentStateAsVersion(
          workspaceId,
          'Pre-Generation',
          generationOutputScope ? { outputScope: generationOutputScope } : undefined,
        );
    const generationParentVersionId = isListProjectGeneration
      ? null
      : (kernel.current.getWorkspace(workspaceId)?.activeVersionId ?? preGenerationVersionId);
    return handleQueueEditDiffusion(
      workspaceId,
      runtimeStateOverride,
      generationParentVersionId,
      1,
      undefined,
      options?.abortSignal,
      generationOutputScope ? { outputScope: generationOutputScope } : undefined,
    );
  }, [buildQuickPowerRuntimeState, handleQueueEditDiffusion, prepareWorkspaceForGeneration, kernel]);

  const sweepUpscale = useCallback(async (workspaceId: string, gridSize: SweepUpscaleGridSize): Promise<void> => {
    const k = kernel.current;
    if (!k) return;
    const frameRect = k.getWorkspace(workspaceId)?.frameRect;
    if (!frameRect || !(frameRect.w > 0 && frameRect.h > 0)) {
      throw new Error('This workspace has no frame.');
    }
    const baseRect: Rect = { x: frameRect.x, y: frameRect.y, w: frameRect.w, h: frameRect.h };
    const modelParams = { ...getDefaultSeedVR2Params(), outputMegaPixels: SWEEP_UPSCALE_OUTPUT_MEGAPIXELS[gridSize] };
    // SELECT_MODEL only seeds defaults for a model that has none, so without this pin an
    // earlier manual megapixel change would silently resize the tiles of a fixed-price power.
    k.setModelParams(prev => ({ ...prev, [SWEEP_UPSCALE_MODEL_ID]: modelParams }));
    const batchId = crypto.randomUUID();
    const script = buildGridScript(baseRect, buildSweepUpscaleRuntime(gridSize), SWEEP_UPSCALE_MODEL_ID, 'current', {
      batchId,
      modelParams: modelParams as unknown as Record<string, unknown>,
    });
    await k.addAssistantMessage(`${gridSize === 2 ? '8K' : '16K'} upscale grid script (${script.commands.length} commands).`, [script], 'Fater');
    await k.runGridBatch(workspaceId, script, batchId);
  }, [kernel]);

  const developStreet = useCallback(async (workspaceId: string, recipeId: string, cuts: StreetCuts): Promise<void> => {
    const k = kernel.current;
    if (!k) return;
    const recipe = STREET_DEVELOP_RECIPES.find((r) => r.id === recipeId);
    if (!recipe) throw new Error(`Unknown Develop 360 recipe "${recipeId}".`);
    const workspace = k.getWorkspace(workspaceId);
    if (!workspace) throw new Error('This workspace no longer exists.');
    const invalid = streetDevelopErrors(recipe, cuts, workspace.frameRect);
    if (invalid.length) throw new Error(`Develop 360 "${recipe.label}" cannot run. ${invalid.join(' ')}`);
    const defaultParams = getDefaultParamsForModel(recipe.model);
    if (!defaultParams) throw new Error(`Develop 360 "${recipe.label}" names an unknown model.`);
    if (streetDevelopRuns.has(workspaceId)) throw new Error(STREET_RUN_BUSY);
    streetDevelopRuns.add(workspaceId);
    try {
      const frame = workspace.frameRect;
      const steps = streetDevelopSteps(recipe, cuts).map((step) => ({ ...step, rect: streetZoneRect(step.zone, frame) }));
      const round = (n: number) => Math.round(n * 10) / 10;
      await k.addAssistantMessage([
        `Develop 360 · ${recipe.label}: ${steps.length} generations with ${recipe.model} ${JSON.stringify(recipe.params)}, all at once, nothing attached.`,
        ...steps.map(({ rect, prompt }, i) => [
          `Step ${i + 1}: zone x ${round(rect.x - frame.x)}, y ${round(rect.y - frame.y)}, ${round(rect.w)} × ${round(rect.h)} in the frame.`,
          prompt,
        ].join('\n\n')),
      ].join('\n\n'), undefined, 'Fater');

      k.prepareWorkspaceForGeneration(workspaceId);
      // A task id, or why the slice has none; launched one after the other, so their Pre-Generation versions cannot race.
      const launches: (string | Error)[] = [];
      for (const { rect, prompt } of steps) {
        try {
          // The zone rides on the run itself, so the user's Generation Area is never moved and a drag cannot retarget a slice.
          const { tasks } = await k.runModel(workspaceId, {
            diffusionModel: recipe.model,
            parameters: { ...k.getModelParams(), [recipe.model]: { ...defaultParams(), ...recipe.params, prompt } as DiffusionParams },
            referenceImages: [],
          }, 1, undefined, { rect, autoColorMatch: recipe.autoColorMatch });
          launches.push(tasks[0]?.id ?? new Error('the generation did not start.'));
        } catch (error) {
          // Those already launched still land.
          launches.push(error instanceof Error ? error : new Error(String(error)));
          break;
        }
      }
      const landings = await Promise.allSettled(steps.map(async (_, i) => {
        const launch = launches[i] ?? new Error('not started, an earlier launch failed.');
        if (launch instanceof Error) throw launch;
        const completion = await k.waitForTask(launch);
        if (completion.status === 'FAILURE') {
          throw new Error(completion.error instanceof Error ? completion.error.message : String(completion.error ?? 'the generation failed.'));
        }
        const layerId = (completion.data as { layer?: Layer | null } | undefined)?.layer?.id;
        if (!layerId) throw new Error('the generation returned no layer.');
        return layerId;
      }));

      const failures: string[] = [];
      const landed: string[] = [];
      for (const [i, landing] of landings.entries()) {
        if (landing.status === 'fulfilled') landed.push(landing.value);
        else failures.push(`Develop 360, step ${i + 1} of ${steps.length}: ${landing.reason instanceof Error ? landing.reason.message : String(landing.reason)}`);
      }
      // Each landing hid the layers its launch did not see, the other slices among them.
      for (const [i, layerId] of landed.entries()) {
        if (k.getLayerRegistry()[layerId]) k.setLayer(layerId, (existing) => ({ ...existing, visible: true }));
        else failures.push(`Develop 360, landed slice ${i + 1} of ${landed.length}: its layer is gone.`);
      }
      // Each step's version holds only its own output: the developed panorama is one more, of what is on screen.
      if (landed.length > 0 && !await k.ensureCurrentStateAsVersion(workspaceId, 'Develop 360')) failures.push('Develop 360: the final version could not be saved.');
      if (failures.length) throw new Error(failures.join(' '));
    } finally {
      streetDevelopRuns.delete(workspaceId);
    }
  }, [kernel]);

  const texturiseStreet = useCallback(async (workspaceId: string, recipeId: string): Promise<void> => {
    const k = kernel.current;
    if (!k) return;
    const recipe = STREET_TEXTURE_RECIPES.find((r) => r.id === recipeId);
    if (!recipe) throw new Error(`Unknown texturise recipe "${recipeId}".`);
    const workspace = k.getWorkspace(workspaceId);
    if (!workspace) throw new Error('This workspace no longer exists.');
    if (!isStreetPanoramaFrame(workspace.frameRect)) throw new Error('This workspace is not a 360° panorama.');
    const defaultParams = getDefaultParamsForModel(recipe.model);
    if (!defaultParams) throw new Error(`${recipe.label} names an unknown model.`);
    if (streetDevelopRuns.has(workspaceId)) throw new Error(STREET_RUN_BUSY);
    streetDevelopRuns.add(workspaceId);
    try {
      await k.addAssistantMessage(`${recipe.label}: one generation with ${recipe.model} ${JSON.stringify(recipe.params)} over the whole panorama frame, nothing attached.\n\n${recipe.prompt}`, undefined, 'Fater');
      k.prepareWorkspaceForGeneration(workspaceId);
      const { tasks } = await k.runModel(workspaceId, {
        diffusionModel: recipe.model,
        parameters: { ...k.getModelParams(), [recipe.model]: { ...defaultParams(), ...recipe.params, prompt: recipe.prompt } as DiffusionParams },
        referenceImages: [],
      }, 1, undefined, { rect: workspace.frameRect, autoColorMatch: recipe.autoColorMatch, autoIntegrate: recipe.autoIntegrate });
      if (!tasks[0]) throw new Error(`${recipe.label}: the generation did not start.`);
      const completion = await k.waitForTask(tasks[0].id);
      if (completion.status === 'FAILURE') {
        throw new Error(`${recipe.label}: ${completion.error instanceof Error ? completion.error.message : String(completion.error ?? 'the generation failed.')}`);
      }
    } finally {
      streetDevelopRuns.delete(workspaceId);
    }
  }, [kernel]);

  const repairStreetCuts = useCallback(async (workspaceId: string): Promise<void> => {
    const k = kernel.current;
    if (!k) return;
    const workspace = k.getWorkspace(workspaceId);
    if (!workspace) throw new Error('This workspace no longer exists.');
    const recipe = STREET_REPAIR_RECIPE;
    const frame = workspace.frameRect;
    const cuts = rememberedStreetCuts(k.getLayersInWorkspaceContext(workspaceId));
    const invalid = streetRepairErrors(cuts, frame);
    if (!cuts || invalid.length) throw new Error(`${recipe.label} cannot run. ${invalid.join(' ')}`);
    const defaultParams = getDefaultParamsForModel(recipe.model);
    if (!defaultParams) throw new Error(`${recipe.label} names an unknown model.`);
    if (streetDevelopRuns.has(workspaceId)) throw new Error(STREET_RUN_BUSY);
    streetDevelopRuns.add(workspaceId);
    try {
      const steps = streetRepairSteps(cuts).map((step) => ({ ...step, rect: streetZoneRect(step.zone, frame), strokeRect: streetZoneRect(step.stroke, frame) }));
      const round = (n: number) => Math.round(n * 10) / 10;
      await k.addAssistantMessage([
        `${recipe.label}: ${steps.length} generations with ${recipe.model} ${JSON.stringify(recipe.params)}, all at once, each with a red stroke on its cut;`
          + ` each output is then cut to the middle ${round(100 * (recipe.stroke * recipe.keptStrokes))} % of its width, edges feathered over ${recipe.blendRadius} px.`,
        recipe.prompt,
        ...steps.map(({ rect, strokeRect }, i) => `Cut ${i + 1}: zone x ${round(rect.x - frame.x)}, y ${round(rect.y - frame.y)}, ${round(rect.w)} × ${round(rect.h)} in the frame;`
          + ` red stroke x ${round(strokeRect.x - frame.x)}, ${round(strokeRect.w)} wide.`),
      ].join('\n\n'), undefined, 'Fater');

      k.prepareWorkspaceForGeneration(workspaceId);
      // A task id, or why the cut has none; launched one after the other, so their Pre-Generation versions cannot race.
      const launches: (string | Error)[] = [];
      for (const { rect, strokeRect } of steps) {
        try {
          const { tasks } = await k.runModel(workspaceId, {
            diffusionModel: recipe.model,
            parameters: { ...k.getModelParams(), [recipe.model]: { ...defaultParams(), ...recipe.params, prompt: recipe.prompt } as DiffusionParams },
            referenceImages: [],
          }, 1, undefined, { rect, autoColorMatch: recipe.autoColorMatch, mainImageRedBand: { x: strokeRect.x, w: strokeRect.w } });
          launches.push(tasks[0]?.id ?? new Error('the generation did not start.'));
        } catch (error) {
          // Those already launched still land, and are cut below.
          launches.push(error instanceof Error ? error : new Error(String(error)));
          break;
        }
      }
      const landings = await Promise.allSettled(steps.map(async (_, i) => {
        const launch = launches[i] ?? new Error('not started, an earlier launch failed.');
        if (launch instanceof Error) throw launch;
        const completion = await k.waitForTask(launch);
        if (completion.status === 'FAILURE') {
          throw new Error(completion.error instanceof Error ? completion.error.message : String(completion.error ?? 'the generation failed.'));
        }
        const layerId = (completion.data as { layer?: Layer | null } | undefined)?.layer?.id;
        if (!layerId) throw new Error('the generation returned no layer.');
        return layerId;
      }));

      const failures: string[] = [];
      const landed: string[] = [];
      const repaired: string[] = [];
      for (const [i, landing] of landings.entries()) {
        const failed = (reason: string) => failures.push(`${recipe.label}, cut ${i + 1} of ${steps.length}: ${reason}`);
        if (landing.status === 'rejected') {
          failed(landing.reason instanceof Error ? landing.reason.message : String(landing.reason));
          continue;
        }
        const layer = k.getLayerRegistry()[landing.value];
        if (!layer) {
          failed('its layer is gone.');
          continue;
        }
        landed.push(layer.id);
        // The pixels applyNativeLayerCut cuts in: a layer's world rect is its image's pixels times its scale.
        const scale = layer.scale ?? 1;
        const width = layer.rect.w / scale, height = layer.rect.h / scale;
        const [from, to] = steps[i]!.kept.map((share) => share * width) as [number, number];
        const strip: CropPoint[] = [{ x: from, y: 0 }, { x: to, y: 0 }, { x: to, y: height }, { x: from, y: height }];
        if (await k.applyNativeLayerCut(layer.id, [strip], { blendRadius: recipe.blendRadius, invert: false })) repaired.push(layer.id);
        else failed('its output could not be cut, so it is hidden.');
      }
      // Each landing hid the layers its launch did not see, the other repairs among them.
      for (const layerId of landed) {
        k.setLayer(layerId, (existing) => ({ ...existing, visible: repaired.includes(layerId) }));
      }
      if (repaired.length > 0 && !await k.ensureCurrentStateAsVersion(workspaceId, recipe.label)) failures.push(`${recipe.label}: the final version could not be saved.`);
      if (failures.length) throw new Error(failures.join(' '));
    } finally {
      streetDevelopRuns.delete(workspaceId);
    }
  }, [kernel]);

  const detectRecastZones = useCallback(async (workspaceId: string): Promise<RecastZone[]> => {
    const k = kernel.current;
    if (!k) return [];
    const workspace = k.getWorkspace(workspaceId);
    if (!workspace) throw new Error('This workspace no longer exists.');
    if (recastDetections.has(workspaceId)) throw new Error('People are already being detected on this workspace.');
    recastDetections.add(workspaceId);
    try {
      const frame = workspace.frameRect;
      // The layers' native pixels per world unit, which getIdealLongestCanvasSize measures over the Generation Area.
      const density = (await k.getIdealLongestCanvasSize(workspaceId) ?? 0) / Math.max(workspace.generationRect.w, workspace.generationRect.h);
      const longSide = Math.min(RECAST_RENDER_MAX_SIDE, density > 0 ? density * Math.max(frame.w, frame.h) : RECAST_RENDER_MAX_SIDE);
      const pixels = Math.round((longSide * longSide * Math.min(frame.w, frame.h)) / Math.max(frame.w, frame.h));
      const { file } = await k.renderWorkspaceArea(workspaceId, undefined, pixels, false, false, 1, frame, 'jpeg');
      if (!file) throw new Error('The workspace could not be rendered.');
      const bitmap = await createImageBitmap(file);
      try {
        const { side, tiles } = recastTiles(bitmap.width, bitmap.height);
        if (tiles.length > RECAST_MAX_TILES) throw new Error('This frame is too long and narrow to search for people: draw the zones by hand on a less elongated frame.');
        const upload = pLimit(4);
        const images = await Promise.all(tiles.map(({ x, y }) => upload(async () => getCloudTmpUrl(await recastTileJpeg(bitmap, x, y, side), 60 * 60, 'image/jpeg'))));
        const response = await fetch('/api/ai/detect-people', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ projectId: workspace.projectId, images }),
        });
        const result = await response.json().catch(() => null) as { tiles?: { masks: string[] }[]; failedTiles?: number; message?: string } | null;
        if (!response.ok || !Array.isArray(result?.tiles) || result.tiles.length !== tiles.length) throw new Error(result?.message ?? 'People could not be detected.');
        const found: { box: RecastBox; tile: number }[] = [];
        for (const [t, { x, y }] of tiles.entries()) {
          for (const mask of result.tiles[t]!.masks) {
            // On the main thread: a mask is about 1 Mpx, a few milliseconds, and the awaited decode between masks yields.
            const { data, width, height } = await loadImageDataFromBlob(new Blob([Uint8Array.from(atob(mask), (c) => c.charCodeAt(0))], { type: 'image/png' }));
            const sx = side / width, sy = side / height;
            const minBlob = recastMinBlobPixels(bitmap.width, bitmap.height, side, width, height);
            for (const b of maskBlobBoxes(maskBits(data, width, height), width, height, minBlob)) found.push({ box: [x + b[0] * sx, y + b[1] * sy, x + b[2] * sx, y + b[3] * sy], tile: t });
          }
        }
        if (result.failedTiles) toast.warning(`${result.failedTiles} of ${tiles.length} parts of the image could not be read: people there may have no zone.`);
        const zones = groupRecastZones(mergePeopleBoxes(found), bitmap.width, bitmap.height);
        return recastZonesFromPixels(zones.map(({ rect }) => rect), bitmap.width, bitmap.height, frame);
      } finally {
        bitmap.close();
      }
    } finally {
      recastDetections.delete(workspaceId);
    }
  }, [kernel]);

  const recastExtras = useCallback(async (workspaceId: string, zones: RecastZone[]): Promise<void> => {
    const k = kernel.current;
    if (!k) return;
    const workspace = k.getWorkspace(workspaceId);
    if (!workspace) throw new Error('This workspace no longer exists.');
    const frame = workspace.frameRect;
    const invalid = recastExtrasErrors(zones, frame);
    if (invalid.length) throw new Error(`Recast extras cannot run. ${invalid.join(' ')}`);
    const { model, params } = RECAST_EXTRAS_RECIPE;
    const generate = recastGeneration(k, workspaceId);
    if (recastExtrasRuns.has(workspaceId)) throw new Error('Recast extras is already running on this workspace.');
    const run = { stopRequested: false };
    recastExtrasRuns.set(workspaceId, run);
    let landed = 0;
    let failure: unknown = null;
    try {
      const steps = recastExtrasSteps(zones, frame);
      const round = (n: number) => Math.round(n * 10) / 10;
      await k.addAssistantMessage([
        `Recast extras: ${steps.length} generations with ${model} ${JSON.stringify(params)}, one after the other, top to bottom then left to right.`,
        RECAST_EXTRAS_RECIPE.prompt,
        ...steps.map(({ number, rect, note }) => `Zone ${number}: x ${round(rect.x - frame.x)}, y ${round(rect.y - frame.y)}, side ${round(rect.w)} in the frame.${note ? ` ${RECAST_EXTRAS_RECIPE.noteLead}${note}` : ''}`),
      ].join('\n\n'), undefined, 'Fater');

      for (const [i, { number, rect, prompt }] of steps.entries()) {
        if (run.stopRequested) break;
        await generate(prompt, rect, (reason) => new Error(`Recast extras, zone ${number} (${i + 1} of ${steps.length}): ${reason}`));
        landed += 1;
      }
    } catch (error) {
      failure = error;
    } finally {
      recastExtrasRuns.delete(workspaceId);
    }
    // What landed stays, after a failure or a stop too: one version holds it.
    if (landed > 0 && !await k.ensureCurrentStateAsVersion(workspaceId, 'Recast extras')) throw new Error('Recast extras: the final version could not be saved.');
    if (failure) throw failure;
  }, [kernel]);

  const recastGenerationArea = useCallback(async (workspaceId: string, options?: { abortSignal?: AbortSignal }): Promise<void> => {
    const k = kernel.current;
    if (!k) return;
    if (!k.getWorkspace(workspaceId)) throw new Error('This workspace no longer exists.');
    const invalid = recastRecipeErrors();
    if (invalid.length) throw new Error(`Recast extras cannot run. ${invalid.join(' ')}`);
    const { model, params } = RECAST_EXTRAS_RECIPE;
    const generate = recastGeneration(k, workspaceId);
    if (recastExtrasRuns.has(workspaceId)) throw new Error('Recast extras is already running on this workspace.');
    recastExtrasRuns.set(workspaceId, { stopRequested: false });
    try {
      await k.addAssistantMessage([
        `Recast extras: one generation with ${model} ${JSON.stringify(params)} on the generation area.`,
        RECAST_EXTRAS_RECIPE.prompt,
      ].join('\n\n'), undefined, 'Fater');
      await generate(RECAST_EXTRAS_RECIPE.prompt, undefined, (reason) => new Error(`Recast extras, generation area: ${reason}`), options?.abortSignal);
    } finally {
      recastExtrasRuns.delete(workspaceId);
    }
    if (!await k.ensureCurrentStateAsVersion(workspaceId, 'Recast extras')) throw new Error('Recast extras: the final version could not be saved.');
  }, [kernel]);

  const isRecastExtrasRunning = useCallback((workspaceId: string) => recastExtrasRuns.has(workspaceId), []);

  const stopRecastExtras = useCallback((workspaceId: string) => {
    const run = recastExtrasRuns.get(workspaceId);
    if (run) run.stopRequested = true;
  }, []);

  const vectorizeLayer = useCallback(async (layerId: string): Promise<Layer | null> => {
    const k = kernel.current;
    if (!k) return null;
    const layer = k.getLayerRegistry()[layerId];
    if (!layer) return null;
    if (isLayerVectorized(layer)) {
      return layer;
    }
    if (layer.isVectorizing === true) {
      return layer;
    }
    if (layer.type === 'video' || layer.type === 'audio') {
      throw new Error('Only image layers can be vectorized.');
    }

    const workspaceId = layer.layerWorkspaceId;
    if (!workspaceId) throw new Error('Project layers cannot be vectorized in a workspace context.');
    const projectId = getProjectIdFromWorkspaceId(workspaceId, 'vectorizeLayer');
    k.patchEntityRuntimeState('StudioLayer', layer.id, {
      isVectorizing: true,
      pulseToken: Date.now(),
    });

    try {
      let imageUrl = typeof layer.imageSrc === 'string' ? layer.imageSrc.trim() : '';
      const isAppApiUrl = (() => {
        if (!imageUrl) return false;
        if (imageUrl.startsWith('/api/')) return true;
        if (typeof window === 'undefined') return false;
        try {
          const parsed = new URL(imageUrl, window.location.origin);
          return parsed.origin === window.location.origin && parsed.pathname.startsWith('/api/');
        } catch {
          return false;
        }
      })();

      if (imageUrl) {
        const original = await fetch(imageUrl).then((response) => (response.ok ? response.blob() : null)).catch(() => null);
        if (original && original.size > RECRAFT_VECTORIZE_WORKSPACE_MAX_FILE_BYTES) {
          imageUrl = await getCloudTmpUrl(await shrinkImageForTrace(original), 60 * 60 * 2, 'image/png');
        } else if (original && resolveVectorTraceModel(projectId) === 'ideogram-vector-1-vectorize') {
          // Our server uploads the picture to Ideogram, and reads only a public image copy of ours.
          imageUrl = await getCloudTmpUrl(original, 60 * 60 * 2, original.type || 'image/png');
        } else if (isAppApiUrl) {
          imageUrl = await getCloudTmpUrl(imageUrl, 60 * 60 * 2);
        }
      } else if (layer.imageData) {
        imageUrl = await getCloudTmpUrl(await shrinkImageForTrace(layer.imageData), 60 * 60 * 2, 'image/png');
      }
      if (!imageUrl) {
        throw new Error('This layer has no image source to vectorize.');
      }

      k.logUserAction('Layer Vectorize Started', {
        layer_id: layer.id,
        layer_name: layer.name,
        workspaceId,
      });

      const taskId = crypto.randomUUID();
      const taskChatSessionId = resolveTaskChatSessionId(projectId);
      const workspace = k.getWorkspace(workspaceId);
      const frameRect = workspace?.frameRect ?? { x: 0, y: 0, w: 0, h: 0 };
      const layerRect = layer.rect;
      const layerDisplayWidth = Number.isFinite(layerRect.w) && layerRect.w > 0
        ? layerRect.w
        : Math.max(1, layer.imageData?.width ?? 1);
      const layerDisplayHeight = Number.isFinite(layerRect.h) && layerRect.h > 0
        ? layerRect.h
        : Math.max(1, layer.imageData?.height ?? 1);
      const taskWorldRect: Rect = {
        x: frameRect.x + (Number.isFinite(layerRect.x) ? layerRect.x : 0),
        y: frameRect.y + (Number.isFinite(layerRect.y) ? layerRect.y : 0),
        w: layerDisplayWidth,
        h: layerDisplayHeight,
      };
      const hiddenSourceSnapshot = getContextSnapshot(workspaceId).map((entry) => (
        entry.id === layer.id ? { ...entry, visible: false } : entry
      ));
      const task: GenerationTask = {
        id: taskId,
        chatSessionId: taskChatSessionId,
        isAgentSpawned: resolveTaskIsAgentSpawned(taskChatSessionId),
        type: 'diffuse',
        workspaceId,
        outputScope: { kind: 'WORKSPACE', workspaceId },
        projectId,
        sourceLayerId: layer.id,
        operation: 'vectorize-layer',
        params: {
          prompt: '',
          seed: -1,
          noParamsSave: true,
        } as DiffusionParams,
        model: resolveVectorTraceModel(projectId),
        mainImagePublicUrl: imageUrl,
        mergeStrategy: 'stretchOnArea',
        rect: taskWorldRect,
        targetWidth: Math.max(1, Math.round(layerDisplayWidth)),
        targetHeight: Math.max(1, Math.round(layerDisplayHeight)),
        upscaleFactor: 1,
        blurRadius: 0,
        layerContextSnapshot: hiddenSourceSnapshot,
        parentVersionId: workspace?.activeVersionId ?? null,
      };

      const processing = k.processTasks([task]);
      const completion = await k.waitForTask(taskId);
      await processing;

      if (completion.status === 'FAILURE') {
        throw completion.error instanceof Error
          ? completion.error
          : new Error(typeof completion.error === 'string' ? completion.error : 'Vectorization failed.');
      }

      const vectorizedLayer = (completion.data as { layer?: Layer | null } | undefined)?.layer ?? null;
      if (vectorizedLayer?.id) {
        // In Vector, as Illustrator's Image Trace, the paths replace their picture on the artboard.
        if (k.getInventory().Project.registry[projectId]?.projectType === 'VECTOR') {
          k.setLayer(layer.id, (current) => ({ ...current, visible: false }));
          k.invalidateWorkspaceVersionCache(workspaceId);
        }
        setProjectSelectedLayersId(k, projectId, [vectorizedLayer.id]);
        k.logUserAction('Layer Vectorize Completed', {
          layer_id: layer.id,
          vector_layer_id: vectorizedLayer.id,
          workspaceId,
          svgUrl: vectorizedLayer.imageSrc,
        });
      }
      return vectorizedLayer;
    } finally {
      k.patchEntityRuntimeState('StudioLayer', layer.id, {
        isVectorizing: false,
      });
    }
  }, [getContextSnapshot, getProjectIdFromWorkspaceId, kernel, resolveTaskChatSessionId, resolveTaskIsAgentSpawned]);

  const vectorizeVisibleLayers = useCallback(async (workspaceId: string): Promise<Layer[]> => {
    const k = kernel.current;
    if (!k) return [];

    const workspace = k.getWorkspace(workspaceId);
    if (!workspace) {
      throw new Error('Workspace not found for vectorization.');
    }

    const visibleRenderableLayers = getWorkspaceLayers(workspaceId).filter((layer) => (
      layer.visible !== false &&
      layer.type !== 'audio'
    ));
    const hasVectorizableLayer = visibleRenderableLayers.some((layer) => (
      !isLayerVectorized(layer) &&
      layer.isVectorizing !== true &&
      layer.type !== 'video'
    ));
    if (visibleRenderableLayers.length === 0 || !hasVectorizableLayer) {
      return [];
    }

    const generationRect = workspace.generationRect;
    const layerIdsToVectorize = visibleRenderableLayers.map((layer) => layer.id);
    let targetPixels = Math.max(
      1,
      Math.min(
        RECRAFT_VECTORIZE_WORKSPACE_START_MAX_PIXELS,
        Math.round(Math.max(1, generationRect.w) * Math.max(1, generationRect.h)),
      ),
    );
    let rendered: Awaited<ReturnType<typeof k.renderWorkspaceArea>> | null = null;

    for (let attempt = 0; attempt < 12; attempt += 1) {
      rendered = await k.renderWorkspaceArea(
        workspaceId,
        layerIdsToVectorize,
        targetPixels,
        false,
        true,
        1,
        generationRect,
        'webp',
        undefined,
        undefined,
        undefined,
        0.92
      );
      if (!rendered.file) {
        throw new Error('Could not render workspace before vectorization.');
      }
      if (rendered.file.size <= RECRAFT_VECTORIZE_WORKSPACE_MAX_FILE_BYTES) {
        break;
      }
      const nextTargetPixels = Math.max(RECRAFT_VECTORIZE_WORKSPACE_MIN_PIXELS, Math.floor(targetPixels * RECRAFT_VECTORIZE_WORKSPACE_RETRY_PIXEL_SCALE));
      if (nextTargetPixels >= targetPixels) {
        break;
      }
      targetPixels = nextTargetPixels;
    }

    if (!rendered?.file || rendered.file.size > RECRAFT_VECTORIZE_WORKSPACE_MAX_FILE_BYTES) {
      throw new Error('Rendered workspace image is too heavy to vectorize after downscaling below 5 MB.');
    }

    const { publicUrl: imageUrl, privateUrl: privateImageUrl } = await dualUploadAsset(rendered.file, {
      ttlSeconds: 60 * 60 * 2,
      filenamePrefix: 'vectorize-workspace-src',
      fallbackContentType: 'image/webp',
      fallbackExtension: 'webp',
    }, workspaceId);

    const fullWorkspaceVectorization = (() => {
      const frameRect = workspace.frameRect;
      const tolerance = 1;
      return (
        Math.abs(generationRect.x - frameRect.x) <= tolerance &&
        Math.abs(generationRect.y - frameRect.y) <= tolerance &&
        Math.abs(generationRect.w - frameRect.w) <= tolerance &&
        Math.abs(generationRect.h - frameRect.h) <= tolerance
      );
    })();

    k.logUserAction('Workspace Vectorize Started', {
      workspaceId,
      renderedLayerCount: visibleRenderableLayers.length,
      fullWorkspaceVectorization,
      sourceFileSizeBytes: rendered.file.size,
      renderTargetPixels: targetPixels,
    });

    const taskId = crypto.randomUUID();
    const projectId = getProjectIdFromWorkspaceId(workspaceId, 'vectorizeVisibleLayers');
    const taskChatSessionId = resolveTaskChatSessionId(projectId);
    const sourceSnapshot = getContextSnapshot(workspaceId);
    const replacedLayerIds = new Set(visibleRenderableLayers.map((layer) => layer.id));
    const vectorizationSnapshot = fullWorkspaceVectorization
      ? sourceSnapshot.map((entry) => (
          replacedLayerIds.has(entry.id) ? { ...entry, visible: false } : entry
        ))
      : sourceSnapshot;
    const task: GenerationTask = {
      id: taskId,
      chatSessionId: taskChatSessionId,
      isAgentSpawned: resolveTaskIsAgentSpawned(taskChatSessionId),
      type: 'diffuse',
      workspaceId,
      outputScope: { kind: 'WORKSPACE', workspaceId },
      projectId,
      operation: 'vectorize-workspace',
      params: {
        prompt: '',
        seed: -1,
        noParamsSave: true,
      } as DiffusionParams,
      model: resolveVectorTraceModel(projectId),
      mainImagePublicUrl: imageUrl,
      mainImagePrivateUrl: privateImageUrl,
      mergeStrategy: 'stretchOnArea',
      rect: { ...generationRect },
      targetWidth: Math.max(1, Math.round(rendered.targetWidth || generationRect.w || 1)),
      targetHeight: Math.max(1, Math.round(rendered.targetHeight || generationRect.h || 1)),
      upscaleFactor: rendered.upscaleFactor || 1,
      blurRadius: 0,
      layerOverride: {
        name: `${workspace.name || 'Workspace'} Vectorised`,
        locked: true,
      },
      layerContextSnapshot: vectorizationSnapshot,
      parentVersionId: workspace.activeVersionId ?? null,
    };

    const processing = k.processTasks([task]);
    const completion = await k.waitForTask(taskId);
    await processing;

    if (completion.status === 'FAILURE') {
      throw completion.error instanceof Error
        ? completion.error
        : new Error(typeof completion.error === 'string' ? completion.error : 'Workspace vectorization failed.');
    }

    const vectorizedLayer = (completion.data as { layer?: Layer | null } | undefined)?.layer ?? null;
    if (vectorizedLayer?.id) {
      setProjectSelectedLayersId(k, projectId, [vectorizedLayer.id]);
      k.logUserAction('Workspace Vectorize Completed', {
        workspaceId,
        vector_layer_id: vectorizedLayer.id,
        svgUrl: vectorizedLayer.imageSrc,
      });
      return [vectorizedLayer];
    }
    return [];
  }, [dualUploadAsset, getContextSnapshot, getProjectIdFromWorkspaceId, getWorkspaceLayers, kernel, resolveTaskChatSessionId, resolveTaskIsAgentSpawned]);


  const runModel = useCallback(async (
    workspaceId: string,
    runtimeStateOverride?: RuntimeState,
    numOutputsOverride?: number,
    abortSignal?: AbortSignal,
    generationOptions?: ModelRunGenerationOptions
  ): Promise<{ tasks: GenerationTask[], newWorkspaceId: string | null }> => {
    if (!kernel.current) return { tasks: [], newWorkspaceId: null };
    throwIfAborted(abortSignal);

    const model = runtimeStateOverride?.diffusionModel || kernel.current.getDiffusionModel();
    const normalizedWorkspaceId = typeof workspaceId === 'string' ? workspaceId.trim() : '';
    const workspaceProjectId = kernel.current.getWorkspace(workspaceId)?.projectId;
    const owningProjectId = generationOptions?.projectId?.trim() || workspaceProjectId;
    if (
      normalizedWorkspaceId
      && workspaceProjectId
      && generationOptions?.projectId?.trim()
      && workspaceProjectId !== generationOptions.projectId.trim()
    ) {
      kernel.current.handleError('Generation project scope does not match the selected workspace.');
      return { tasks: [], newWorkspaceId: null };
    }
    const scopedWorkspaceId = normalizedWorkspaceId && workspaceProjectId === owningProjectId
      ? normalizedWorkspaceId
      : '';
    const projectType = owningProjectId
      ? kernel.current.getInventory().Project.registry[owningProjectId]?.projectType
      : null;
    const compatibilityError = getStudioModelProjectCompatibilityError(model, projectType);
    if (compatibilityError) {
      kernel.current.handleError(compatibilityError);
      return { tasks: [], newWorkspaceId: null };
    }

    const generationOutputScope = generationOptions?.outputScope
      ?? resolveGenerationOutputScopeForView(
        owningProjectId,
        scopedWorkspaceId,
        getProjectStudioViewMode(kernel.current, owningProjectId),
      );
    const isListProjectGeneration = generationOutputScope?.kind === 'PROJECT';
    const effectiveGenerationOptions: ModelRunGenerationOptions = {
      ...(generationOptions ?? {}),
      ...(generationOutputScope ? { outputScope: generationOutputScope } : {}),
    };
    const preGenerationVersionId = isListProjectGeneration || !scopedWorkspaceId
      ? null
      : await kernel.current.ensureCurrentStateAsVersion(
          workspaceId,
          'Pre-Generation',
          generationOutputScope ? { outputScope: generationOutputScope } : undefined,
        );
    const generationParentVersionId = isListProjectGeneration || !scopedWorkspaceId
      ? null
      : (kernel.current.getWorkspace(workspaceId)?.activeVersionId ?? preGenerationVersionId);
    throwIfAborted(abortSignal);

    const result = await (async () => {
      switch(model){
        case 'nano-banana':
        case 'nano-banana-pro':
        case 'nano-banana-2':
        case 'nano-banana-2.1':
        case 'grok-imagine-image':
        case 'grok-imagine-2':
        case 'muse-image':
        case 'flux-3-image':
        case 'flux-3-klein':
        case 'flux-2-pro':
        case 'flux-2-klein-4b':
        case 'fater-enhance':
        case 'fater-upscale':
        case 'topaz-image-enhance':
        case 'magnific-image-upscale':
        case 'seedvr2':
        case 'seedream4.5':
        case 'gpt-image-2':
        case 'gpt-image-2.5':
        case 'mai-2.5-flash':
        case 'mai-2.5-pro':
        case 'mai-2.6-flash':
        case 'mai-2.6':
        case 'qwen-image-2-pro':
        case 'qwen-image-3-pro':
        case 'qwen-image-2.1':
        case 'qwen-edit-multiangle':
        case 'ideogram-4-edit':
        case 'ideogram-inpaint':
        case 'ideogram-3':
        case 'arrow-2':
        case 'arrow-2-telos':
        case 'ideogram-vector-1':
        case 'recraft-v4-1-vector':
        case 'recraft-v4-1-vector-pro':
          return handleQueueEditDiffusion(
            scopedWorkspaceId,
            runtimeStateOverride,
            generationParentVersionId,
            numOutputsOverride,
            effectiveGenerationOptions.rect,
            abortSignal,
            effectiveGenerationOptions
          );

        case 'kling-3':
        case 'kling-3-omni':
        case 'seedance-2.0':
        case 'seedance-2.0-fast':
        case 'seedance-2.5':
        case 'flux-3':
        case 'flux-3-draft':
        case 'gemini-omni-flash-preview':
        case 'hailuo-02-fast':
        case 'wan-2.2-i2v':
        case 'wan-2.2-fast':
        case 'veo-3.1-fast':
        case 'kling-2.5-turbo':
        case 'ltx-2.3-fast':
        case 'luma-ray-2-flash':
        case 'minimax-h3-max':
        case 'minimax-h3-max-turbo':
        case 'wan-3.0-prime':
        case 'grok-imagine-video-1.5':
        case 'kling-3-turbo':
        case 'flux-3-max':
        case 'wan-3.0':
        case 'ltx-2.5-fast':
        case 'luma-ray-3.2':
          return handleQueueFilm(scopedWorkspaceId, runtimeStateOverride, generationParentVersionId, numOutputsOverride, abortSignal, effectiveGenerationOptions);

        case 'topaz-video-upscale':
        case 'mirelo-sfx-1.6':
        case 'magnific-video-upscale':
          return handleQueueVideoEdit(scopedWorkspaceId, runtimeStateOverride, generationParentVersionId, numOutputsOverride, abortSignal, effectiveGenerationOptions);

        default:
          kernel.current?.handleError(`Unknown model for generation: ${model}`);
          return { tasks: [], newWorkspaceId: null };
      }
    })();

    // If tasks were successfully created and this wasn't a headless/scripted run, clear the reference images.
    if (result.tasks.length > 0 && !runtimeStateOverride) {
      const projectId = typeof result.tasks[0]?.projectId === 'string' ? result.tasks[0].projectId.trim() : '';
      if (projectId) {
        setExtraFiles(projectId, prevFiles => prevFiles.filter(f => f.locked));
        setAudioReferenceFiles(projectId, prevFiles => prevFiles.filter(f => f.locked));
        setVideoLastImages(projectId, prevFiles => prevFiles.filter(f => f.locked));
      }
    }

    return result;

    }, [handleQueueEditDiffusion, handleQueueFilm, handleQueueVideoEdit, kernel, setAudioReferenceFiles, setExtraFiles, setVideoLastImages]);


  const runIdeogramInpaintOnMask = useCallback(async (
    workspaceId: string,
    prompt: string,
    maskPoint: MaskPoint
  ): Promise<void> => {
    const kernelHandle = kernel.current;
    if (!kernelHandle) return;

    if (!kernelHandle.getWorkspace(workspaceId)) {
      kernelHandle.handleError('Could not find the workspace for Ideogram inpaint.');
      return;
    }

    const maskShapes = kernelHandle.getInpaintMaskShapes()[workspaceId] ?? null;
    if (!inpaintMaskHasContent(maskShapes)) {
      kernelHandle.handleError('Paint a region to inpaint with the Mask tool first.');
      return;
    }
    if (!isPointInsideInpaintMask(maskPoint, maskShapes)) {
      kernelHandle.handleError('Right-click inside the painted mask to run Ideogram inpaint.');
      return;
    }

    const modelParams = kernelHandle.getModelParams();
    const currentIdeogramParams = modelParams['ideogram-inpaint'];
    const ideogramInpaintParams = {
      ...getDefaultIdeogramInpaintParams(),
      ...(currentIdeogramParams && typeof currentIdeogramParams === 'object' ? currentIdeogramParams : {}),
      prompt,
    };
    const runtimeStateOverride: RuntimeState = {
      diffusionModel: 'ideogram-inpaint',
      parameters: {
        ...modelParams,
        'ideogram-inpaint': ideogramInpaintParams,
      },
    };

    kernelHandle.logUserAction('Ideogram Inpaint Prompt Submitted via Canvas Shortcut', {
      workspaceId,
      prompt_length: prompt.length,
    });

    try {
      prepareWorkspaceForGeneration(workspaceId);
      await runModel(workspaceId, runtimeStateOverride, 1);
    } catch (error) {
      console.error('[InpaintCanvas] Ideogram inpaint shortcut failed', error);
      kernel.current?.handleError((error as Error)?.message || 'Ideogram inpaint failed.');
    }
  }, [kernel, prepareWorkspaceForGeneration, runModel]);


  return useMemo(() => ({
    getLocalNumOutputs,
    setLocalNumOutputs,
    getExtraFiles,
    setExtraFiles,
    getAudioReferenceFiles,
    setAudioReferenceFiles,
    getVideoLastImages,
    setVideoLastImages,
    getModelParameterValue,
    setModelParameter,
    resetModelParameters,
    selectDiffusionModel,
    ensureDiffusionModelForProject,
    setReferenceImages,
    prepareWorkspaceForGeneration,
    runModel,
    runIdeogramInpaintOnMask,
    autoCut,
    generateInParallel,
    quickEnhance,
    quickUpscale,
    sweepUpscale,
    developStreet,
    texturiseStreet,
    repairStreetCuts,
    detectRecastZones,
    recastExtras,
    recastGenerationArea,
    isRecastExtrasRunning,
    stopRecastExtras,
    vectorizeLayer,
    vectorizeVisibleLayers,
    setMasterRunChatSessionId
  }), [
    getLocalNumOutputs,
    setLocalNumOutputs,
    getExtraFiles,
    setExtraFiles,
    getAudioReferenceFiles,
    setAudioReferenceFiles,
    getVideoLastImages,
    setVideoLastImages,
    getModelParameterValue,
    setModelParameter,
    resetModelParameters,
    selectDiffusionModel,
    ensureDiffusionModelForProject,
    setReferenceImages,
    prepareWorkspaceForGeneration,
    runModel,
    runIdeogramInpaintOnMask,
    autoCut,
    generateInParallel,
    quickEnhance,
    quickUpscale,
    sweepUpscale,
    developStreet,
    texturiseStreet,
    repairStreetCuts,
    detectRecastZones,
    recastExtras,
    recastGenerationArea,
    isRecastExtrasRunning,
    stopRecastExtras,
    vectorizeLayer,
    vectorizeVisibleLayers,
    setMasterRunChatSessionId
  ]);
};

export default useModels;
